import './main.scss';
import { createNavbar } from './components/Navbar';
import { createFooter } from './components/Footer';
import { UrlRouter } from './utils/urlRouter';
import { enableGlobalWebpImages } from './utils/webp';

// === SYSTÈME DE TRADUCTION SIMPLE ===
type Lang = 'fr' | 'en';
const langFromUrl = UrlRouter.detectLanguageFromUrl();
let currentLang: Lang = langFromUrl;
localStorage.setItem('cryptoaccounting-lang', currentLang);

const DEFAULT_LANG: Lang = 'fr';
let baseI18nCaptured = false;
const baseTranslateHtml = new WeakMap<HTMLElement, string>();
const baseNavBtnText = new WeakMap<HTMLElement, string>();
const baseAttrs = new WeakMap<HTMLElement, Map<string, string | null>>();

function ensureBaseI18nCaptured(): void {
  if (baseI18nCaptured) return;

  document.querySelectorAll<HTMLElement>('[data-translate]').forEach((el) => {
    baseTranslateHtml.set(el, el.innerHTML);
  });

  document.querySelectorAll<HTMLElement>('[data-nav], [data-btn]').forEach((el) => {
    baseNavBtnText.set(el, el.textContent ?? '');
  });

  document
    .querySelectorAll<HTMLElement>('[data-tr-placeholder], [data-tr-title], [data-tr-aria-label], [data-tr-value]')
    .forEach((el) => {
      const map = new Map<string, string | null>();
      (['placeholder', 'title', 'aria-label', 'value'] as const).forEach((attr) => {
        map.set(attr, el.getAttribute(attr));
      });
      baseAttrs.set(el, map);
    });

  baseI18nCaptured = true;
}

function restoreBaseI18nForElement(el: HTMLElement): boolean {
  const baseHtml = baseTranslateHtml.get(el);
  if (baseHtml === undefined) return false;
  el.innerHTML = baseHtml;
  return true;
}

const translations = {
  fr: {
    // Navigation
    'nav.home': 'Accueil',
    'nav.solution': 'Notre solution',
    'nav.skip': 'Aller au contenu',
    'nav.solutions': 'Solutions',
    'nav.solutions.header': 'Nos solutions',
    'nav.solutions.experts': 'Experts-comptables',
    'nav.solutions.experts.desc': 'Export FEC, conformité ANC 2026-01, intégration Pennylane & TIIME',
    'nav.solutions.tpe': 'Entreprises & Professionnels',
    'nav.solutions.tpe.desc': 'Import automatique, zéro saisie, conformité fiscale',
    'nav.solutions.psca': 'PSCA, Web3 & Institutionnels',
    'nav.solutions.psca.desc': 'Analyse blockchain, plan comptable sur-mesure, conformité ANC 2026-01',
    'nav.solutions.flux': 'Analyse des flux économiques',
    'nav.solutions.flux.desc': 'Revue des process, plan comptable sur-mesure, marque blanche',
    'nav.solutions.accompagnement': 'Traitement comptable complet',
    'nav.solutions.accompagnement.desc': 'De la collecte au dossier exploitable, à partir de 500 €',
    'nav.solutions.particuliers': 'Particuliers',
    'nav.solutions.particuliers.desc': 'Déclaration crypto 2086, dès 49 €',
    'nav.solutions.pricing': 'Voir tous nos tarifs',
    'nav.solutions.how': 'Comment ça marche',
    'nav.solutions.how.desc': 'Le parcours en 9 étapes',
    'nav.solutions.compliance': 'Compliance & AML',
    'nav.solutions.compliance.desc': 'KYC/KYB, screening AML, traçabilité',
    'nav.pricing': 'Tarifs',
    'nav.blog': 'Blog',
    'nav.academy': 'Ressources',
    'nav.webinaires': 'Webinaires',
    'nav.partners': 'Partenaires',
    'nav.contact': 'Contactez-nous',

    // Boutons navbar
    'btn.account': 'Créer un compte',
    'btn.login': 'Se connecter',
    'btn.appointment': 'Prendre rendez-vous',
    'btn.appointment.header': 'Prendre rendez-vous',
    'btn.appointment.sanaa': 'Avec Sanaa',
    'btn.appointment.gonzague': 'Avec Stanislas',

    // Footer - Call to action
    'footer.cta.title': 'Passez à la comptabilité Crypto sans effort',
    'footer.cta.subtitle': 'Simplifiez dès aujourd\'hui la gestion de vos actifs numériques avec Crypto Accounting.',
    'footer.cta.start': 'Commencer maintenant',
    'footer.cta.demo': 'Demandez une démo',

    // Footer - Navigation
    'footer.nav.home': 'Accueil',
    'footer.nav.pricing': 'Nos tarifs',
    'footer.nav.guide': 'Guide comptabilité crypto',
    'footer.nav.articles': 'Articles',
    'footer.nav.app': 'Accéder à l\'application',
    'footer.nav.about': 'À propos',
    'footer.nav.contact': 'Contactez-nous',
    'footer.nav.legal': 'Mentions légales',
    'footer.nav.terms': 'CGU',
    'footer.nav.cookies': 'Gérer les cookies',
    'footer.tagline': 'La comptabilité crypto, simplifiée.',
    'footer.nav.title': 'Navigation',
    'footer.solutions.title': 'Solutions',
    'footer.solutions.experts': 'Experts-comptables',
    'footer.solutions.tpe': 'TPE-PME',
    'footer.solutions.particuliers': 'Particuliers',
    'footer.solutions.psca': 'PSCA & Grandes entreprises',
    'footer.solutions.partners': 'Partenaires',
    'footer.resources.title': 'Ressources',
    'footer.nav.academy': 'Ressources',
    'footer.nav.glossary': 'Glossaire',
    'footer.copyright': '© 2025 CRYPTOACCOUNTING . Tous droits réservés',

    // Page d'accueil - Catégories
    'category.experts.title': 'Solution complète pour experts-comptables',
    'category.experts.description': 'Découvrez notre plateforme parfaitement adaptée à vos besoins.',
    'category.experts.optimize': 'Optimisez votre temps',
    'category.experts.optimize.text': 'Automatisez la pré-comptabilité des actifs numériques de vos clients.',
    'category.experts.compliance': 'Assurez la conformité',
    'category.experts.compliance.text': 'Respectez les normes comptables avec une solution fiable.',
    'category.experts.support': 'Accompagnement dédié',
    'category.experts.support.text': 'Recevez le support de nos experts pour des cas spécifiques.',

    'category.tpe.title': 'Solutions adaptées aux TPE-PME',
    'category.tpe.description': 'Plus besoin de comprendre la comptabilité crypto',
    'category.tpe.efficiency': 'Gagnez en efficacité',
    'category.tpe.efficiency.text': 'Réduisez le temps passé sur vos opérations comptables quotidiennes.',
    'category.tpe.reports': 'Accédez à des rapports clairs',
    'category.tpe.reports.text': 'Obtenez une visibilité complète sur vos finances en temps réel.',
    'category.tpe.personalized': 'Support personnalisé',
    'category.tpe.personalized.text': 'Nos experts sont disponibles pour répondre à vos besoins spécifiques.',

    // PSAN et Grandes Entreprises
    'category.psan.title': 'Solutions pour PSCA et Grandes Entreprises',
    'category.psan.description': 'Automatisez la comptabilisation de vos propres flux crypto, en conformité avec les exigences réglementaires.',
    'category.psan.compliance': 'Conformité réglementaire',
    'category.psan.compliance.text': 'Ségrégation des actifs clients (Art. 629-1 ANC 2026-01), traçabilité renforcée, reporting réglementaire ACPR, conformité LCB-FT.',
    'category.psan.scalability': 'Scalabilité',
    'category.psan.scalability.text': 'Une solution adaptée aux volumes importants d\'opérations.',
    'category.psan.expertise': 'Expertise avancée',
    'category.psan.expertise.text': 'Accédez à un support expert pour répondre aux défis uniques de votre secteur.',
    // Catégorie - libellés des boutons
    'category.button.experts': 'Experts-Comptables',
    'category.button.tpe': 'TPE - PME',
    'category.button.psan': 'PSCA et Grandes Entreprises',

    // Homepage Category Cards
    'category.experts.card.title': 'Experts-Comptables',
    'category.experts.card.description': 'Automatisez la comptabilité crypto de vos clients. Export FEC, conformité ANC, intégration Pennylane & TIIME.',
    'category.tpe.card.title': 'Entreprises & Professionnels',
    'category.tpe.card.description': 'Simplifiez votre comptabilité crypto. Import automatique, calculs conformes, zéro saisie manuelle.',
    'category.particuliers.card.title': 'Particuliers',
    'category.particuliers.card.description': 'Préparez votre déclaration crypto : plus-values, formulaire 2086, dès 49 € par année fiscale, sans abonnement.',
    'category.psan.card.title': 'PSCA, Web3 & Institutionnels',
    'category.psan.card.description': 'Solutions sur-mesure pour grands volumes. Conformité ANC 2026-01, plan comptable personnalisé, automatisation complète.',
    'category.card.cta': 'Découvrir',

    // Hero Section
    'hero.eyebrow': 'Intégrations Pennylane, TIIME & Experts-Comptables',
    'hero.title': 'Crypto|| Accounting||La comptabilité crypto de vos clients, directement dans votre logiciel.',
    'hero.subtitle': 'Imports, calculs, reporting détaillé et dossier prêt pour clôture, audit ou contrôle fiscal.<br>Pilotez vos dossiers crypto avec la rigueur d\'un cabinet.',
    'hero.badge1': 'Export FEC',
    'hero.badge2': 'Piste d\'audit',
    'hero.badge3': 'API native',
    'hero.pennylaneNote': 'Intégrations Pennylane & TIIME : exportez vos écritures et votre FEC en un clic. D\'autres intégrations en cours.',

    // Guides essentiels
    'guides.badge': 'Guides essentiels',
    'guides.title': '3 lectures incontournables',
    'guides.subtitle': 'L’essentiel pour sécuriser vos traitements comptables et préparer vos audits.',
    'guides.card1.title': 'Règlement ANC 2026-01',
    'guides.card1.text': 'Maîtrisez le cadre légal et sécurisez vos écritures crypto dès la première clôture.',
    'guides.card2.title': 'Comptabiliser ses crypto-actifs en entreprise',
    'guides.card2.text': 'Le guide opérationnel pour éviter les erreurs et gagner du temps.',
    'guides.card3.title': 'Reconstituer sa comptabilité crypto',
    'guides.card3.text': 'Réparez l’historique en un processus clair, auditable et conforme.',
    'hero.step1.title': 'Connectez',
    'hero.step1.text': 'Connectez vos wallets.<br>Laissez la magie opérer.',
    'hero.step2.title': 'Analysez',
    'hero.step2.text': 'Notre logiciel traite et catégorise automatiquement vos transactions',
    'hero.step3.title': 'Exportez',
    'hero.step3.text': 'Obtenez un rapport clair, complet et conforme',
    'hero.cta': 'Demandez une démo',
    'hero.cta.secondary': 'Analyser un dossier complexe',

    // Partenaires (homepage)
    'partners.kicker': 'Partenaires & intégrations',
    'partners.acd': 'ACD',
    'partners.myunisoft': 'MyUnisoft',
    'partners.note': 'Export FEC et formats compatibles cabinet, sans retraitement manuel.',

    // Comparatif implicite (homepage)
    'comparison.kicker': 'Pourquoi nous choisir',
    'comparison.title': 'La différence entre un export CSV et une vraie comptabilité',
    'comparison.subtitle': 'Les outils grand public vous laissent seul face aux erreurs. Nous livrons des dossiers prêts pour la clôture.',
    'comparison.col.criteria': 'Critères',
    'comparison.col.others': 'Outils Crypto "Grand Public"',
    'comparison.col.ca': 'Crypto Accounting',
    'comparison.row1.label': 'Intégration Pennylane / ACD',
    'comparison.row1.othersText': 'CSV générique (à reformater)',
    'comparison.row1.caText': 'API native & Format FEC',
    'comparison.row2.label': 'Qualité des écritures',
    'comparison.row2.others': 'Données brutes (transactions)',
    'comparison.row2.ca': 'Écritures comptables lettrées',
    'comparison.row3.label': 'Gestion des anomalies (DeFi)',
    'comparison.row3.othersText': 'À traiter manuellement',
    'comparison.row3.caText': 'Pré-analyse par nos experts',
    'comparison.row4.label': 'Support technique',
    'comparison.row4.others': 'Chatbot / FAQ',
    'comparison.row4.ca': 'Expert dédié (Visio/Tel)',

    // Reporting comptable détaillé (homepage)
    'reporting.title': 'Un reporting comptable détaillé, pas juste un export.',
    'reporting.bullet1': 'Stock de fin d\'année',
    'reporting.bullet2': 'Gains & pertes par crypto-actif (CUMP ou FIFO, normes ANC)',
    'reporting.bullet3': 'Gains & pertes par plateforme au CUMP ou FIFO (normes ANC)',
    'reporting.bullet4': 'Plus ou moins-values latentes (clôtures annuelles ou mensuelles)',
    'reporting.bullet5': 'Fichiers exploitables + intégré dans le rapport comptable',
    'reporting.bullet6': 'Alertes : soldes négatifs, cotations manquantes, catégorisations manquantes (flux entrants/sortants), scams identifiés',
    'reporting.microcopy': 'Chaque chiffre est relié aux opérations sources via une piste d\'audit.',

    // Dossier prêt audit (homepage)
    'audit.title': 'Un dossier prêt pour l\'audit et le contrôle fiscal.',
    'audit.bullet1': 'Piste d\'audit complète',
    'audit.bullet2': 'Rapport structuré',
    'audit.bullet3': 'Justification des calculs',
    'audit.bullet4': 'Livrables exploitables par l\'expert-comptable ou le CAC',
    'audit.cta': 'Demander une démo',

    // CA-ACCOM-01 : Accompagnement complet des dossiers crypto (homepage)
    'accom.title': 'Accompagnement complet des dossiers crypto',
    'accom.subtitle': 'Quand un dossier est complexe, on vous accompagne jusqu\'à un dossier exploitable et justifiable.',
    'accom.step1.title': 'Cadrage & collecte',
    'accom.step1.text': 'Checklist d\'accès (exchanges, wallets, fichiers, périodes). Collecte des fichiers manquants + validation du périmètre.',
    'accom.step2.title': 'Mapping & mise au propre',
    'accom.step2.text': 'Mapping wallets/plateformes/entités (qui contrôle quoi). Reconstitution de l\'historique exploitable.',
    'accom.step3.title': 'Traitement comptable des flux',
    'accom.step3.text': 'Normalisation, catégorisation automatique des crypto-actifs, calculs (plus/moins-values). Reporting détaillé : stock fin d\'année + gains/pertes par crypto-actif et plateforme au CUMP ou FIFO.',
    'accom.step4.title': 'Alertes & points d\'attention',
    'accom.step4.text': 'Signalement des soldes négatifs, cotations manquantes, catégorisations manquantes (flux entrants/sortants), scams. Liste d\'actions (ce qu\'il manque / ce qui doit être revu).',
    'accom.step5.title': 'Pack de justification & remise',
    'accom.step5.text': 'Rapport comptable structuré + fichiers exploitables + piste d\'audit. Pack "audit / contrôle fiscal". Handover à l\'expert-comptable / CAC.',
    'accom.price': 'À partir de 500 € pour un dossier simple.',
    'accom.cta': 'Parler à un expert dossier',
    'accom.compliance.note': 'Besoin de conformité LCB-FT ? <a href="/compliance" class="underline text-[#497BF8] hover:text-white transition-colors">Découvrez notre offre Compliance & AML indépendante.</a>',

    // Résumés homepage (accompagnement + compliance)
    'accom.summary.title': 'Dossiers complexes : on vous accompagne.',
    'accom.summary.text': 'De la collecte à la remise d\'un dossier exploitable : 5 étapes, un interlocuteur dédié. À partir de 500 €.',
    'accom.summary.cta': 'En savoir plus',
    'compliance.summary.title': 'Compliance & AML',
    'compliance.summary.text': 'Traçabilité, KYC/KYB, screening AML et pack de justification. De la TPE aux grandes institutions financières. À partir de 1 000 €.',
    'compliance.summary.cta': 'Découvrir l\'offre',

    // Expertise Web3 - Summary (homepage), removed, merged into PSCA

    // Expertise Web3 - Page dédiée
    'web3.hero.title': 'Expertise comptable Web3 : de l\'analyse blockchain au plan comptable sur-mesure',
    'web3.hero.subtitle': 'Une offre dédiée aux PSCA, corporates, institutionnels et pure players Web3/crypto qui nécessitent une expertise sectorielle approfondie.',
    'web3.hero.cta': 'Demander un diagnostic',
    'web3.flux.title': 'Analyse des flux économiques blockchain',
    'web3.flux.desc': 'Analyse des opérations on-chain en fonction de l\'activité spécifique du client. Identification et qualification de chaque type de flux : trading, staking, lending, yield farming, minting, burn, airdrops, bridge, liquidity provision, frais de gas, revenus de protocole...',
    'web3.flux.card1.title': 'Cartographie complète des flux on-chain et off-chain',
    'web3.flux.card2.title': 'Qualification des opérations par nature économique (revenu, charge, investissement, trésorerie)',
    'web3.flux.card3.title': 'Réconciliation entre données blockchain, plateformes d\'échange et flux bancaires',
    'web3.flux.card4.title': 'Identification des flux inter-entités et intra-groupe',
    'web3.process.title': 'Revue des process et flux économiques',
    'web3.process.desc': 'Audit et documentation des process métier pour comprendre comment les flux économiques traversent l\'organisation.',
    'web3.process.card1.title': 'Mapping des process métier : de la transaction blockchain à l\'écriture comptable',
    'web3.process.card2.title': 'Identification des points de contrôle et des risques opérationnels',
    'web3.process.card3.title': 'Documentation des règles de gestion comptable spécifiques',
    'web3.process.card4.title': 'Recommandations d\'optimisation des process',
    'web3.plan.title': 'Plan comptable spécifique, en marque blanche',
    'web3.plan.desc': 'Création d\'un plan comptable adapté au business model du client, livré en marque blanche, intégrable dans n\'importe quel ERP ou logiciel comptable.',
    'web3.plan.card1.title': 'Plan comptable personnalisé selon votre activité : exchange, broker, gestionnaire d\'actifs, protocol DeFi, staking-as-a-service, NFT marketplace...',
    'web3.plan.card2.title': 'Nomenclature conforme ANC 2026-01 et adaptée à votre modèle économique',
    'web3.plan.card3.title': 'Livraison en marque blanche : le plan comptable porte votre identité',
    'web3.plan.card4.title': 'Intégration avec vos outils existants (ERP, logiciel comptable, reporting interne)',
    'web3.audience.title': 'Pour qui ?',
    'web3.audience.card1.title': 'PSCA & plateformes',
    'web3.audience.card1.desc': 'Exchanges, brokers, custodians agréés MiCA',
    'web3.audience.card2.title': 'Corporate & institutionnels',
    'web3.audience.card2.desc': 'Fonds d\'investissement, family offices, trésoreries corporate avec exposition crypto',
    'web3.audience.card3.title': 'Pure players Web3',
    'web3.audience.card3.desc': 'Protocoles DeFi, DAOs, NFT marketplaces, infrastructure blockchain',
    'web3.audience.card4.title': 'Cabinets & intégrateurs',
    'web3.audience.card4.desc': 'Cabinets comptables, intégrateurs ERP qui veulent proposer une offre crypto à leurs clients (marque blanche)',
    'web3.cta.title': 'Chaque mission est sur-mesure. Contactez-nous pour un diagnostic.',
    'web3.pricing': 'Sur devis uniquement, chaque mission est calibrée selon la complexité et le volume.',
    'web3.cta.btn': 'Demander un diagnostic',
    'web3.xref.accompagnement': 'Accompagnement complet des dossiers crypto',
    'web3.xref.compliance': 'Compliance & AML',

    // CA-COMP-01 : Compliance & AML (homepage)
    'compliance.badge': 'Offre indépendante',
    'compliance.title': 'Compliance & AML : sécuriser et documenter les dossiers crypto',
    'compliance.subtitle': 'Une offre indépendante de traçabilité, cohérence documentaire, et préparation des éléments de conformité. De la TPE aux grandes institutions financières.',
    'compliance.card1': 'Organisation des pièces et préparation KYC/KYB (checklists, cohérence, archivage)',
    'compliance.card2': 'Screening AML (selon besoin et périmètre confirmé)',
    'compliance.card3': 'Traçabilité des flux et transactions (selon périmètre confirmé)',
    'compliance.card4': 'Analyse des données plateformes et blockchain, y compris les alertes des outils d\'analyse blockchain, en une décision d\'origine de richesse, prête à être auditée : graphiques de création de richesse, agrégation de données à 360°',
    'compliance.card5': 'Préparation d\'un pack de justification (audit/contrôle fiscal)',
    'compliance.price': 'À partir de 1 000 € sur un petit dossier. Sur devis.',
    'compliance.cta': 'Demander un devis',

    // Sources officielles (homepage)
    'sources.title': 'Références officielles (FR/UE)',
    'sources.link1': 'ANC : Règlements 2026-01 et 2026-02',
    'sources.link2': 'CMF L.561-2',
    'sources.link3': 'RGPD (UE 2016/679)',
    'sources.link4': 'CNIL : RGPD',

    // Workflow Pilote (homepage)
    'workflow.kicker': 'Workflow simplifié',
    'workflow.title': 'De la blockchain à Pennylane en 3 étapes',
    'workflow.subtitle': 'Un processus transparent qui vous redonne le contrôle sur les dossiers crypto.',
    'workflow.step1.title': '1. Connexion sécurisée',
    'workflow.step1.text': 'Connectez vos sources en toute sérénité. Nous supportons les <strong>adresses publiques</strong>, les clés API en <strong>lecture seule</strong> (0 risque) ou l\'import direct de vos <strong>fichiers CSV</strong>.',
    'workflow.step2.badge': 'Gain estimé : ~10h / mois',
    'workflow.step2.title': '2. Auto-catégorisation',
    'workflow.step2.text': 'Nos algorithmes détectent et qualifient automatiquement les opérations complexes : <strong>Staking, Liquidity Pools, Airdrops, Trading</strong>.<br><span class="text-[#133AC9] font-medium">Zéro retraitement manuel nécessaire.</span>',
    'workflow.step3.title': '3. Validation & export',
    'workflow.step3.text': 'Prévisualisez les actifs et la compta. Si tout est OK, générez votre <strong>dossier d\'audit complet</strong> en un clic.',
    'workflow.step3.item1': 'Relevé d\'opérations (€ & Crypto)',
    'workflow.step3.item2': 'Calcul plus-values + suivi stock',
    'workflow.step3.item3': '<strong>Fichier FEC prêt pour Pennylane</strong>',

    // Process Section (étapes détaillées)
    'process.title': 'Étapes du processus',
    'process.step1.title': 'Laissez l\'algorithme travailler',
    'process.step1.text': 'Transactions importées, classées, valorisées.<br>Vous n\'avez rien à faire.<br>Notre logiciel traite automatiquement toutes vos opérations crypto.',
    'process.step2.title': 'Téléchargez votre rapport',
    'process.step2.text': 'Calcul des plus/moins-values, récapitulatif annuel, explication des traitements, justificatifs.<br>Tout est prêt pour votre comptabilité.',

    // FAQ Section
    'faq.title': 'FAQ',
    'faq.subtitle': 'Des questions ?',
    'faq.contact': 'Si vous ne trouvez pas ce que vous cherchez, <a href="/contact-us" class="underline gradient-text text-primary font-medium">contactez nous !</a>',
    'faq.q1': 'Est-ce que Crypto Accounting est un cabinet d\'expertise comptable ?',
    'faq.a1': 'Non. Crypto Accounting est un logiciel SaaS de comptabilité crypto. Nous ne remplaçons pas les experts-comptables, nous leur faisons gagner du temps en automatisant le traitement des actifs numériques.',
    'faq.q2': 'Que contient le rapport généré ?',
    'faq.a2': 'Un calcul détaillé des plus et moins-values, classées par nature d\'opération, avec documentation des flux, valorisation en fiat, et justificatifs d\'origine.',
    'faq.q3': 'À qui est destiné Crypto Accounting ?',
    'faq.a3': 'Notre solution de comptabilité crypto s\'adresse aux entreprises qui détiennent des actifs numériques et veulent les intégrer proprement en comptabilité, ainsi qu\'aux cabinets comptables qui souhaitent automatiser cette tâche fastidieuse.',
    'faq.q4': 'Quels types d\'opérations sont pris en charge ?',
    'faq.a4': 'Trading, achat/vente, staking, airdrops, mining, NFTs, swaps, etc.<br>Le logiciel reconnaît et traite automatiquement la majorité des flux courants.',
    'faq.q5': 'Puis-je connecter plusieurs wallets ou plateformes ?',
    'faq.a5': 'Oui. Le logiciel est compatible avec la plupart des exchanges, wallets chauds/froids, et permet des connexions multiples.',
    'faq.q6': 'Est-ce conforme aux exigences fiscales ?',
    'faq.a6': 'Oui. Le traitement suit les méthodes reconnues (FIFO, CUMP), applique les taux de change officiels, et documente tous les calculs pour répondre à un contrôle fiscal.',
    'faq.q7': 'Est-ce que je peux tester avant de m\'engager ?',
    'faq.a7': 'Oui. Une version gratuite permet de tester la solution avec un nombre limité de transactions.',
    'faq.q8': 'Crypto-monnaies, crypto-actifs... de quoi s\'agit-il ?',
    'faq.a8': 'Selon l\'AMF, une crypto-monnaie ou un crypto-actif désigne « des actifs numériques virtuels qui reposent sur la technologie de la blockchain à travers un registre décentralisé et un protocole informatique crypté ». Ces actifs permettent des transactions sans avoir recours à une monnaie légale.',
    'faq.q9': 'Comment puis-je sécuriser mes actifs numériques ?',
    'faq.a9': 'La sécurité des actifs numériques passe par l\'utilisation de portefeuilles sécurisés, la sauvegarde de vos clés privées et l\'activation d\'une authentification à deux facteurs.',
    'faq.q10': 'Qui crée les crypto-monnaie ?',
    'faq.a10': 'Elles sont créées par une communauté d\'internautes, également appelés « miners » à partir d\'un algorithme qui génère des « jetons » (ou tokens, en anglais) qui sont ensuite alloués à chaque « miner » en récompense de sa participation au fonctionnement du système.',
    'faq.q11': 'Qu\'est ce que la blockchain ?',
    'faq.a11': 'La blockchain est une technologie de stockage et de transmission d\'informations. La blockchain est un grand livre comptable public, transparent et sécurisé où chaque transaction est enregistrée de manière permanente. Les informations sont stockées dans des blocs, qui sont liés les uns aux autres de manière chronologique, formant ainsi une chaîne de blocs.',
    'faq.q12': 'Qu\'est ce que le minage de crypto-monnaie ?',
    'faq.a12': 'Le minage de crypto-monnaie est le processus de vérification et de validation des transactions sur une blockchain. Les mineurs utilisent la puissance de calcul de leurs ordinateurs pour résoudre des problèmes mathématiques complexes, ce qui permet d\'ajouter de nouveaux blocs à la chaîne. En retour, ils sont récompensés par des crypto-monnaies nouvellement créées et/ou des frais de transaction.',
    'faq.q13': 'Les crypto-monnaies sont-elles légales ?',
    'faq.a13': 'La légalité des crypto-monnaies varie selon les pays. En France, les crypto-monnaies comme Bitcoin et Ethereum sont légales. Le gouvernement français reconnaît les crypto-monnaies et a mis en place une réglementation pour encadrer leur utilisation.',
    'faq.q14': 'Les crypto-monnaies sont elles imposables ?',
    'faq.a14': 'Dans de nombreux pays, les gains en capital réalisés sur les crypto-monnaies sont soumis à l\'impôt. Les règles fiscales varient d\'un pays à l\'autre, et il est conseillé de consulter un professionnel de la fiscalité pour obtenir des informations précises sur votre situation personnelle.',
    'faq.q15': 'Comment fonctionne les transactions de crypto-monnaies ?',
    'faq.a15': 'Les transactions de crypto-monnaies sont effectuées en envoyant des jetons d\'une adresse à une autre sur un réseau de blockchain. Les transactions sont vérifiées par des mineurs, qui valident les transactions et les ajoutent à la chaîne de blocs en résolvant des problèmes mathématiques complexes.',
    'faq.q16': 'Qu\'est ce qu\'un portefeuille de crypto-monnaie ?',
    'faq.a16': 'Un portefeuille de crypto-monnaie est un logiciel ou un dispositif matériel qui permet de stocker, envoyer et recevoir des crypto-monnaies. Les portefeuilles chauds sont connectés à Internet et offrent une plus grande commodité, mais sont plus vulnérables aux cyberattaques. Les portefeuilles froids sont des dispositifs de stockage hors ligne qui offrent une meilleure sécurité.',
    'faq.q17': 'Comment acheter et vendre des crypto-monnaies ?',
    'faq.a17': 'Les crypto-monnaies peuvent être achetées et vendues sur des plateformes d\'échange en ligne, appelées exchanges. Les utilisateurs peuvent déposer des fonds en monnaie fiat ou en crypto-monnaie et effectuer des transactions sur la plateforme.',
    'faq.q18': 'Qu\'est ce que la DeFi (Finance décentralisée) ?',
    'faq.a18': 'La DeFi est un écosystème financier décentralisé qui repose sur des applications décentralisées (dApps) et des contrats intelligents. Elle vise à créer des services financiers ouverts, transparents et accessibles à tous, sans intermédiaires comme les banques.',
    'faq.q19': 'Qu\'est ce qu\'une ICO (Initial Coin Offering) ?',
    'faq.a19': 'Un ICO est une méthode de financement utilisée par les startups pour lever des fonds en émettant de nouvelles crypto-monnaies ou jetons « tokens ». Les investisseurs achètent ces jetons en échange de crypto-monnaies existantes, généralement du Bitcoin ou de l\'Ethereum.',
    'faq.q20': 'Qu\'est ce que le stacking de crypto-monnaies ?',
    'faq.a20': 'Le staking est un processus dans lequel les détenteurs de crypto-monnaies participent au fonctionnement et à la sécurité d\'un réseau de blockchain en verrouillant (ou « staking ») leurs tokens. En échange, ils reçoivent des récompenses sous forme de tokens supplémentaires.',
    'faq.q21': 'Qu\'est ce qu\'un contrat intelligent ?',
    'faq.a21': 'Un contrat intelligent est un programme informatique qui exécute automatiquement les termes d\'un contrat lorsque certaines conditions sont remplies. Ils sont principalement utilisés sur la plateforme Ethereum et permettent d\'automatiser des processus sans intervention humaine.',
    'faq.q22': 'Quels sont les risques liés aux crypto-monnaies ?',
    'faq.a22': 'Investir dans les crypto-monnaies comporte des risques, notamment la volatilité des prix, la réglementation gouvernementale, la sécurité des portefeuilles et des plateformes d\'échange, et les arnaques potentielles. Il est important de faire des recherches approfondies.',
    'faq.q23': 'Comment conserver en toute sécuriré mes crypto-monnaies ?',
    'faq.a23': 'Pour stocker vos crypto-monnaies en toute sécurité, il est recommandé d\'utiliser un portefeuille hors ligne (cold wallet) tel qu\'un portefeuille matériel. Il est également important de sauvegarder régulièrement les clés privées.',
    'faq.q24': 'Qu\'est ce que la preuve de travail (Proof of work) et la preuve d\'enjeu (Proof of stake) ?',
    'faq.a24': 'La preuve de travail (PoW) est le mécanisme utilisé par Bitcoin où les mineurs résolvent des problèmes mathématiques complexes. La preuve d\'enjeu (PoS) est un mécanisme alternatif où les validateurs sont sélectionnés en fonction de la quantité de tokens qu\'ils détiennent.',
    'faq.q25': 'Qu\'est ce qu\'un échange décentralisé (DEX) ?',
    'faq.a25': 'Un échange décentralisé (DEX) est une plateforme d\'échange de crypto-monnaies qui fonctionne sans autorité centrale. Les utilisateurs effectuent des transactions directement entre eux en utilisant des contrats intelligents.',
    'faq.q26': 'Qu\'est ce que le trading de crypto-monnaies ?',
    'faq.a26': 'Le trading de crypto-monnaies implique l\'achat et la vente de crypto-monnaies sur des plateformes d\'échange dans le but de réaliser un profit. Les traders peuvent profiter de la volatilité des prix en utilisant différentes stratégies.',
    'faq.q27': 'Qu\'est ce qu\'un stablecoin ?',
    'faq.a27': 'Un stablecoin est une crypto-monnaie dont la valeur est indexée sur un actif sous-jacent stable, comme une monnaie fiat ou un actif précieux. Les exemples populaires incluent Tether (USDT), USD Coin (USDC) et Binance USD (BUSD).',
    'faq.q28': 'Qu\'est ce qu\'un token non fongible (NFT) ?',
    'faq.a28': 'Un token non fongible (NFT) est un type de crypto-monnaie qui représente un objet unique et indivisible. Les NFT sont souvent utilisés pour représenter des œuvres d\'art numériques, des objets de collection ou des biens virtuels dans les jeux en ligne.',
    'faq.q29': 'Comment suivre l\'évolution des prix des crypto-monnaies ?',
    'faq.a29': 'Il existe plusieurs sites web et applications qui fournissent des informations sur les prix des crypto-monnaies, y compris CoinMarketCap, CoinGecko et CryptoCompare. Les traders peuvent également utiliser des outils d\'analyse technique.',
    'faq.q30': 'Qu\'est ce que le yield farming (agriculture de rendement) ?',
    'faq.a30': 'Le yield farming est une stratégie d\'investissement dans la DeFi qui permet aux utilisateurs de gagner des récompenses en mettant à disposition leurs crypto-monnaies. Les investisseurs déposent leurs actifs dans des protocoles DeFi pour recevoir des récompenses.',
    'faq.q31': 'Qu\'est ce que la gouvernance décentralisée ?',
    'faq.a31': 'La gouvernance décentralisée fait référence à la prise de décision et à la gestion d\'un projet par l\'ensemble de ses participants, plutôt que par une autorité centralisée. Elle est souvent mise en œuvre via des tokens de gouvernance.',
    'faq.q32': 'Qu\'est ce que l\'arbitrage ?',
    'faq.a32': 'L\'arbitrage est une stratégie de trading qui consiste à tirer profit des différences de prix d\'un actif entre plusieurs marchés ou plateformes d\'échange. Les traders achètent sur un marché où le prix est inférieur et vendent sur un autre où le prix est supérieur.',
    'faq.q33': 'Qu\'est ce que la tokenisation d\'actifs ?',
    'faq.a33': 'La tokenisation d\'actifs est le processus de représentation d\'actifs réels, tels que des biens immobiliers ou des œuvres d\'art, sous forme de tokens numériques sur une blockchain. Cela permet la création de marchés plus liquides et accessibles.',
    'faq.q34': 'Qu\'est ce que l\'évolutivité des blockchains ?',
    'faq.a34': 'L\'évolutivité des blockchains se réfère à la capacité d\'un réseau blockchain à traiter un grand nombre de transactions par seconde tout en maintenant des temps de confirmation rapides et des coûts de transaction faibles.',
    'faq.q35': 'Qu\'est ce qu\'un airdrop\' ?',
    'faq.a35': 'Un airdrop est une distribution gratuite de tokens ou de crypto-monnaies à un grand nombre de détenteurs de portefeuilles, généralement dans le but de promouvoir un nouveau projet ou de récompenser les détenteurs d\'un certain token.',
    'faq.q36': 'Qu\'est ce qu\'un fork ?',
    'faq.a36': 'Un fork est une modification du code source d\'un projet blockchain qui entraîne la création de deux versions distinctes de la chaîne. Les soft forks sont rétrocompatibles, tandis que les hard forks ne le sont pas.',
    'faq.q37': 'Qu\'est ce qu\'un oracle ?',
    'faq.a37': 'Un oracle est un intermédiaire qui fournit des données du monde réel aux smart contracts sur la blockchain. Les smart contracts ne peuvent pas accéder directement aux informations externes en raison de la nature décentralisée des blockchains.',
    'faq.q38': 'Comment comptabiliser les actifs numériques dans une entreprise française ?',
    'faq.a38': 'La comptabilisation des actifs numériques en France dépend des opérations effectuées par l\'entreprise. Le Règlement ANC 2026-01 (qui remplace l\'ancien règlement 2020-05) fournit le cadre comptable applicable aux crypto-actifs.',
    'faq.q39': 'Comment les NFT sont-ils comptabilisés ?',
    'faq.a39': 'Avant de comptabiliser un NFT, il faut déterminer sa qualification juridique et les droits attachés. Les NFT peuvent être considérés comme une œuvre d\'art, un actif numérique ou un actif incorporel. Une analyse juridique est nécessaire.',
    'faq.q40': 'Comment traiter les revenus de la DeFi en comptabilité ?',
    'faq.a40': 'Pour les produits de la DeFi, il est crucial de comprendre la nature des revenus pour déterminer leur traitement comptable. Les revenus peuvent être considérés comme une commission, un intérêt ou autre, avec des conséquences sur l\'application de la TVA.',
    'faq.q41': 'Comment sont imposées les plus-value réalisées par les particuliers en France ?',
    'faq.a41': 'Les plus-values réalisées par les particuliers lors de la conversion de crypto-monnaies en monnaie fiat sont imposées soit à la « flat tax » au taux de 30 %, soit au taux marginal d\'imposition.',
    'faq.q42': 'Comment calculé la plus-value pour les entreprises Française ?',
    'faq.a42': 'Pour les entreprises françaises, la plus-value ou la moins-value doit être calculée à chaque mouvement fiat ou crypto à crypto, en utilisant l\'une des deux méthodes : le CMUP (coût moyen unitaire pondéré) ou le FIFO (premier entré, premier sorti).',

    // Stats Counter Section
    'stats.hours.label': 'Heures<br>Gagnées',
    'stats.transactions.label': 'Transactions<br>Comptabilisées',
    'stats.saved.label': 'Économisé',

    // Why Section
    'why.title': 'Pourquoi <span class="gradient-text font-bold">Cryptoaccounting</span> ?',
    'why.subtitle': 'Un outil <span class="gradient-text">fiable et puissant</span> pour maîtriser la comptabilité des actifs numériques',
    'why.advantage1.title': '1. Expertise reconnue',
    'why.advantage1.text': 'Faites confiance à l\'expertise de Sanaa Moussaïd, membre du groupe de travail « Actifs Numériques » de l\'ANC, pour vous guider dans la gestion comptable de vos cryptoactifs.',
    'why.advantage2.title': '2. Gain de temps',
    'why.advantage2.text': 'Automatisez les tâches répétitives et consacrez-vous à votre cœur de métier.',
    'why.advantage3.title': '3. Précision et conformité',
    'why.advantage3.text': 'Des calculs exacts et une traçabilité complète pour une comptabilité conforme.',
    'why.advantage4.title': '4. Accompagnement d\'experts',
    'why.advantage4.text': 'Nos professionnels vous assistent dans la vérification et la mise à jour de votre comptabilité.',
    'why.advantage5.title': '5. Flexibilité et personnalisation',
    'why.advantage5.text': 'Adaptez le logiciel à vos besoins spécifiques grâce à des options personnalisables.',
    'why.advantage6.title': '6. Sécurité des données',
    'why.advantage6.text': 'Vos informations sont protégées par des protocoles de sécurité avancés.',

    // Solution Section
    'solution.title': '<strong>Optimisez votre comptabilité</strong> avec des outils performants, découvrez notre solution.',
    'solution.card1.title': 'Une vision claire : stock, flux, performance.',
    'solution.card1.text': 'Suivi par crypto-actif et par plateforme. Gains/pertes réalisés. Plus ou moins-values latentes. Alertes sur points d\'attention.',
    'solution.card1.microcopy': 'La vision dashboard et le rapport comptable racontent la même histoire : cohérence + traçabilité.',
    'solution.card2.title': 'Automatisation de la comptabilité',
    'solution.card2.text': 'Réduisez vos efforts manuels grâce à notre logiciel qui automatise les transactions comptables.',
    'solution.card3.title': 'Importation facile des données',
    'solution.card3.point1': 'Intégration via API avec les principales plateformes d\'échange.',
    'solution.card3.point2': 'Import de fichiers CSV, Excel, TXT.',
    'solution.card3.point3': 'Lecture directe des transactions sur la blockchain.',
    'solution.card4.title': 'Génération automatique du FEC',
    'solution.card4.text': 'Obtenez votre Fichier d\'Écriture Comptable prêt à être intégré dans votre logiciel de comptabilité.',
    'solution.card5.title': 'Calcul précis des plus-values',
    'solution.card5.text': 'Choisissez entre les méthodes FIFO ou CUMP pour un calcul conforme aux normes comptables.',

    // Testimonials Section
    'testimonials.title': 'Ce que disent nos utilisateurs',
    'testimonials.subtitle': 'Découvrez les retours d\'expérience de nos clients',
    'testimonials.quote1': 'CryptoAccounting m\'a fait gagner un temps précieux en automatisant la gestion de mes cryptoactifs. La précision des calculs et la conformité aux normes sont impeccables. Un outil indispensable pour tout professionnel de la comptabilité',
    'testimonials.name1': 'Alexandre',
    'testimonials.title1': 'Expert-comptable',
    'testimonials.quote2': 'Une solution complète et intuitive qui nous a permis d\'optimiser notre gestion des actifs numériques. Le support client est réactif et professionnel. Je recommande vivement à tous les professionnels du secteur.',
    'testimonials.name2': 'Marie',
    'testimonials.title2': 'Directrice financière',
    'testimonials.quote3': 'La transition vers la comptabilité crypto n\'a jamais été aussi simple. L\'interface est intuitive et les rapports générés sont détaillés et conformes. Un véritable gain de temps pour notre cabinet.',
    'testimonials.name3': 'Thomas',
    'testimonials.title3': 'Expert-comptable',
    'testimonials.quote4': 'Une innovation majeure dans le secteur de la comptabilité crypto. La plateforme répond parfaitement aux exigences réglementaires tout en restant simple d\'utilisation.',
    'testimonials.name4': 'Sophie',
    'testimonials.title4': 'Responsable conformité',
    'testimonials.quote5': 'Excellent outil pour la gestion comptable des cryptoactifs. Le calcul automatique des plus-values et la génération des rapports nous font gagner un temps considérable.',
    'testimonials.name5': 'Laurent',
    'testimonials.title5': 'Directeur cabinet comptable',

    // Report Content Section
    'report.title': 'Ce que contient le rapport Crypto Accounting',
    'report.subtitle': 'Un document complet et conforme pour simplifier votre gestion comptable',
    'report.item1.title': 'Synthèse des opérations crypto',
    'report.item1.text': 'Classification détaillée de toutes vos opérations : achat, vente, staking, et autres transactions crypto',
    'report.item2.title': 'Calculs des plus et moins-values',
    'report.item2.text': 'Calculs automatiques par méthode FIFO ou CUMP, conformes aux normes comptables françaises',
    'report.item3.title': 'Valorisation en euros',
    'report.item3.text': 'Conversion automatique selon les taux de change officiels réglementaires',
    'report.item4.title': 'Documentation des sources',
    'report.item4.text': 'Traçabilité complète des exchanges, wallets et plateformes utilisées pour chaque opération',
    'report.item5.title': 'Recommandations automatiques',
    'report.item5.text': 'Suggestions de plan comptable et ventilation optimisée pour votre structure',

    // Expertise Section
    'expertise.title': 'Une expertise comptable à la disposition de tous',
    'expertise.subtitle': 'Découvrez nos solutions dédiées selon votre profil',

    // Logos / Supported platforms
    'logos.title': 'Nos plateformes et blockchains supportées',
    'logos.importMessage': 'Plateforme ou blockchain non prise en charge ?<br>Utilisez notre format CSV pour importer vos transactions manuellement.',
    'logos.partnership.badge': 'Nouveau partenariat',
    'logos.partnership.headline': 'Exportez vos écritures comptables crypto vers <strong>Pennylane</strong>, en un clic.',
    'logos.partnership.text': 'Une intégration fluide pour automatiser vos écritures crypto, jusqu’à leur saisie dans votre logiciel comptable.',
    'logos.partnership.cta': 'Créer un compte',
    'logos.prev': 'Image précédente',
    'logos.next': 'Image suivante',

    // How It Works Section
    'how.title': 'Comment ça marche ?',
    'how.subtitle': 'Un processus simple et automatisé pour une comptabilité crypto sans stress',
    'how.step1.title': 'Connectez vos comptes',
    'how.step1.text': 'Wallets, plateformes d\'échange, cold wallets : tout est synchronisé automatiquement. Connexion sécurisée avec les principales plateformes du marché.',
    'how.step2.title': 'Laissez l\'algorithme travailler',
    'how.step2.text': 'Notre logiciel analyse et catégorise automatiquement toutes vos transactions crypto selon leur nature économique.',
    'how.step3.title': 'Validez et ajustez',
    'how.step3.text': 'Vérifiez les catégorisations proposées et apportez les ajustements nécessaires selon vos spécificités.',
    'how.step4.title': 'Exportez votre comptabilité',
    'how.step4.text': 'Générez vos rapports, FEC et écritures comptables prêts à intégrer dans votre logiciel de gestion.',

    // Checklist Section
    'checklist.badge': 'DOCUMENT EXCLUSIF',
    'checklist.title': 'Checklist Crypto-Actifs',
    'checklist.subtitle': 'Pour une comptabilité crypto conforme aux normes ANC',
    'checklist.content.title': 'Ce que contient votre checklist',
    'checklist.item1.title': 'Identification et gestion des actifs',
    'checklist.item1.text': 'Exhaustivité des wallets, sécurisation des clés privées et procédures internes de création',
    'checklist.item2.title': 'Aspects comptables et fiscaux',
    'checklist.item2.text': 'Traitement selon la réglementation ANC, calcul des plus-values et déclarations obligatoires',
    'checklist.item3.title': 'Conformité réglementaire',
    'checklist.item3.text': 'Respect des obligations PSAN, AML-KYC et reporting aux autorités compétentes',
    'checklist.item4.title': 'Procédures et contrôles',
    'checklist.item4.text': 'Mise en place de processus de validation, audit trail et documentation des opérations',
    'checklist.item5.title': 'Guide complet en 10 sections',
    'checklist.item5.text': 'Guide complet en 10 sections couvrant tous les aspects essentiels'
    ,
    // Checklist author credit
    'checklist.author': 'Créée par Sanaa Moussaïd, membre du groupe de travail ANC',
    'checklist.advantages.title': 'Les avantages de notre checklist',

    // Checklist reCAPTCHA modal
    'checklist.modal.title': 'Vérification de sécurité',
    'checklist.modal.subtitle': "Pour télécharger votre checklist, merci de confirmer que vous n'êtes pas un robot.",
    'checklist.modal.captcha_prompt': "Veuillez confirmer que vous n'êtes pas un robot",
    'checklist.modal.submit': 'Valider et recevoir la checklist',
    'checklist.modal.load_error': "Erreur: Impossible de charger reCAPTCHA. Veuillez rafraîchir la page.",
    'checklist.modal.render_error': "Erreur: Impossible d'afficher le captcha. Veuillez rafraîchir la page.",

    // Checklist form
    'checklist.form.header': 'Téléchargez gratuitement',
    'checklist.form.subheader': 'Notre checklist pour la gestion comptable des crypto-actifs',
    'checklist.form.email.label': 'Email',
    'checklist.form.email.placeholder': 'votre@email.com',
    'checklist.form.firstName.label': 'Prénom',
    'checklist.form.firstName.placeholder': 'Votre prénom',
    'checklist.form.lastName.label': 'Nom',
    'checklist.form.lastName.placeholder': 'Votre nom',
    'checklist.form.company.label': 'Entreprise',
    'checklist.form.company.placeholder': 'Nom de votre entreprise',
    'checklist.form.optin': "J'accepte de recevoir des communications sur les produits et services de Crypto Accounting.",
    'checklist.form.submit': 'Télécharger ma checklist',
    'checklist.form.disclaimer': 'Accès immédiat dans votre boîte de réception. Aucun spam.',
    'checklist.form.privacy': 'En soumettant ce formulaire, vous acceptez notre <a href="/CGU" class="text-[#133AC9] hover:underline">politique de confidentialité</a>.',
    'checklist.form.loading': 'Traitement en cours...',
    'checklist.form.fix_errors': 'Veuillez corriger les erreurs dans le formulaire',
    'checklist.form.submit_success': 'Votre checklist a été envoyée avec succès! Vérifiez votre boîte de réception dans quelques instants.',
    'checklist.form.config_error': "Configuration incomplète. Contactez l'administrateur.",
    'checklist.form.generic_error': 'Une erreur est survenue. Veuillez réessayer.',

    // Form errors
    'errors.required': 'Ce champ est requis',
    'errors.email': 'Veuillez entrer une adresse email valide',
    'errors.min2': 'Ce champ doit contenir au moins 2 caractères',

    // How section CTA
    'how.cta.title': 'Prêt à simplifier votre comptabilité crypto ?',
    'how.cta.subtitle': "Rejoignez les centaines d'entreprises qui font confiance à Crypto Accounting",

    // Cookie consent
    'cookie.banner.title': 'Vos préférences de confidentialité',
    'cookie.banner.text': 'Nous utilisons des cookies pour améliorer votre expérience et analyser le trafic de notre site.',
    'cookie.banner.link': 'En savoir plus sur notre politique de confidentialité',
    'cookie.btn.customize': 'Personnaliser',
    'cookie.btn.accept': 'Tout accepter',
    'cookie.btn.decline': 'Continuer sans accepter',
    'cookie.modal.title': 'Paramètres des cookies',
    'cookie.modal.subtitle': 'Personnalisez vos préférences de confidentialité',
    'cookie.modal.necessary.title': 'Cookies nécessaires',
    'cookie.modal.necessary.desc': 'Indispensables au fonctionnement du site',
    'cookie.modal.analytics.title': 'Cookies analytiques',
    'cookie.modal.analytics.desc': 'Nous aident à améliorer votre expérience',
    'cookie.modal.preferences.title': 'Cookies de préférences',
    'cookie.modal.preferences.desc': 'Sauvegardent vos préférences',
    'cookie.modal.save': 'Enregistrer mes préférences',
    'cookie.manager.label': 'Gérer les cookies',

    // Contact page
    'contact.title': 'Contactez nos experts',
    'contact.subtitle': 'Notre équipe est à votre disposition pour vous accompagner dans la gestion de votre comptabilité crypto.',
    'contact.left.title': 'Restons en contact',
    'contact.left.text': 'Choisissez le moyen qui vous convient le mieux pour nous contacter.',
    'contact.left.email': 'Email',
    'contact.left.address': 'Adresse',
    'contact.form.title': 'Envoyez-nous un message',
    'contact.form.firstName.label': 'Prénom',
    'contact.form.firstName.placeholder': 'Votre prénom',
    'contact.form.lastName.label': 'Nom',
    'contact.form.lastName.placeholder': 'Votre nom',
    'contact.form.email.label': 'Email',
    'contact.form.email.placeholder': 'votre@email.com',
    'contact.form.phone.label': 'Téléphone',
    'contact.form.phone.placeholder': '+33 6 XX XX XX XX',
    'contact.form.message.label': 'Message',
    'contact.form.message.placeholder': 'Votre message...',
    'contact.form.submit': 'Envoyer le message',

    // Blog page
    'blog.title': 'Explorez l\'évolution de la crypto-comptabilité',
    'blog.recent': 'Articles Récents',
    'blog.all': 'Tous nos articles',
    'blog.subtitle': 'Abonnez-vous pour rester informé sur la comptabilité des crypto-monnaies',
    'blog.newsletter.placeholder': 'Votre adresse email',
    'blog.newsletter.cta': 'S\'abonner',
    'blog.timeline.label': 'Chronologie',

    // Pricing page
    'pricing.title': 'Une tarification <br>simple et transparente',
    'pricing.header.p1': 'Vous n’êtes pas à l’aise avec la configuration ?<br>Pas de panique, on est là pour vous !',
    'pricing.header.p2': 'On regarde ensemble vos fichiers, on vous signale les points importants, et si besoin, on s’occupe de tout à votre place.',
    'pricing.header.p3': '👉 Accompagnement des cabinets dans le paramétrage, l’analyse des opérations et l’intégration des flux crypto : <br>à partir de 500 €, selon la complexité des dossiers.',
    'pricing.header.enterprise.title': 'Entreprise',
    'pricing.header.enterprise.badge': 'Automatisation',
    'pricing.header.enterprise.desc':
      'Catégorisation automatique de vos opérations : notre algorithme reconnaît automatiquement vos transactions.',
    'pricing.header.enterprise.item1': 'Staking',
    'pricing.header.enterprise.item2': 'Trading',
    'pricing.header.enterprise.item3': 'Mining',
    'pricing.header.enterprise.item4': 'Rewards',
    'pricing.header.enterprise.item5': 'Liquidity Pools',
    'pricing.header.enterprise.item6': 'Airdrop',
    'pricing.header.accountants.title': 'Pour les experts comptables',
    'pricing.header.accountants.badge': 'Dossier prêt',
    'pricing.header.accountants.desc': 'Un dossier avec une piste d\'audit prêt à être analysé par vos équipes :',
    'pricing.header.accountants.item1': 'Relevé des opérations',
    'pricing.header.accountants.item2': 'Fichier de calcul des plus ou moins values par actif et portefeuille',
    'pricing.header.accountants.item3': 'Un FEC prêt à être intégré dans votre logiciel (dont Pennylane)',
    'pricing.header.accountants.item4': 'Une comptabilité analytique par support et par plateforme',
    'pricing.header.support':
      'Accompagnement des cabinets d\'expertise comptable dans le paramétrage et l’intégration des flux crypto :<br>👉Encadrement gratuit sur un dossier pilot<br>👉Deuxième dossier à partir de 500 €, selon la complexité.',
    'pricing.header.cta': 'Discutons ensemble 🤝',
    'pricing.plan.starter': 'Starter',
    'pricing.plan.basic': 'Basic',
    'pricing.plan.standard': 'Standard',
    'pricing.plan.pro': 'Pro',
    'pricing.plan.custom': 'CUSTOM',
    'pricing.contact': 'Contactez-nous',
    'pricing.per_month_ht': '/ mois HT',
    'pricing.per_year_ht': '/ an HT',
    'pricing.toggle.monthly': 'Mensuel',
    'pricing.toggle.yearly': 'Annuel',
    'pricing.start': 'Commencer',
    'pricing.plan.starter.desc': 'Faites vos premiers pas avec un outil adapté aux petites volumétries',
    'pricing.plan.basic.desc': 'Passez à la vitesse supérieure avec plus de transactions et d’utilisateurs.',
    'pricing.plan.standard.desc': "L'équilibre idéal pour répondre à vos besoins croissants",
    'pricing.plan.pro.desc': 'La solution parfaite pour gérer un grand nombre de transactions en toute tranquillité.',
    'pricing.features.users2': '2 utilisateurs',
    'pricing.features.users_multiple': 'Plusieurs utilisateurs',
    'pricing.features.sources_unlimited': "Sources d'imports illimitées",
    'pricing.features.tx200': '200 transactions sur 12 mois',
    'pricing.features.tx1500': '1 500 transactions sur 12 mois',
    'pricing.features.tx10000': '10 000 transactions sur 12 mois',
    'pricing.features.tx50000': '50 000 transactions sur 12 mois',
    'pricing.features.tag_ops': 'Tag des opérations sur la plateforme',
    'pricing.features.custom_chart': 'Personalisation du plan comptable',
    'pricing.features.fec': 'Production du FEC',
    'pricing.features.fifo_cump': 'Calculs FIFO et CUMP',
    'pricing.features.result_per_asset': 'Fichier résultat par actif numérique',
    'pricing.features.statement_per_asset': 'Relevé des opérations par actif numérique',
    'pricing.custom.usage': 'Usage personnalisé pour les plus gros dossiers',
    'pricing.custom.benefit1': '<strong>Un service personnalisé :</strong> Toutes les fonctionnalités des offres précédentes adaptées à vos besoins spécifiques.',
    'pricing.custom.benefit2': '<strong>Flexibilité maximale :</strong> Transactions, comptes wallet, et connexions exchange personnalisés.',
    'pricing.custom.benefit3': '<strong>Assistance prioritaire :</strong> Service d’assistance élargi et personnalisé.',

    // Legal page
    'legal.hero.title': 'Mentions Légales',
    'legal.hero.subtitle': 'Informations légales et réglementaires de Crypto Accounting',
    'legal.identity.title': 'Identité du propriétaire du Site Web',
    'legal.identity.intro': 'Le site est la propriété de la SAS CRYPTO ACCOUNTING',
    'legal.identity.capital': '<span class="font-medium">Capital :</span> 25 500€',
    'legal.identity.address': '<span class="font-medium">Siège social :</span> 40 RUE DE CRIMEE, 75019 PARIS',
    'legal.identity.ceo': '<span class="font-medium">Président :</span> Sanaa MOUSSAID',
    'legal.identity.email': '<span class="font-medium">Adresse mail :</span> contact@cryptoaccounting.fr',
    'legal.identity.siren': '<span class="font-medium">SIREN :</span> 905 297 941',
    'legal.identity.ape': '<span class="font-medium">APE :</span> 6201Z',
    'legal.identity.vat': '<span class="font-medium">N° TVA intracommunautaire :</span> FR50 905297941',
    'legal.publication.title': 'Direction de la publication',
    'legal.publication.text': 'Directeur de la publication : Sanaa MOUSSAID',
    'legal.hosting.title': 'Hébergement',
    'legal.hosting.text': 'SAS OVH<br>2 rue Kellermann<br>59100 ROUBAIX<br>09 72 10 10 07',
    'legal.rgpd.title': 'Déclaration et droits de l\'utilisateur',
    'legal.rgpd.text': 'Conformément à la délibération n°2006-138 du 09/05/2006 et au règlement général sur la protection des données (RGPD), la SAS CRYPTO ACCOUNTING vous garantit un droit d\'opposition, d\'accès, de rectification, d\'effacement, et de limitation du traitement sur les données personnelles vous concernant. Ce droit peut être exercé par courrier électronique à l\'adresse contact@cryptoaccounting.fr.',
    'legal.marketing.title': 'Emails d\'information et offres',
    'legal.marketing.text': 'Si vous avez un compte professionnel (entreprise ou cabinet), Crypto Accounting peut vous écrire au sujet de ses services, sur la base de son intérêt légitime. Si vous êtes un particulier, nous ne vous écrivons qu\'avec votre accord, donné à l\'inscription. Chaque email contient un lien de désinscription ; vous pouvez aussi nous écrire à contact@cryptoaccounting.fr. Les emails liés à votre compte (codes de connexion, factures) ne sont pas concernés. Ces emails sont envoyés par nos prestataires Brevo et Amazon Web Services.',
    'legal.links.title': 'Liens hypertexte',
    'legal.links.text': 'Les liens vers d\'autres sites web sont fournis à titre indicatif. La SAS CRYPTO ACCOUNTING décline toute responsabilité quant à leur contenu. Toute utilisation d\'un lien vers cryptoaccounting.fr doit être préalablement autorisée.',
    'legal.content.title': 'Contenu du site',
    'legal.content.text': 'Les contenus du site ont une vocation d\'information générale non-exhaustive. Ils ne constituent en aucun cas un conseil ou une offre de service. CRYPTO ACCOUNTING ne saurait être tenue responsable de leur exploitation.',
    'legal.rights.title': 'Droits d\'auteur',
    'legal.rights.text': 'L\'ensemble des éléments composant le site sont la propriété exclusive de SAS CRYPTO ACCOUNTING. Toute reproduction sans autorisation expresse est interdite.<br> © CRYPTO ACCOUNTING - https://cryptoaccounting.fr/ – droits réservés.',

    // Terms (CGU) page
    'terms.hero.title': 'Conditions générales d\'utilisation',
    'terms.hero.subtitle': 'Conditions et modalités d\'utilisation des services Crypto Accounting',
    'terms.article1.title': 'ARTICLE 1. OBJET',
    'terms.article1.content': `<p>Les présentes « conditions générales d'utilisation » ont pour objet de fixer les conditions et modalités de mise à disposition des services proposés via l'accès https://app.cryptoaccounting.fr</p>
<p>La société SAS Crypto Accounting a conçu et exploite un logiciel disponible en mode SaaS. Le logiciel propose un outil de traitement comptable automatisé des flux attachés à des actifs numériques et le calcul des plus-values réalisées selon deux méthodes acceptées en France : FIFO et CMUP.</p>
<p>La société SAS Crypto Accounting n'est pas un cabinet d'expertise comptable inscrit à l'ordre, ne réalise aucune prestation de tenue comptable et n'intervient pas en tant que tel auprès des clients.</p>`,
    'terms.article2.title': 'ARTICLE 2. ACCEPTATION DES CGU',
    'terms.article2.content': `<p>L'accès et l'utilisation de l'Application sont soumis à l'acceptation expresse pleine et entière des présentes CGU par l'utilisateur. Par conséquent, l'accès au Logiciel requiert la souscription à l'offre en ligne, ce qui vaut acceptation sans réserve des CGU par le Client.</p>
<p>Les présentes CGU sont susceptibles d'être mises à jour, sans préavis et à tout moment par la société SAS Crypto Accounting. L'utilisateur est invité à les consulter régulièrement. Toute mise à jour est effective dès sa publication dans la rubrique « Conditions générales d'utilisation » du site.</p>`,
    'terms.article3.title': 'ARTICLE 3. CHAMPS D\'APPLICATION DES CGU',
    'terms.article3.content': `<p>Les présentes CGU s'appliquent, sans restriction ni réserve, à l'utilisation des services proposés sur l'application par les utilisateurs.</p>
<p>Les présentes CGU sont accessibles à tout moment sur le site internet https://cryptoaccounting.fr/.</p>
<p>Les modifications de ces CGU sont opposables aux utilisateurs à compter de leur mise en ligne et ne peuvent s'appliquer aux transactions conclues antérieurement.</p>
<p>Les conditions d'utilisation s'appliquent dès la première connexion par l'utilisateur sur le site et pour toute la durée d'utilisation des services.</p>`,
    'terms.article4.title': 'ARTICLE 4. DURÉE, RENOUVELLEMENT DU CONTRAT',
    'terms.article4.content': `<p>Le contrat entre en vigueur à la date de souscription par l'utilisateur. Les modalités de paiement du prix des services sont décrites sur le site.</p>
<p>Le paiement peut s'effectuer :</p>
<ul class="list-disc pl-6 space-y-2">
  <li>Par prélèvement automatique à partir du numéro de carte bancaire de l'utilisateur. Le prélèvement est initié par le prestataire de paiement utilisé sur le site. Il est seul à conserver les coordonnées bancaires de l'utilisateur à une fin de prélèvement. Crypto Accounting ne conserve aucune coordonnée bancaire.</li>
  <li>Par virement bancaire dans un délai de 48h depuis un IBAN français.</li>
  <li>Par paiement en crypto-actifs sur l'adresse publique communiquée préalablement par la société SAS Crypto Accounting.</li>
</ul>
<p>Le paiement en crypto-actifs doit faire l'objet d'une autorisation préalable par la société.</p>
<h3 class="text-xl font-semibold mt-6 mb-4 text-primary">Retards et incidents de paiement</h3>
<p>En cas de retard de paiement, des pénalités de retard sont exigibles le jour suivant la date de règlement figurant sur la facture ; sauf disposition contraire qui ne peut toutefois fixer un taux d'intérêt de ces pénalités inférieur à trois fois le taux d'intérêt légal, le taux d'intérêt sera égal à celui appliqué par la Banque Centrale Européenne à son opération de refinancement la plus récente majoré de 10 points de pourcentage.</p>
<p>Une indemnité forfaitaire pour frais de recouvrement d'un montant de 40 euros est également exigible de plein droit en cas de retard de paiement.</p>
<p>Le rétablissement de l'accès est subordonné au paiement de l'indemnité de 40 euros et des intérêts de retard.</p>`,
    'terms.article5.title': 'ARTICLE 5. ADMINISTRATION ET SERVICES',
    'terms.article5.content': `<p>L'utilisateur s'engage à mettre à jour les informations le concernant sur l'interface dédiée en cas de modifications de celles-ci. Le prestataire de services ne saurait être tenue responsable au cas où il n'aurait pas été avisé d'un changement de situation du client ou d'informations erronées le concernant.</p>
<p>L'accès au logiciel nécessite un compte utilisateur actif attribué par le client individuellement à chaque utilisateur.</p>
<p>L'application Crypto Accounting est un outil informatique en mode SAAS de comptabilisation des actifs numériques et calcul des plus-values sous-jacentes.</p>
<p>L'utilisateur a accès aux services décrits sur le site, selon les modalités, les fonctionnalités et les moyens techniques définis par Crypto Accounting.</p>`,
    'terms.article6.title': 'ARTICLE 6. PROPRIÉTÉ INTELLECTUELLE',
    'terms.article6.content': `<p>Tous les éléments de propriété intellectuelle utilisés par la Société, en particulier le Logiciel, sont protégés par les lois relatives à la propriété intellectuelle et autres, et notamment le droit d'auteur. Le Logiciel et tous ses éléments, notamment mais non limitativement, les textes, les images, les vidéos, les photographies, les marques, les logos, les dénominations sociales, les noms de domaine sont la propriété exclusive de la société ou de celle de ses partenaires. Toute utilisation ou reproduction, même partielle, non autorisée du logiciel, des contenus et modèles permettant de générer des documents est strictement interdite.</p>`,
    'terms.article7.title': 'ARTICLE 7. INFRASTRUCTURE PERMETTANT D\'OPÉRER LE SERVICE',
    'terms.article7.content': `<p>Les services sont exploités dans un environnement sécurisé, assurant le contrôle des accès, la continuité de service, le stockage et la protection des données, l'exploitation des équipements, la connectivité aux réseaux distants.</p>`,
    'terms.article8.title': 'ARTICLE 8. ENGAGEMENTS ET RESPONSABILITÉ DE LA SOCIÉTÉ SAS CRYPTO ACCOUNTING',
    'terms.article8.content': `<p>La prestataire de services s'engage à apporter, dans le cadre d'une obligation de moyens, tout le soin et la diligence nécessaires à la fourniture d'un service de qualité. Il s'engage à mettre tout en œuvre pour assurer la permanence, la continuité et la qualité des Services. Les Services sont accessibles tous les jours 24h sur 24h, sauf en cas de Maintenance.</p>
<p>Cependant, le prestataire de services ne garantit pas que le Logiciel répond à l'ensemble des traitements sur actifs numériques ou opérations réalisées par client dans la mesure où il reste un vide juridique, fiscal et comptable certain. A cet égard, le prestataire de services ne prend aucun engagement quant à l'exactitude et la complétude des informations communiquées, mises en ligne ou traitées par les utilisateurs – notamment en cas de carence dans la transmission de l'exhaustivité des wallets détenus par la Plateforme.</p>
<p>Les opérations non taguées ou non identifiées (interwallet, fournisseur, client,..) seront mises dans le compte comptable "471 – Compte d'attente", à charge du client de fournir les informations à son expert-comptable pour les imputer sur son propre logiciel comptable en conformité avec la règlementation comptable.</p>`,
    'terms.article9.title': 'ARTICLE 9. OBLIGATIONS ET RESPONSABILITÉ DU CLIENT',
    'terms.article9.content': `<p>Le client est seul responsable de l'utilisation du logiciel par les utilisateurs qu'il a autorisés, du contenu des données qu'ils transmettent ou rendent accessible, des informations transmises, de leur exploitation et de leur mise à jour. Le client s'engage notamment à fournir des informations sincères, véritables et exhaustives. L'utilisateur s'engage à communiquer l'ensemble des informations nécessaires pour le traitement de ses actifs numériques.</p>
<p>Le client s'engage à sauvegarder ses données. A cet égard, le client autorise la Société à héberger et accéder à ses données pour les besoins du présent contrat. La société se réserve la possibilité d'exercer des contrôles sur la conformité de l'utilisation du logiciel par le client.</p>
<p>L'utilisateur s'engage notamment à respecter les droits des tiers et notamment les droits de propriété intellectuelle tels que droits d'auteur, droits sur les brevets ou sur les marques. En conséquence le prestataire de services ne saurait être tenu pour responsable du contenu des informations transmises, diffusées ou collectées et de leur mise à jour.</p>
<p>Il supporte seul les conséquences du défaut de fonctionnement des services, consécutif à toute utilisation par les membres de son personnel ou par toute personne à laquelle l'utilisateur aurait fourni son mot de passe.</p>
<p>Il appartient à l'utilisateur de s'acquitter du montant correspondant au prix de l'accès et utilisation de l'application.</p>
<p>A défaut de règlement ou de manquement à une obligation prévue (dans les conditions générales ou particulières), le prestataire de services se réserve le droit de suspendre sans préavis l'accès au service.</p>`,
    'terms.article10.title': 'ARTICLE 10. COLLABORATION ENTRE LES PARTIES',
    'terms.article10.content': `<p>Les parties s'engagent à collaborer de manière active, permanente et de bonne foi en vue de la bonne exécution du Contrat et le bon déroulement du Service.</p>
<p>A ce titre, les Parties s'engagent à :</p>
<ul class="list-disc pl-6 space-y-2">
  <li>S'investir activement et sérieusement dans l'exécution de leurs obligations réciproques</li>
  <li>S'abstenir de tout comportement susceptible d'impacter et/ou d'entraver le bon fonctionnement des services ainsi que l'exécution des obligations de l'autre Partie</li>
  <li>Se fournir mutuellement, dans un délai suffisant, utile et raisonnable, toutes informations et documents nécessaires à l'exécution du contrat</li>
  <li>Se notifier mutuellement et sans délai toute difficulté rencontrée dans l'exécution de leurs obligations réciproques et/ou dans l'exécution des services</li>
</ul>
<p>Dans ce cadre, les parties s'engagent à se concerter dans les meilleurs délais afin de trouver et mettre en place la solution la plus adéquate.</p>
<p>Les parties s'engagent à communiquer en permanence et à se rencontrer autant que nécessaire pour veiller au bon déroulement du contrat.</p>`,
    'terms.article11.title': 'ARTICLE 11. SUSPENSION DU SERVICE',
    'terms.article11.content': `<p>Le contrat peut être suspendu et l'accès à l'application momentanément interrompu par le prestataire de services dans les cas suivants :</p>
<ul class="list-disc pl-6 space-y-2">
  <li>En cas de maintenance. L'utilisateur est informé que le prestataire de services pourra procéder aux mises à jour du service et ce, sans son autorisation préalable.</li>
  <li>En cas de non-respect des engagements prévus à l'article 15 (Obligations de l'Utilisateur)</li>
  <li>Non paiement du service</li>
</ul>`,
    'terms.article12.title': 'ARTICLE 12. CONFIDENTIALITÉ',
    'terms.article12.content': `<p>Les parties conviennent que sont considérées confidentiels toutes les informations, données, livrables et savoir-faire, couverts ou non par les lois de propriété intellectuelle, quelle qu'en soient la forme et la nature (commerciale, industrielle, technique, financière, etc…), communiqués par une Partie (le « Titulaire ») à l'autre Partie (le « Destinataire »), ou dont elles auraient eu connaissance lors de l'exécution du contrat.</p>
<p>Ne sont pas considérées comme des Informations confidentielles les informations qui :</p>
<ul class="list-disc pl-6 space-y-2">
  <li>étaient en possession du destinataire avant leur divulgation par le titulaire</li>
  <li>font partie du domaine public à la date d'acceptation du contrat</li>
  <li>ont été élaborées de façon indépendante par le destinataire</li>
</ul>
<p>Les parties s'engagent à respecter les obligations résultant du présent article pendant toute la durée du contrat ainsi que pendant trois (3) ans suivant l'expiration ou la résiliation du contrat.</p>`,
    'terms.article13.title': 'ARTICLE 13. POLITIQUE DE PROTECTION DES DONNÉES À CARACTÈRE PERSONNEL',
    'terms.article13.content': `<p>Les parties s'engagent à respecter les dispositions de la réglementation en vigueur afférente à la protection des données à caractère personnel et notamment la loi n° 78-17 du 6 janvier 1978 en sa version actuelle et le règlement UE 2016/679 du 27 avril 2016 ci-après dénommé « RGPD ».</p>
<h3 class="text-xl font-semibold mt-6 mb-4 text-primary">Contact et réclamations</h3>
<p>Toutes les questions soulevées dans la présente Politique, les demandes d'exercice des droits de la personne concernée, sont gérées par le Référent en protection de données qui peut être contacté :</p>
<ul class="list-disc pl-6 space-y-2">
  <li>Par email : rgpd@cryptoaccounting.fr</li>
  <li>Par courrier : à l'attention du référent en protection de données, SAS Crypto Accounting, 7 rue Mortinat, 92600 Asnière-sur-seine</li>
</ul>`,
    'terms.article14.title': 'ARTICLE 14. LOI APPLICABLE – ATTRIBUTION DE JURIDICTION',
    'terms.article14.content': `<p>Le présent contrat est régi par la loi française.</p>
<p>Sous réserve de la qualité de « professionnel » de l'Utilisateur, tout litige relatif à l'interprétation ou l'exécution du présent contrat sera de la compétence exclusive du Tribunal de Commerce de Nanterre.</p>`,
    // Experts-comptables hero
    'experts.hero.title': 'Des dossiers crypto exploitables, traçables, justifiables.',
    'experts.hero.subtitle': 'Reporting détaillé, alertes et accompagnement pour vos dossiers crypto.',

    // Experts-comptables reporting section
    'experts.reporting.title': 'Reporting détaillé : stock + gains/pertes par crypto-actif et plateforme',
    'experts.reporting.cta': 'Demander une démo',

    // Experts-comptables alertes section
    'experts.alerts.title': 'Alertes intégrées à chaque dossier',
    'experts.alert1.badge': 'Alerte solde négatif',
    'experts.alert1.desc': 'Détection automatique des soldes négatifs impossibles sur un actif.',
    'experts.alert2.badge': 'Point d\'attention (incohérence)',
    'experts.alert2.desc': 'Signalement des incohérences dans les flux ou les montants.',
    'experts.alert3.badge': 'Cotation manquante',
    'experts.alert3.desc': 'Identification des actifs sans cotation disponible pour valorisation.',
    'experts.alert4.badge': 'Catégorisation manquante',
    'experts.alert4.desc': 'Flux entrants ou sortants non catégorisés nécessitant une action.',
    'experts.alert5.badge': 'Scam détecté',
    'experts.alert5.desc': 'Identification automatique des transactions suspectes ou frauduleuses.',

    // TPE-PME hero
    'tpepme.hero.title': 'Vous avez des flux crypto ? On vous aide à produire un dossier clair.',
    'tpepme.hero.subtitle': 'Du flux brut au dossier exploitable pour votre expert-comptable.',

    // TPE-PME livrables section
    'tpepme.livrables.title': 'Ce que vous recevez',
    'tpepme.livrables.bullet1': 'Historique reconstitué',
    'tpepme.livrables.bullet2': 'Gestion des stocks initiaux',
    'tpepme.livrables.bullet3': 'Stock fin de période',
    'tpepme.livrables.bullet4': 'Gains/pertes par actif et plateforme',
    'tpepme.livrables.bullet5': 'Alertes sur incohérences',
    'tpepme.livrables.bullet6': 'Rapport structuré + fichiers exploitables',
    'tpepme.livrables.cta': 'Faire analyser mon dossier',

    // Legal references
    'legal.references.title': 'Références officielles',
    'legal.references.anc': 'ANC : Règlements 2026-01 et 2026-02',
    'legal.references.cmf': 'CMF L.561-2',
    'legal.references.rgpd': 'RGPD (UE 2016/679)',
    'legal.references.cnil': 'CNIL : RGPD',

    // Founder section
    'founder.title': 'La fondatrice',
    'founder.subtitle': 'Une expertise reconnue au service des professionnels',
    'founder.name': 'Sanaa Moussaïd <br>Experte-comptable, commissaire aux comptes, entrepreneure',
    'founder.p1': 'Ex-vice-présidente du CSOEC (2019–2020), où elle a piloté la stratégie numérique de la profession, en travaillant notamment sur les usages de l’IA et de la blockchain ; elle a d’ailleurs conduit en 2019 un projet de certification des diplômes des experts-comptables sur la blockchain Bitcoin.',
  'founder.p2': 'Contributrice au groupe de travail « actifs numériques » de l’ANC et lauréate <a href="https://www.internationalaccountingbulletin.com/news/news2017-the-accountant-international-accounting-bulletin-awards-winners-5941877/" target="_blank" rel="noopener noreferrer" class="text-[#133AC9] underline">Young Accountant of the Year 2017 (The Accountant &amp; IAB)</a>, elle fonde Crypto Accounting en 2021 pour apporter un outil fiable aux professionnels confrontés aux flux crypto.',
    'founder.ref.label': 'Référence :',
    'founder.linkedin.aria': 'Profil LinkedIn de Sanaa Moussaïd',

    // Accompagnement page extras
    'accom.timeline.title': '5 étapes pour un dossier exploitable',
    'accom.xref.compliance': 'Besoin de conformité LCB-FT ? <a href="/compliance" class="font-semibold text-[#133AC9] hover:underline transition-colors">Découvrez notre offre Compliance & AML.</a>',

    // Compliance page extras
    'compliance.xref.accompagnement': 'Dossier crypto complexe ? <a href="/accompagnement" class="font-semibold text-[#133AC9] hover:underline transition-colors">Découvrez notre accompagnement complet.</a>',

    // Calculateur page
    'calc.hero.title': 'Particuliers||votre déclaration crypto 2086, automatisée',
    'calc.hero.subtitle': 'Importez vos transactions, générez votre formulaire 2086, déclarez sereinement.',
    'calc.hero.text': 'Import automatique par API ou fichier, cases du formulaire 2086 prêtes à reporter. L\'ancien calculateur en ligne est désormais intégré à l\'application.',
    'calc.hero.cta.signup': 'Créer mon compte',
    'calc.hero.cta.pricing': 'Voir les tarifs',
    'calc.features.title': 'Une déclaration ||simple et automatisée',
    'calc.features.subtitle': 'Concentrez-vous sur vos investissements, nous gérons la complexité fiscale.',
    'calc.features.f1.title': 'Connexion universelle',
    'calc.features.f1.text': 'Import automatique de vos transactions par API, fichier CSV/Excel ou onchain.',
    'calc.features.f2.title': 'Calcul conforme',
    'calc.features.f2.text': 'Méthode de l\'article 150 VH bis du CGI, seuil de 305 €, cotations à la date de chaque cession.',
    'calc.features.f3.title': 'Contrôles intégrés',
    'calc.features.f3.text': 'Cotations manquantes, opérations non classées et exonération sous le seuil de 305 € : tout est signalé dans votre déclaration.',
    'calc.features.f4.title': 'Comptes à l\'étranger',
    'calc.features.f4.text': 'Vos plateformes étrangères sont détectées pour l\'annexe 3916-bis.',
    'calc.features.f5.title': 'Partage comptable',
    'calc.features.f5.text': 'Invitez votre expert-comptable en lecture seule sur votre déclaration.',
    'calc.features.f6.title': 'D\'une année sur l\'autre',
    'calc.features.f6.text': 'Vos déclarations sont sauvegardées : l\'historique d\'acquisition suit automatiquement.',
    'calc.solution.badge': 'Notre Solution',
    'calc.solution.title': 'Du portefeuille crypto||à la déclaration d\'impôts',
    'calc.solution.subtitle': 'Les fonctionnalités qui simplifient votre déclaration crypto',
    'calc.solution.card1.title': 'Suivi de portefeuille',
    'calc.solution.card1.text': 'Visualisez vos actifs, vos plus-values et la valeur de votre portefeuille, <strong>mis à jour à chaque import</strong>.',
    'calc.solution.card2.title': 'Import des données',
    'calc.solution.card2.text': '<strong>API, CSV, blockchain</strong>, connectez vos exchanges et wallets, toutes vos sources en un clic.',
    'calc.solution.card3.title': 'Automatisation',
    'calc.solution.card3.text': '<strong>Catégorisation automatique</strong> : trades, staking, airdrops, transferts entre vos wallets, sans retraitement manuel.',
    'calc.solution.card4.title': 'Déclaration 2086',
    'calc.solution.card4.text': 'Registre des cessions avec les <strong>cases du Cerfa pré-calculées</strong>, revenus de staking, exports <strong>Excel et PDF</strong>.',
    'calc.work.badge': 'Comment ça marche',
    'calc.work.title': '<span class="gradient-text">De vos wallets au formulaire 2086 en 3 étapes</span>',
    'calc.work.subtitle': 'Un processus transparent : vous voyez le prix de votre déclaration avant de payer.',
    'calc.work.step1.title': 'Connexion sécurisée',
    'calc.work.step1.text': 'Créez votre compte Particulier et connectez vos sources : API des exchanges, fichiers CSV/Excel ou lecture directe sur la blockchain.',
    'calc.work.step2.title': 'Un prix selon votre dossier',
    'calc.work.step2.badge': 'Sans abonnement',
    'calc.work.step2.text': 'Vos transactions sont catégorisées et consultables dans l\'application : trades, staking, transferts entre vos wallets. Au moment de déclarer, choisissez l\'année : le prix s\'affiche selon le <strong>nombre de transactions</strong> de votre dossier.<br><span class="text-primary-600 font-medium">Un seul paiement par année fiscale : 49 €, 99 € ou 229 €.</span>',
    'calc.work.step3.title': 'Calcul & déclaration',
    'calc.work.step3.text': 'Dès le paiement confirmé, vos plus-values sont calculées selon l\'<strong>article 150 VH bis</strong>, avec les cotations à la date de chaque cession : le <strong>registre détaillé</strong> avec les cases du formulaire, prêt à reporter sur impots.gouv.fr.',
    'calc.work.step3.item1': 'Cases du formulaire 2086 pré-calculées||',
    'calc.work.step3.item2': 'Revenus de staking (BNC)||',
    'calc.work.step3.item3': 'Annexe 3916-bis||',
    'calc.work.step3.item4': 'Exports Excel & bilan PDF||',
    'calc.pricing.badge': 'Tarifs particuliers',
    'calc.pricing.title': 'Un tarif simple, sans abonnement',
    'calc.pricing.subtitle': 'Un seul paiement par année fiscale, au moment de générer votre déclaration : le prix est déterminé automatiquement selon le nombre de transactions de votre dossier. Vous importez et consultez vos transactions avant de payer.',
    'calc.pricing.over10k': 'Plus de 10 000 transactions ? <a href="/contact-us" class="text-primary-600 font-semibold underline underline-offset-2 hover:text-primary-700">Contactez-nous</a>.',
    'calc.price.per_year': '/ année fiscale TTC',
    'calc.price.cta': 'Commencer',
    'calc.price.t200.name': 'Essentiel',
    'calc.price.t200.desc': 'Quelques opérations dans l\'année',
    'calc.price.t200.f1': 'Dossier jusqu\'à 200 transactions',
    'calc.price.t200.f5': 'Calcul art. 150 VH bis, plus-value nette et estimation d\'impôt',
    'calc.price.t200.f2': 'Registre détaillé, cases du formulaire 2086',
    'calc.price.t200.f3': 'Exports Excel et bilan PDF',
    'calc.price.t200.f4': 'Recalculs et partage avec votre comptable inclus',
    'calc.price.t200.f6': 'Import API et CSV/Excel',
    'calc.price.t200.f7': 'Revenus de staking et comptes à l\'étranger (3916-bis)',
    'calc.price.t1k.badge': 'POPULAIRE',
    'calc.price.t1k.name': 'Déclaration',
    'calc.price.t1k.desc': 'Le volume de l\'investisseur régulier',
    'calc.price.t1k.f1': 'Dossier jusqu\'à 1 000 transactions',
    'calc.price.t1k.f2': 'Tout le plan Essentiel',
    'calc.price.t10k.name': 'Déclaration +',
    'calc.price.t10k.desc': 'Pour les traders actifs et le DeFi',
    'calc.price.t10k.f1': 'Dossier jusqu\'à 10 000 transactions',
    'calc.price.t10k.f2': 'Tout le plan Essentiel',
    'calc.pricing.note': 'Aide au remplissage de la déclaration, pas un conseil fiscal : les paramètres de calcul (taux, seuil de 305 €) sont affichés en toute transparence et les montants restent à vérifier et reporter sous votre responsabilité.',
    'calc.guides.badge': 'Guides essentiels',
    'calc.guides.title': '3 lectures incontournables',
    'calc.guides.subtitle': 'L\'essentiel pour comprendre la fiscalité crypto des particuliers.',
    'calc.guides.card1.title': 'La directive DAC8 en 5 points',
    'calc.guides.card1.text': 'Ce que la nouvelle directive européenne change pour la déclaration de vos crypto-actifs.',
    'calc.guides.card2.title': 'Sécuriser ses actifs numériques',
    'calc.guides.card2.text': 'Les enjeux de vigilance pour les porteurs de crypto et leurs experts-comptables.',
    'calc.guides.card3.title': 'Blockchain et cryptoactifs : concepts et enjeux',
    'calc.guides.card3.text': 'Les bases pour comprendre ce que vous détenez et ce que vous déclarez.',
    'calc.faq.title': 'FAQ',
    'calc.faq.subtitle': 'Des questions ?',
    'calc.faq.q2': 'Qu\'est devenu l\'ancien calculateur gratuit en ligne ?',
    'calc.faq.a2': 'Il a été intégré à l\'application Crypto Accounting : import automatique depuis vos plateformes, cotations à la date de chaque cession, déclarations sauvegardées d\'une année sur l\'autre. Vous importez et consultez vos transactions, puis vous payez au moment de générer votre déclaration : à partir de 49 € TTC par année fiscale, sans abonnement.',
    'calc.faq.q3': 'Le calcul est-il conforme au formulaire 2086 ?',
    'calc.faq.a3': 'Le moteur applique la méthode de l\'article 150 VH bis du CGI : prix total d\'acquisition du portefeuille, cessions contre monnaie fiat imposables, échanges crypto-crypto en report d\'imposition, seuil d\'exonération de 305 €. Il signale aussi vos revenus de staking et vos comptes d\'actifs numériques à l\'étranger (formulaire 3916-bis). C\'est un outil d\'aide au remplissage : les montants restent à reporter et vérifier sous votre responsabilité.',
    'calc.faq.q4': 'Puis-je partager ma déclaration avec mon expert-comptable ?',
    'calc.faq.a4': 'Oui, vous pouvez inviter votre expert-comptable en lecture seule sur votre déclaration. Les cabinets d\'expertise comptable peuvent aussi gérer directement des dossiers particuliers pour leurs clients depuis leur propre espace.',
    'calc.faq.q5': 'Comment fonctionne la tarification ?',
    'calc.faq.a5': 'Vous importez et consultez vos transactions dans l\'application, puis vous payez une seule fois, par année fiscale, au moment de générer votre déclaration : calcul, plus-value nette, estimation d\'impôt, registre détaillé et exports. Le prix est déterminé automatiquement selon la taille de votre dossier : 49 € TTC jusqu\'à 200 transactions, 99 € jusqu\'à 1 000, 229 € jusqu\'à 10 000, au-delà contactez-nous. Aucun abonnement, aucune reconduction. Plusieurs années à régulariser ? Contactez-nous.',
    'calc.final.title': 'Prêt à déclarer vos cryptos ||sereinement ?',
    'calc.final.subtitle': 'Importez vos transactions, choisissez l\'année et obtenez votre formulaire 2086 prêt à reporter.',
    'calc.also.experts': 'Experts-comptables',
    'calc.also.tpe': 'Entreprises & Professionnels',

    // Comment ça marche page
    'ccm.hero.title': 'Comment ça marche',
    'ccm.hero.subtitle': 'De l\'import des données jusqu\'à l\'export comptable, Crypto Accounting suit un parcours simple, structuré et contrôlé.',
    'ccm.overview.title': 'Le parcours en un coup d\'œil',
    'ccm.phase1': 'Phase 1 : Préparez vos données',
    'ccm.phase2': 'Phase 2 : Contrôlez et structurez',
    'ccm.phase3': 'Phase 3 : Produisez et exportez',
    'ccm.step1.title': 'Import des données',
    'ccm.step1.text': 'Crypto Accounting fonctionne à partir de vos flux de transactions. Vous pouvez importer vos données de 3 manières :',
    'ccm.step2.title': 'Visualisation et préparation des transactions',
    'ccm.step2.text': 'Une fois les données intégrées, accédez à la liste des transactions puis cliquez sur <strong>« Charger les cotations »</strong>.',
    'ccm.step3.title': 'Catégorisation automatique',
    'ccm.step3.text': 'Crypto Accounting catégorise automatiquement la majorité des opérations :',
    'ccm.step4.title': 'Contrôle des données dans le dashboard',
    'ccm.step4.text': 'Le dashboard permet de vérifier l\'intégrité des données à partir de plusieurs alertes :',
    'ccm.step5.title': 'Paramétrage des utilisateurs',
    'ccm.step5.text': 'Avant de générer la comptabilité, ajoutez les utilisateurs concernés au dossier :',
    'ccm.step6.title': 'Définition des exercices comptables',
    'ccm.step6.text': 'Dans le module comptabilité, définissez un ou plusieurs exercices comptables. Le logiciel s\'appuie sur les périodes retenues pour structurer correctement les exercices à traiter.',
    'ccm.step7.title': 'Génération de la comptabilité',
    'ccm.step7.text': 'Une fois les données contrôlées, cliquez sur <strong>générer la comptabilité</strong>.',
    'ccm.step8.title': 'Calcul des plus ou moins-values',
    'ccm.step8.text': 'Une fois la comptabilité validée, accédez au module des valeurs latentes. Calculez vos plus ou moins-values selon la périodicité souhaitée :',
    'ccm.step9.title': 'Export et intégration',
    'ccm.step9.text': 'Les écritures peuvent être exportées ou intégrées directement :',
    'ccm.cta.title': 'Prêt à structurer votre comptabilité crypto ?',
    'ccm.cta.subtitle': 'Créez votre compte gratuitement ou demandez une démo pour découvrir la solution en action.',
    'ccm.cta.register': 'Créer un compte',
    'ccm.cta.demo': 'Demander une démo',

    // Webinaires page
    'webinar.hero.badge': 'Un webinaire gratuit par mois : 30 min avec Q&A, à 13h',
    'webinar.hero.title': 'Nos webinaires',
    'webinar.hero.subtitle': 'Sessions pratiques pour structurer vos dossiers crypto, comprendre la fiscalité et anticiper les évolutions réglementaires.',
    'webinar.w1.title': 'Structurer un dossier crypto en cabinet',
    'webinar.w1.subtitle': 'En 20 minutes, une méthodologie actionnable pour qualifier, fiabiliser et livrer un dossier crypto exploitable.',
    'webinar.w2.title': 'Déclarer ses crypto-actifs sans erreur',
    'webinar.w2.subtitle': 'Les règles fiscales essentielles, les erreurs fréquentes et comment anticiper la transparence croissante.',
    'webinar.w3.title': 'MiCA et DAC8 : ce que ça change concrètement',
    'webinar.w3.subtitle': 'Les conséquences pratiques des nouveaux textes européens sur vos dossiers et vos process.',
    'webinar.w4.title': 'Comptabiliser les crypto-actifs en entreprise',
    'webinar.w4.subtitle': 'Qualifier, comptabiliser et sécuriser les opérations crypto : le cadre opérationnel pour les entreprises.',
    'webinar.cta.title': 'Tous nos webinaires sont gratuits',
    'webinar.cta.subtitle': '30 minutes de contenu pratique + Q&A. Un rendez-vous par mois pour monter en compétence sur la crypto-comptabilité.',
    'webinar.cta.btn': 'Nous contacter pour s\'inscrire',
  },
  en: {
    // Navigation
    'nav.home': 'Home',
    'nav.solution': 'Our solution',
    'nav.skip': 'Skip to content',
    'nav.solutions': 'Solutions',
    'nav.solutions.header': 'Our solutions',
    'nav.solutions.experts': 'Accountants',
    'nav.solutions.experts.desc': 'FEC export, ANC 2026-01 compliance, Pennylane & TIIME integration',
    'nav.solutions.tpe': 'Businesses & Professionals',
    'nav.solutions.tpe.desc': 'Automatic import, zero entry, tax compliance',
    'nav.solutions.psca': 'CASP, Web3 & Institutional',
    'nav.solutions.psca.desc': 'Blockchain analysis, custom chart of accounts, ANC 2026-01 compliance',
    'nav.solutions.flux': 'Economic flow analysis',
    'nav.solutions.flux.desc': 'Process review, custom chart of accounts, white label',
    'nav.solutions.accompagnement': 'Full accounting processing',
    'nav.solutions.accompagnement.desc': 'From collection to ready-to-use file, starting at 500 EUR',
    'nav.solutions.particuliers': 'Individuals',
    'nav.solutions.particuliers.desc': 'Crypto tax return (form 2086), from €49',
    'nav.solutions.pricing': 'See all pricing',
    'nav.solutions.how': 'How it works',
    'nav.solutions.how.desc': 'The 9-step process',
    'nav.solutions.compliance': 'Compliance & AML',
    'nav.solutions.compliance.desc': 'KYC/KYB, AML screening, traceability',
    'nav.pricing': 'Pricing',
    'nav.blog': 'Blog',
    'nav.academy': 'Resources',
    'nav.webinaires': 'Webinars',
    'nav.partners': 'Partners',
    'nav.contact': 'Contact us',

    // Boutons navbar
    'btn.account': 'Create account',
    'btn.login': 'Log in',
    'btn.appointment': 'Book appointment',
    'btn.appointment.header': 'Book an appointment',
    'btn.appointment.sanaa': 'With Sanaa',
    'btn.appointment.gonzague': 'With Stanislas',

    // Footer - Call to action
    'footer.cta.title': 'Switch to effortless Crypto accounting',
    'footer.cta.subtitle': 'Simplify your digital asset management today with Crypto Accounting.',
    'footer.cta.start': 'Get started now',
    'footer.cta.demo': 'Request a demo',

    // Experts-comptables hero
    'experts.hero.title': 'Actionable, traceable, auditable crypto files.',
    'experts.hero.subtitle': 'Detailed reporting, alerts and support for your crypto cases.',

    // Experts-comptables reporting section
    'experts.reporting.title': 'Detailed reporting: holdings + gains/losses per crypto-asset and platform',
    'experts.reporting.cta': 'Request a demo',

    // Experts-comptables alertes section
    'experts.alerts.title': 'Built-in alerts for every case',
    'experts.alert1.badge': 'Negative balance alert',
    'experts.alert1.desc': 'Automatic detection of impossible negative balances on an asset.',
    'experts.alert2.badge': 'Attention point (inconsistency)',
    'experts.alert2.desc': 'Flagging of inconsistencies in flows or amounts.',
    'experts.alert3.badge': 'Missing quotation',
    'experts.alert3.desc': 'Identification of assets without available quotation for valuation.',
    'experts.alert4.badge': 'Missing categorization',
    'experts.alert4.desc': 'Uncategorized inflows or outflows requiring action.',
    'experts.alert5.badge': 'Scam detected',
    'experts.alert5.desc': 'Automatic identification of suspicious or fraudulent transactions.',

    // TPE-PME hero
    'tpepme.hero.title': 'Got crypto flows? We help you produce a clear file.',
    'tpepme.hero.subtitle': 'From raw flows to a ready-to-use file for your accountant.',

    // TPE-PME livrables section
    'tpepme.livrables.title': 'What you receive',
    'tpepme.livrables.bullet1': 'Reconstructed history',
    'tpepme.livrables.bullet2': 'Initial stock management',
    'tpepme.livrables.bullet3': 'End-of-period stock',
    'tpepme.livrables.bullet4': 'Gains/losses per asset and platform',
    'tpepme.livrables.bullet5': 'Inconsistency alerts',
    'tpepme.livrables.bullet6': 'Structured report + usable files',
    'tpepme.livrables.cta': 'Get my file analyzed',

    // Legal references
    'legal.references.title': 'Official references',
    'legal.references.anc': 'ANC : Regulations 2026-01 and 2026-02',
    'legal.references.cmf': 'CMF L.561-2',
    'legal.references.rgpd': 'GDPR (EU 2016/679)',
    'legal.references.cnil': 'CNIL : GDPR',

    // Founder section
    'founder.title': 'The Founder',
    'founder.subtitle': 'Recognized expertise serving professionals',
    'founder.name': 'Sanaa Moussaïd <br>Chartered Accountant, Statutory Auditor, Entrepreneur',
    'founder.p1': 'Former Vice-President of the CSOEC (2019–2020), where she led the profession’s digital strategy, notably working on the uses of AI and blockchain; in 2019, she led a project to certify chartered accountants’ diplomas on the Bitcoin blockchain.',
  'founder.p2': 'A contributor to the ANC “digital assets” working group and <a href="https://www.internationalaccountingbulletin.com/news/news2017-the-accountant-international-accounting-bulletin-awards-winners-5941877/" target="_blank" rel="noopener noreferrer" class="text-[#133AC9] underline">winner of the 2017 Young Accountant of the Year (The Accountant &amp; IAB)</a>, she founded Crypto Accounting in 2021 to provide a reliable tool for professionals dealing with crypto flows.',
    'founder.ref.label': 'Reference:',
    'founder.linkedin.aria': 'LinkedIn profile of Sanaa Moussaïd',

    // Footer - Navigation
    'footer.nav.home': 'Home',
    'footer.nav.pricing': 'Pricing',
    'footer.nav.guide': 'Crypto accounting guide',
    'footer.nav.articles': 'Articles',
    'footer.nav.app': 'Access application',
    'footer.nav.about': 'About us',
    'footer.nav.contact': 'Contact us',
    'footer.nav.legal': 'Legal notices',
    'footer.nav.terms': 'Terms',
    'footer.nav.cookies': 'Manage cookies',
    'footer.tagline': 'Crypto accounting, simplified.',
    'footer.nav.title': 'Navigation',
    'footer.solutions.title': 'Solutions',
    'footer.solutions.experts': 'Accountants',
    'footer.solutions.tpe': 'SMBs',
    'footer.solutions.particuliers': 'Individuals',
    'footer.solutions.psca': 'CASP & Large enterprises',
    'footer.solutions.partners': 'Partners',
    'footer.resources.title': 'Resources',
    'footer.nav.academy': 'Resources',
    'footer.nav.glossary': 'Glossary',
    'footer.copyright': '© 2025 CRYPTOACCOUNTING . All rights reserved',

    // Page d'accueil - Catégories
    'category.experts.title': 'Complete solution for accountants',
    'category.experts.description': 'Discover our platform perfectly adapted to your needs.',
    'category.experts.optimize': 'Optimize your time',
    'category.experts.optimize.text': 'Automate the pre-accounting of your clients\' digital assets.',
    'category.experts.compliance': 'Ensure compliance',
    'category.experts.compliance.text': 'Respect accounting standards with a reliable solution.',
    'category.experts.support': 'Dedicated support',
    'category.experts.support.text': 'Receive support from our experts for specific cases.',

    'category.tpe.title': 'Solutions adapted for SMEs',
    'category.tpe.description': 'No need to understand crypto accounting anymore',
    'category.tpe.efficiency': 'Gain efficiency',
    'category.tpe.efficiency.text': 'Reduce time spent on your daily accounting operations.',
    'category.tpe.reports': 'Access clear reports',
    'category.tpe.reports.text': 'Get complete visibility on your finances in real time.',
    'category.tpe.personalized': 'Personalized support',
    'category.tpe.personalized.text': 'Our experts are available to meet your specific needs.',

    // PSAN et Grandes Entreprises
    'category.psan.title': 'Solutions for PSCA and Large Enterprises',
    'category.psan.description': 'Automate the accounting of your own crypto flows, in compliance with regulatory requirements.',
    'category.psan.compliance': 'Regulatory compliance',
    'category.psan.compliance.text': 'Client asset segregation (Art. 629-1 ANC 2026-01), enhanced traceability, ACPR regulatory reporting, AML/CFT compliance.',
    'category.psan.scalability': 'Scalability',
    'category.psan.scalability.text': 'A solution adapted to large volumes of operations.',
    'category.psan.expertise': 'Advanced expertise',
    'category.psan.expertise.text': 'Access expert support to meet the unique challenges of your sector.',
    // Category - button labels
    'category.button.experts': 'Certified Accountants',
    'category.button.tpe': 'SMBs',
    'category.button.psan': 'PSCA and Large Enterprises',

    // Homepage Category Cards
    'category.experts.card.title': 'Certified Accountants',
    'category.experts.card.description': 'Automate your clients\' crypto accounting. FEC export, ANC compliance, Pennylane & TIIME integration.',
    'category.tpe.card.title': 'Businesses & Professionals',
    'category.tpe.card.description': 'Simplify your crypto accounting. Automatic import, compliant calculations, zero manual entry.',
    'category.particuliers.card.title': 'Individuals',
    'category.particuliers.card.description': 'Prepare your crypto tax return: capital gains, form 2086, from €49 per tax year, no subscription.',
    'category.psan.card.title': 'CASP, Web3 & Institutional',
    'category.psan.card.description': 'Custom solutions for large volumes. ANC 2026-01 compliance, personalized chart of accounts, complete automation.',
    'category.card.cta': 'Learn more',

    // Hero Section
    'hero.eyebrow': 'Pennylane & TIIME integrations & accounting firms',
    'hero.title': 'Crypto|| Accounting||Your clients\' crypto accounting, directly in your accounting software.',
    'hero.subtitle': 'Imports, calculations, detailed reporting and files ready for closing, audit or tax review.<br>Manage your crypto files with the rigor of an accounting firm.',
    'hero.badge1': 'FEC export',
    'hero.badge2': 'Audit trail',
    'hero.badge3': 'Native API',
    'hero.pennylaneNote': 'Pennylane & TIIME integrations: export entries and your FEC in one click. More integrations coming soon.',

    // Essential guides
    'guides.badge': 'Essential guides',
    'guides.title': '3 must-read resources',
    'guides.subtitle': 'The essentials to secure your accounting treatments and prepare audits.',
    'guides.card1.title': 'ANC 2026-01 guidance',
    'guides.card1.text': 'Master the legal framework and secure your crypto entries from day one.',
    'guides.card2.title': 'Accounting for crypto assets in a company',
    'guides.card2.text': 'The playbook to avoid errors and close faster.',
    'guides.card3.title': 'Rebuilding crypto accounting',
    'guides.card3.text': 'Fix your history with a clear, auditable, compliant process.',
    'hero.step1.title': 'Connect',
    'hero.step1.text': 'Connect your wallets.<br>Let the magic happen.',
    'hero.step2.title': 'Analyze',
    'hero.step2.text': 'Our software automatically processes and categorizes your transactions',
    'hero.step3.title': 'Export',
    'hero.step3.text': 'Get a clear, complete and compliant report',
    'hero.cta': 'Request a demo',
    'hero.cta.secondary': 'Analyze a complex case',

    // Partners (homepage)
    'partners.kicker': 'Partners & integrations',
    'partners.acd': 'ACD',
    'partners.myunisoft': 'MyUnisoft',
    'partners.note': 'FEC export and firm-friendly formats, without manual rework.',

    // Comparison (homepage)
    'comparison.kicker': 'Why choose us',
    'comparison.title': 'The difference between a CSV export and real accounting',
    'comparison.subtitle': 'Consumer tools leave you alone with errors. We deliver files ready for closing.',
    'comparison.col.criteria': 'Criteria',
    'comparison.col.others': 'Consumer crypto tools',
    'comparison.col.ca': 'Crypto Accounting',
    'comparison.row1.label': 'Pennylane / ACD integration',
    'comparison.row1.othersText': 'Generic CSV (needs reformatting)',
    'comparison.row1.caText': 'Native API & FEC format',
    'comparison.row2.label': 'Entry quality',
    'comparison.row2.others': 'Raw data (transactions)',
    'comparison.row2.ca': 'Lettered accounting entries',
    'comparison.row3.label': 'Anomalies handling (DeFi)',
    'comparison.row3.othersText': 'Manual handling',
    'comparison.row3.caText': 'Pre-analysis by our experts',
    'comparison.row4.label': 'Technical support',
    'comparison.row4.others': 'Chatbot / FAQ',
    'comparison.row4.ca': 'Dedicated expert (call/video)',

    // Detailed accounting reporting (homepage)
    'reporting.title': 'Detailed accounting reporting, not just an export.',
    'reporting.bullet1': 'Year-end inventory',
    'reporting.bullet2': 'Gains & losses per crypto asset (WAC or FIFO, ANC standards)',
    'reporting.bullet3': 'Gains & losses per platform using WAC or FIFO (ANC standards)',
    'reporting.bullet4': 'Unrealized gains/losses (annual or monthly closings)',
    'reporting.bullet5': 'Actionable files + integrated into the accounting report',
    'reporting.bullet6': 'Alerts: negative balances, missing quotes, missing categorizations (inflows/outflows), identified scams',
    'reporting.microcopy': 'Every figure is linked to source transactions via an audit trail.',

    // Audit-ready file (homepage)
    'audit.title': 'A file ready for audit and tax review.',
    'audit.bullet1': 'Complete audit trail',
    'audit.bullet2': 'Structured report',
    'audit.bullet3': 'Calculation justification',
    'audit.bullet4': 'Deliverables usable by the accountant or auditor',
    'audit.cta': 'Request a demo',

    // CA-ACCOM-01: Full crypto case support (homepage)
    'accom.title': 'Full crypto case support',
    'accom.subtitle': 'When a case is complex, we guide you to an actionable and justifiable file.',
    'accom.step1.title': 'Scoping & collection',
    'accom.step1.text': 'Access checklist (exchanges, wallets, files, periods). Collection of missing files + scope validation.',
    'accom.step2.title': 'Mapping & cleanup',
    'accom.step2.text': 'Wallet/platform/entity mapping (who controls what). Reconstruction of actionable history.',
    'accom.step3.title': 'Accounting treatment of flows',
    'accom.step3.text': 'Normalization, automatic crypto-asset categorization, calculations (gains/losses). Detailed reporting: year-end stock + gains/losses per crypto-asset and platform using CUMP or FIFO.',
    'accom.step4.title': 'Alerts & attention points',
    'accom.step4.text': 'Flagging of negative balances, missing prices, missing categorizations (inflows/outflows), scams. Action list (what\'s missing / what needs review).',
    'accom.step5.title': 'Justification pack & delivery',
    'accom.step5.text': 'Structured accounting report + actionable files + audit trail. "Audit / tax review" pack. Handover to the accountant / auditor.',
    'accom.price': 'Starting at €500 for a simple case.',
    'accom.cta': 'Talk to a case expert',
    'accom.compliance.note': 'Need AML compliance? <a href="/compliance" class="underline text-[#497BF8] hover:text-white transition-colors">Discover our independent Compliance & AML offering.</a>',

    // Homepage summaries (accompagnement + compliance)
    'accom.summary.title': 'Complex cases: we support you.',
    'accom.summary.text': 'From collection to delivery of an actionable file : 5 steps, a dedicated contact. Starting at \u20ac500.',
    'accom.summary.cta': 'Learn more',
    'compliance.summary.title': 'Compliance & AML',
    'compliance.summary.text': 'Traceability, KYC/KYB, AML screening and justification pack. From small businesses to large financial institutions. Starting at \u20ac1,000.',
    'compliance.summary.cta': 'Discover the offering',

    // Expertise Web3 - Summary (homepage), removed, merged into PSCA

    // Expertise Web3 - Dedicated page
    'web3.hero.title': 'Web3 Accounting Expertise: from blockchain analysis to custom chart of accounts',
    'web3.hero.subtitle': 'A dedicated offering for CASP, corporates, institutional investors and Web3/crypto pure players requiring deep sector expertise.',
    'web3.hero.cta': 'Request a diagnostic',
    'web3.flux.title': 'Blockchain economic flow analysis',
    'web3.flux.desc': 'Analysis of on-chain operations based on the client\'s specific activity. Identification and qualification of each flow type: trading, staking, lending, yield farming, minting, burn, airdrops, bridge, liquidity provision, gas fees, protocol revenue...',
    'web3.flux.card1.title': 'Complete mapping of on-chain and off-chain flows',
    'web3.flux.card2.title': 'Qualification of operations by economic nature (revenue, expense, investment, treasury)',
    'web3.flux.card3.title': 'Reconciliation between blockchain data, exchange platforms and bank flows',
    'web3.flux.card4.title': 'Identification of inter-entity and intra-group flows',
    'web3.process.title': 'Process and economic flow review',
    'web3.process.desc': 'Audit and documentation of business processes to understand how economic flows traverse the organization.',
    'web3.process.card1.title': 'Business process mapping: from blockchain transaction to accounting entry',
    'web3.process.card2.title': 'Identification of control points and operational risks',
    'web3.process.card3.title': 'Documentation of specific accounting management rules',
    'web3.process.card4.title': 'Process optimization recommendations',
    'web3.plan.title': 'Custom chart of accounts, white-label',
    'web3.plan.desc': 'Creation of a chart of accounts tailored to the client\'s business model, delivered white-label, integrable into any ERP or accounting software.',
    'web3.plan.card1.title': 'Custom chart of accounts based on your activity: exchange, broker, asset manager, DeFi protocol, staking-as-a-service, NFT marketplace...',
    'web3.plan.card2.title': 'ANC 2026-01 compliant nomenclature adapted to your business model',
    'web3.plan.card3.title': 'White-label delivery: the chart of accounts bears your identity',
    'web3.plan.card4.title': 'Integration with your existing tools (ERP, accounting software, internal reporting)',
    'web3.audience.title': 'Who is it for?',
    'web3.audience.card1.title': 'CASP & platforms',
    'web3.audience.card1.desc': 'Exchanges, brokers, MiCA-licensed custodians',
    'web3.audience.card2.title': 'Corporate & institutional',
    'web3.audience.card2.desc': 'Investment funds, family offices, corporate treasuries with crypto exposure',
    'web3.audience.card3.title': 'Web3 pure players',
    'web3.audience.card3.desc': 'DeFi protocols, DAOs, NFT marketplaces, blockchain infrastructure',
    'web3.audience.card4.title': 'Firms & integrators',
    'web3.audience.card4.desc': 'Accounting firms, ERP integrators looking to offer crypto services to their clients (white-label)',
    'web3.cta.title': 'Every engagement is custom. Contact us for a diagnostic.',
    'web3.pricing': 'On quote only, each engagement is calibrated based on complexity and volume.',
    'web3.cta.btn': 'Request a diagnostic',
    'web3.xref.accompagnement': 'Complete crypto case management',
    'web3.xref.compliance': 'Compliance & AML',

    // CA-COMP-01: Compliance & AML (homepage)
    'compliance.badge': 'Independent offering',
    'compliance.title': 'Compliance & AML: secure and document crypto cases',
    'compliance.subtitle': 'An independent offering for traceability, document consistency, and compliance preparation. From small businesses to large financial institutions.',
    'compliance.card1': 'Document organization and KYC/KYB preparation (checklists, consistency, archiving)',
    'compliance.card2': 'AML screening (based on confirmed needs and scope)',
    'compliance.card3': 'Flow and transaction traceability (based on confirmed scope)',
    'compliance.card4': 'Platform and blockchain data analysis, including blockchain analytics tool alerts, into an audit-ready source of wealth decision: wealth creation charts, 360° data aggregation',
    'compliance.card5': 'Preparation of a justification pack (audit/tax review)',
    'compliance.price': 'Starting at €1,000 for a small case. On quote.',
    'compliance.cta': 'Request a quote',

    // Official sources (homepage)
    'sources.title': 'Official references (FR/EU)',
    'sources.link1': 'ANC : Regulations 2026-01 and 2026-02',
    'sources.link2': 'CMF L.561-2',
    'sources.link3': 'GDPR (EU 2016/679)',
    'sources.link4': 'CNIL : GDPR',

    // Pilot workflow (homepage)
    'workflow.kicker': 'Simplified workflow',
    'workflow.title': 'From blockchain to Pennylane in 3 steps',
    'workflow.subtitle': 'A transparent process that gives you control over crypto files.',
    'workflow.step1.title': '1. Secure connection',
    'workflow.step1.text': 'Connect your sources with peace of mind. We support <strong>public addresses</strong>, <strong>read-only</strong> API keys (0 risk), or direct import of your <strong>CSV files</strong>.',
    'workflow.step2.badge': 'Estimated gain: ~10h / month',
    'workflow.step2.title': '2. Auto-categorization',
    'workflow.step2.text': 'Our algorithms detect and qualify complex operations: <strong>Staking, Liquidity Pools, Airdrops, Trading</strong>.<br><span class="text-[#133AC9] font-medium">No manual rework needed.</span>',
    'workflow.step3.title': '3. Validation & export',
    'workflow.step3.text': 'Preview assets and accounting. If everything is OK, generate your <strong>complete audit file</strong> in one click.',
    'workflow.step3.item1': 'Operations statement (€ & crypto)',
    'workflow.step3.item2': 'Capital gains + stock tracking',
    'workflow.step3.item3': '<strong>FEC file ready for Pennylane</strong>',

    // Process Section (étapes détaillées)
    'process.title': 'Process steps',
    'process.step1.title': 'Let the algorithm work',
    'process.step1.text': 'Transactions imported, classified, valued.<br>You have nothing to do.<br>Our software automatically processes all your crypto operations.',
    'process.step2.title': 'Download your report',
    'process.step2.text': 'Calculation of capital gains/losses, annual summary, treatment explanations, supporting documents.<br>Everything is ready for your accounting.',

    // FAQ Section
    'faq.title': 'FAQ',
    'faq.subtitle': 'Questions?',
    'faq.contact': 'If you can\'t find what you\'re looking for, <a href="/contact-us" class="underline gradient-text text-primary font-medium">contact us!</a>',
    'faq.q1': 'Is Crypto Accounting an accounting firm?',
    'faq.a1': 'No. Crypto Accounting is a SaaS software. We don\'t replace accountants, we save them time.',
    'faq.q2': 'What does the generated report contain?',
    'faq.a2': 'A detailed calculation of capital gains and losses, classified by type of operation, with flow documentation, fiat valuation, and supporting evidence.',
    'faq.q3': 'Who is Crypto Accounting for?',
    'faq.a3': 'For companies that have crypto flows and want to integrate them properly into accounting, and for accounting firms that want to automate this tedious task.',
    'faq.q4': 'What types of operations are supported?',
    'faq.a4': 'Trading, buying/selling, staking, airdrops, mining, NFTs, swaps, etc.<br>The software recognizes and automatically processes most common flows.',
    'faq.q5': 'Can I connect multiple wallets or platforms?',
    'faq.a5': 'Yes. The software is compatible with most exchanges, hot/cold wallets, and allows multiple connections.',
    'faq.q6': 'Is it compliant with tax requirements?',
    'faq.a6': 'Yes. The processing follows recognized methods (FIFO, CUMP), applies official exchange rates, and documents all calculations to respond to a tax audit.',
    'faq.q7': 'Can I test before committing?',
    'faq.a7': 'Yes. A free version allows you to test the solution with a limited number of transactions.',
    'faq.q8': 'Cryptocurrencies, crypto-assets... what are they?',
    'faq.a8': 'According to the AMF, a cryptocurrency or crypto-asset refers to "virtual digital assets that rely on blockchain technology through a decentralized ledger and an encrypted computer protocol". These assets enable transactions without using legal tender.',
    'faq.q9': 'How can I secure my digital assets?',
    'faq.a9': 'Digital asset security involves using secure wallets, backing up your private keys, and enabling two-factor authentication.',
    'faq.q10': 'Who creates cryptocurrencies?',
    'faq.a10': 'They are created by a community of internet users, also called "miners", from an algorithm that generates "tokens" which are then allocated to each "miner" as a reward for their participation in the system\'s operation.',
    'faq.q11': 'What is blockchain?',
    'faq.a11': 'Blockchain is a technology for storing and transmitting information. Blockchain is a public, transparent and secure ledger where each transaction is permanently recorded. Information is stored in blocks, which are linked to each other chronologically, forming a chain of blocks.',
    'faq.q12': 'What is cryptocurrency mining?',
    'faq.a12': 'Cryptocurrency mining is the process of verifying and validating transactions on a blockchain. Miners use the computing power of their computers to solve complex mathematical problems, which allows new blocks to be added to the chain. In return, they are rewarded with newly created cryptocurrencies and/or transaction fees.',
    'faq.q13': 'Are cryptocurrencies legal?',
    'faq.a13': 'The legality of cryptocurrencies varies by country. In France, cryptocurrencies like Bitcoin and Ethereum are legal. The French government recognizes cryptocurrencies and has put in place regulations to govern their use.',
    'faq.q14': 'Are cryptocurrencies taxable?',
    'faq.a14': 'In many countries, capital gains realized on cryptocurrencies are subject to tax. Tax rules vary from country to country, and it is advisable to consult a tax professional for precise information about your personal situation.',
    'faq.q15': 'How do cryptocurrency transactions work?',
    'faq.a15': 'Cryptocurrency transactions are performed by sending tokens from one address to another on a blockchain network. Transactions are verified by miners, who validate transactions and add them to the blockchain by solving complex mathematical problems.',
    'faq.q16': 'What is a cryptocurrency wallet?',
    'faq.a16': 'A cryptocurrency wallet is software or hardware that allows you to store, send and receive cryptocurrencies. Hot wallets are connected to the Internet and offer greater convenience, but are more vulnerable to cyber attacks. Cold wallets are offline storage devices that offer better security.',
    'faq.q17': 'How to buy and sell cryptocurrencies?',
    'faq.a17': 'Cryptocurrencies can be bought and sold on online exchange platforms, called exchanges. Users can deposit funds in fiat currency or cryptocurrency and perform transactions on the platform.',
    'faq.q18': 'What is DeFi (Decentralized Finance)?',
    'faq.a18': 'DeFi is a decentralized financial ecosystem that relies on decentralized applications (dApps) and smart contracts. It aims to create open, transparent and accessible financial services for all, without intermediaries like banks.',
    'faq.q19': 'What is an ICO (Initial Coin Offering)?',
    'faq.a19': 'An ICO is a funding method used by startups to raise funds by issuing new cryptocurrencies or "tokens". Investors buy these tokens in exchange for existing cryptocurrencies, usually Bitcoin or Ethereum.',
    'faq.q20': 'What is cryptocurrency staking?',
    'faq.a20': 'Staking is a process in which cryptocurrency holders participate in the operation and security of a blockchain network by locking (or "staking") their tokens. In exchange, they receive rewards in the form of additional tokens.',
    'faq.q21': 'What is a smart contract?',
    'faq.a21': 'A smart contract is a computer program that automatically executes the terms of a contract when certain conditions are met. They are mainly used on the Ethereum platform and allow processes to be automated without human intervention.',
    'faq.q22': 'What are the risks associated with cryptocurrencies?',
    'faq.a22': 'Investing in cryptocurrencies involves risks, including price volatility, government regulation, wallet and exchange platform security, and potential scams. It\'s important to do thorough research.',
    'faq.q23': 'How to securely store my cryptocurrencies?',
    'faq.a23': 'To store your cryptocurrencies securely, it is recommended to use an offline wallet (cold wallet) such as a hardware wallet. It is also important to regularly back up private keys.',
    'faq.q24': 'What is Proof of Work and Proof of Stake?',
    'faq.a24': 'Proof of Work (PoW) is the mechanism used by Bitcoin where miners solve complex mathematical problems. Proof of Stake (PoS) is an alternative mechanism where validators are selected based on the amount of tokens they hold.',
    'faq.q25': 'What is a decentralized exchange (DEX)?',
    'faq.a25': 'A decentralized exchange (DEX) is a cryptocurrency trading platform that operates without a central authority. Users transact directly with each other using smart contracts.',
    'faq.q26': 'What is cryptocurrency trading?',
    'faq.a26': 'Cryptocurrency trading involves buying and selling cryptocurrencies on exchange platforms with the aim of making a profit. Traders can take advantage of price volatility using different strategies.',
    'faq.q27': 'What is a stablecoin?',
    'faq.a27': 'A stablecoin is a cryptocurrency whose value is indexed to a stable underlying asset, such as a fiat currency or precious asset. Popular examples include Tether (USDT), USD Coin (USDC) and Binance USD (BUSD).',
    'faq.q28': 'What is a non-fungible token (NFT)?',
    'faq.a28': 'A non-fungible token (NFT) is a type of cryptocurrency that represents a unique and indivisible object. NFTs are often used to represent digital artworks, collectibles, or virtual goods in online games.',
    'faq.q29': 'How to track cryptocurrency price movements?',
    'faq.a29': 'There are several websites and applications that provide cryptocurrency price information, including CoinMarketCap, CoinGecko and CryptoCompare. Traders can also use technical analysis tools.',
    'faq.q30': 'What is yield farming?',
    'faq.a30': 'Yield farming is an investment strategy in DeFi that allows users to earn rewards by making their cryptocurrencies available. Investors deposit their assets in DeFi protocols to receive rewards.',
    'faq.q31': 'What is decentralized governance?',
    'faq.a31': 'Decentralized governance refers to decision-making and management of a project by all its participants, rather than by a centralized authority. It is often implemented via governance tokens.',
    'faq.q32': 'What is arbitrage?',
    'faq.a32': 'Arbitrage is a trading strategy that consists of taking advantage of price differences of an asset between several markets or exchange platforms. Traders buy on a market where the price is lower and sell on another where the price is higher.',
    'faq.q33': 'What is asset tokenization?',
    'faq.a33': 'Asset tokenization is the process of representing real assets, such as real estate or artwork, as digital tokens on a blockchain. This enables the creation of more liquid and accessible markets.',
    'faq.q34': 'What is blockchain scalability?',
    'faq.a34': 'Blockchain scalability refers to a blockchain network\'s ability to process a large number of transactions per second while maintaining fast confirmation times and low transaction costs.',
    'faq.q35': 'What is an airdrop?',
    'faq.a35': 'An airdrop is a free distribution of tokens or cryptocurrencies to a large number of wallet holders, usually to promote a new project or reward holders of a certain token.',
    'faq.q36': 'What is a fork?',
    'faq.a36': 'A fork is a modification of the source code of a blockchain project that results in the creation of two distinct versions of the chain. Soft forks are backward compatible, while hard forks are not.',
    'faq.q37': 'What is an oracle?',
    'faq.a37': 'An oracle is an intermediary that provides real-world data to smart contracts on the blockchain. Smart contracts cannot directly access external information due to the decentralized nature of blockchains.',
    'faq.q38': 'How to account for digital assets in a French company?',
    'faq.a38': 'The accounting of digital assets in France depends on the operations performed by the company. ANC Regulation 2026-01 (replacing the former 2020-05 regulation) provides the applicable accounting framework for crypto-assets.',
    'faq.q39': 'How are NFTs accounted for?',
    'faq.a39': 'Before accounting for an NFT, its legal qualification and the rights attached must be determined. NFTs can be considered as artwork, digital asset or intangible asset. Legal analysis is required.',
    'faq.q40': 'How to handle DeFi income in accounting?',
    'faq.a40': 'For DeFi products, it is crucial to understand the nature of the income to determine their accounting treatment. Income can be considered as commission, interest or other, with consequences on VAT application.',
    'faq.q41': 'How are capital gains realized by individuals taxed in France?',
    'faq.a41': 'Capital gains realized by individuals when converting cryptocurrencies to fiat currency are taxed either at the "flat tax" rate of 30%, or at the marginal tax rate.',
    'faq.q42': 'How to calculate capital gains for French companies?',
    'faq.a42': 'For French companies, capital gain or loss must be calculated at each fiat or crypto-to-crypto movement, using one of two methods: CUMP (weighted average unit cost) or FIFO (first in, first out).',

    // Stats Counter Section
    'stats.hours.label': 'Hours<br>Saved',
    'stats.transactions.label': 'Transactions<br>Processed',
    'stats.saved.label': 'Saved',

    // Why Section
    'why.title': 'Why <span class="gradient-text font-bold">Cryptoaccounting</span>?',
    'why.subtitle': 'A <span class="gradient-text">reliable and powerful</span> tool to master digital asset accounting',
    'why.advantage1.title': '1. Recognized expertise',
    'why.advantage1.text': 'Trust the expertise of Sanaa Moussaïd, member of the ANC\'s "Digital Assets" working group, to guide you in managing your crypto assets accounting.',
    'why.advantage2.title': '2. Time saving',
    'why.advantage2.text': 'Automate repetitive tasks and focus on your core business.',
    'why.advantage3.title': '3. Precision and compliance',
    'why.advantage3.text': 'Accurate calculations and complete traceability for compliant accounting.',
    'why.advantage4.title': '4. Expert support',
    'why.advantage4.text': 'Our professionals assist you in verifying and updating your accounting.',
    'why.advantage5.title': '5. Flexibility and customization',
    'why.advantage5.text': 'Adapt the software to your specific needs with customizable options.',
    'why.advantage6.title': '6. Data security',
    'why.advantage6.text': 'Your information is protected by advanced security protocols.',

    // Solution Section
    'solution.title': '<strong>Optimize your accounting</strong> with powerful tools, discover our solution.',
    'solution.card1.title': 'A clear vision: stock, flows, performance.',
    'solution.card1.text': 'Tracking per crypto-asset and platform. Realized gains/losses. Unrealized gains/losses. Alerts on attention points.',
    'solution.card1.microcopy': 'The dashboard view and the accounting report tell the same story: consistency + traceability.',
    'solution.card2.title': 'Accounting automation',
    'solution.card2.text': 'Reduce your manual efforts with our software that automates accounting transactions.',
    'solution.card3.title': 'Easy data import',
    'solution.card3.point1': 'API integration with major exchange platforms.',
    'solution.card3.point2': 'Import CSV, Excel, TXT files.',
    'solution.card3.point3': 'Direct reading of transactions on the blockchain.',
    'solution.card4.title': 'Automatic FEC generation',
    'solution.card4.text': 'Get your Accounting Entry File ready to be integrated into your accounting software.',
    'solution.card5.title': 'Accurate capital gains calculation',
    'solution.card5.text': 'Choose between FIFO or CUMP methods for calculation compliant with accounting standards.',

    // Testimonials Section
    'testimonials.title': 'What our users say',
    'testimonials.subtitle': 'Discover our clients\' feedback',
    'testimonials.quote1': 'CryptoAccounting saved me valuable time by automating the management of my crypto assets. The accuracy of calculations and compliance with standards are impeccable. An indispensable tool for any accounting professional.',
    'testimonials.name1': 'Alexandre',
    'testimonials.title1': 'Certified accountant',
    'testimonials.quote2': 'A complete and intuitive solution that allowed us to optimize our digital asset management. Customer support is responsive and professional. I highly recommend it to all industry professionals.',
    'testimonials.name2': 'Marie',
    'testimonials.title2': 'Financial director',
    'testimonials.quote3': 'The transition to crypto accounting has never been so simple. The interface is intuitive and the generated reports are detailed and compliant. A real time saver for our firm.',
    'testimonials.name3': 'Thomas',
    'testimonials.title3': 'Certified accountant',
    'testimonials.quote4': 'A major innovation in the crypto accounting sector. The platform perfectly meets regulatory requirements while remaining simple to use.',
    'testimonials.name4': 'Sophie',
    'testimonials.title4': 'Compliance manager',
    'testimonials.quote5': 'Excellent tool for crypto asset accounting management. Automatic capital gains calculation and report generation save us considerable time.',
    'testimonials.name5': 'Laurent',
    'testimonials.title5': 'Accounting firm director',

    // Report Content Section
    'report.title': 'What the Crypto Accounting report contains',
    'report.subtitle': 'A complete and compliant document to simplify your accounting management',
    'report.item1.title': 'Summary of crypto operations',
    'report.item1.text': 'Detailed classification of all your operations: purchase, sale, staking, and other crypto transactions',
    'report.item2.title': 'Capital gains and losses calculations',
    'report.item2.text': 'Automatic calculations using FIFO or CUMP method, compliant with French accounting standards',
    'report.item3.title': 'Euro valuation',
    'report.item3.text': 'Automatic conversion according to official regulatory exchange rates',
    'report.item4.title': 'Source documentation',
    'report.item4.text': 'Complete traceability of exchanges, wallets and platforms used for each operation',
    'report.item5.title': 'Automatic recommendations',
    'report.item5.text': 'Chart of accounts suggestions and optimized allocation for your structure',

    // Expertise Section
    'expertise.title': 'Accounting expertise available to everyone',
    'expertise.subtitle': 'Discover our dedicated solutions according to your profile',

    // Logos / Supported platforms
    'logos.title': 'Supported platforms and blockchains',
    'logos.importMessage': 'Platform or blockchain not supported?<br>Use our CSV format to import your transactions manually.',
    'logos.partnership.badge': 'New partnership',
    'logos.partnership.headline': 'Export your crypto accounting entries to <strong>Pennylane</strong> in just one click.',
    'logos.partnership.text': 'A smooth integration to automate your crypto entries all the way to your accounting software.',
    'logos.partnership.cta': 'Create an account',
    'logos.prev': 'Previous image',
    'logos.next': 'Next image',

    // How It Works Section
    'how.title': 'How does it work?',
    'how.subtitle': 'A simple and automated process for stress-free crypto accounting',
    'how.step1.title': 'Connect your accounts',
    'how.step1.text': 'Wallets, exchange platforms, cold wallets: everything is synchronized automatically. Secure connection with major market platforms.',
    'how.step2.title': 'Let the algorithm work',
    'how.step2.text': 'Our software automatically analyzes and categorizes all your crypto transactions according to their economic nature.',
    'how.step3.title': 'Validate and adjust',
    'how.step3.text': 'Review proposed categorizations and make necessary adjustments according to your specific requirements.',
    'how.step4.title': 'Export your accounting',
    'how.step4.text': 'Generate your reports, FEC and accounting entries ready to integrate into your management software.',

    // Checklist Section
    'checklist.badge': 'EXCLUSIVE DOCUMENT',
    'checklist.title': 'Crypto-Assets Checklist',
    'checklist.subtitle': 'For crypto accounting compliant with ANC standards',
    'checklist.content.title': 'What your checklist contains',
    'checklist.item1.title': 'Asset identification and management',
    'checklist.item1.text': 'Comprehensive wallets, private key security and internal creation procedures',
    'checklist.item2.title': 'Accounting and tax aspects',
    'checklist.item2.text': 'Treatment according to ANC regulation, capital gains calculation and mandatory declarations',
    'checklist.item3.title': 'Regulatory compliance',
    'checklist.item3.text': 'Compliance with PSAN, AML-KYC obligations and reporting to competent authorities',
    'checklist.item4.title': 'Procedures and controls',
    'checklist.item4.text': 'Implementation of validation processes, audit trail and operation documentation',
    'checklist.item5.title': 'Complete guide in 10 sections',
    'checklist.item5.text': 'Complete guide in 10 sections covering all essential aspects',
    // Checklist author credit
    'checklist.author': 'Created by Sanaa Moussaïd, member of the ANC working group',
    'checklist.advantages.title': 'The benefits of our checklist',

    // Checklist reCAPTCHA modal
    'checklist.modal.title': 'Security verification',
    'checklist.modal.subtitle': 'To download your checklist, please confirm you are not a robot.',
    'checklist.modal.captcha_prompt': 'Please confirm you are not a robot',
    'checklist.modal.submit': 'Validate and receive the checklist',
    'checklist.modal.load_error': 'Error: Unable to load reCAPTCHA. Please refresh the page.',
    'checklist.modal.render_error': 'Error: Unable to display the captcha. Please refresh the page.',

    // Checklist form
    'checklist.form.header': 'Download for free',
    'checklist.form.subheader': 'Our checklist for crypto-asset accounting management',
    'checklist.form.email.label': 'Email',
    'checklist.form.email.placeholder': 'your@email.com',
    'checklist.form.firstName.label': 'First name',
    'checklist.form.firstName.placeholder': 'Your first name',
    'checklist.form.lastName.label': 'Last name',
    'checklist.form.lastName.placeholder': 'Your last name',
    'checklist.form.company.label': 'Company',
    'checklist.form.company.placeholder': 'Your company name',
    'checklist.form.optin': "I agree to receive communications about Crypto Accounting's products and services.",
    'checklist.form.submit': 'Download my checklist',
    'checklist.form.disclaimer': 'Immediate access in your inbox. No spam.',
    'checklist.form.privacy': 'By submitting this form, you agree to our <a href="/CGU" class="text-[#133AC9] hover:underline">privacy policy</a>.',
    'checklist.form.loading': 'Processing...',
    'checklist.form.fix_errors': 'Please correct the errors in the form',
    'checklist.form.submit_success': 'Your checklist has been sent successfully! Check your inbox shortly.',
    'checklist.form.config_error': 'Incomplete configuration. Please contact the administrator.',
    'checklist.form.generic_error': 'An error occurred. Please try again.',

    // Form errors
    'errors.required': 'This field is required',
    'errors.email': 'Please enter a valid email address',
    'errors.min2': 'This field must contain at least 2 characters',

    // How section CTA
    'how.cta.title': 'Ready to simplify your crypto accounting?',
    'how.cta.subtitle': 'Join the hundreds of companies that trust Crypto Accounting',

    // Cookie consent
    'cookie.banner.title': 'Your privacy preferences',
    'cookie.banner.text': 'We use cookies to improve your experience and analyze site traffic.',
    'cookie.banner.link': 'Learn more about our privacy policy',
    'cookie.btn.customize': 'Customize',
    'cookie.btn.accept': 'Accept all',
    'cookie.btn.decline': 'Continue without accepting',
    'cookie.modal.title': 'Cookie settings',
    'cookie.modal.subtitle': 'Customize your privacy preferences',
    'cookie.modal.necessary.title': 'Necessary cookies',
    'cookie.modal.necessary.desc': 'Essential for the website to function',
    'cookie.modal.analytics.title': 'Analytics cookies',
    'cookie.modal.analytics.desc': 'Help us improve your experience',
    'cookie.modal.preferences.title': 'Preference cookies',
    'cookie.modal.preferences.desc': 'Save your preferences',
    'cookie.modal.save': 'Save my preferences',
    'cookie.manager.label': 'Manage cookies',

    // Contact page
    'contact.title': 'Contact our experts',
    'contact.subtitle': 'Our team is here to help you manage your crypto accounting.',
    'contact.left.title': 'Stay in touch',
    'contact.left.text': 'Choose the method that suits you best to contact us.',
    'contact.left.email': 'Email',
    'contact.left.address': 'Address',
    'contact.form.title': 'Send us a message',
    'contact.form.firstName.label': 'First name',
    'contact.form.firstName.placeholder': 'Your first name',
    'contact.form.lastName.label': 'Last name',
    'contact.form.lastName.placeholder': 'Your last name',
    'contact.form.email.label': 'Email',
    'contact.form.email.placeholder': 'your@email.com',
    'contact.form.phone.label': 'Phone',
    'contact.form.phone.placeholder': '+1 555 123 4567',
    'contact.form.message.label': 'Message',
    'contact.form.message.placeholder': 'Your message...',
    'contact.form.submit': 'Send message',

    // Blog page
    'blog.title': 'Explore the evolution of crypto accounting',
    'blog.recent': 'Recent Articles',
    'blog.all': 'All our articles',
    'blog.subtitle': 'Subscribe to stay informed about crypto accounting',
    'blog.newsletter.placeholder': 'Your email address',
    'blog.newsletter.cta': 'Subscribe',
    'blog.timeline.label': 'Timeline',

    // Pricing page
    'pricing.title': 'Simple and transparent <br>pricing',
    'pricing.header.p1': 'Not comfortable with the configuration?<br>Don’t worry, we’ve got you covered!',
    'pricing.header.p2': 'We review your files with you, highlight the important points, and if needed, we handle everything for you.',
    'pricing.header.p3': '👉 Support for accounting firms with setup, transaction analysis, and crypto flow integration:<br>starting at €500 depending on complexity.',
    'pricing.header.enterprise.title': 'Company',
    'pricing.header.enterprise.badge': 'Automation',
    'pricing.header.enterprise.desc':
      'Automatic categorization of your operations: our algorithm automatically recognizes your transactions.',
    'pricing.header.enterprise.item1': 'Staking',
    'pricing.header.enterprise.item2': 'Trading',
    'pricing.header.enterprise.item3': 'Mining',
    'pricing.header.enterprise.item4': 'Rewards',
    'pricing.header.enterprise.item5': 'Liquidity Pools',
    'pricing.header.enterprise.item6': 'Airdrop',
    'pricing.header.accountants.title': 'For accounting firms',
    'pricing.header.accountants.badge': 'Ready-to-audit',
    'pricing.header.accountants.desc': 'A case file with an audit trail ready to be reviewed by your teams:',
    'pricing.header.accountants.item1': 'Operations statement',
    'pricing.header.accountants.item2': 'Capital gains/losses calculation file per asset and portfolio',
    'pricing.header.accountants.item3': 'A FEC ready to be integrated into your software (including Pennylane)',
    'pricing.header.accountants.item4': 'Analytical accounting by wallet/exchange and by platform',
    'pricing.header.support':
      'Support for accounting firms with setup and crypto flow integration:<br>free guidance on a pilot case<br>then from €500 depending on complexity.',
    'pricing.header.cta': 'Let’s talk 🤝',
    'pricing.plan.starter': 'Starter',
    'pricing.plan.basic': 'Basic',
    'pricing.plan.standard': 'Standard',
    'pricing.plan.pro': 'Pro',
    'pricing.plan.custom': 'CUSTOM',
    'pricing.contact': 'Contact us',
    'pricing.per_month_ht': '/ month excl. VAT',
    'pricing.per_year_ht': '/ year excl. VAT',
    'pricing.toggle.monthly': 'Monthly',
    'pricing.toggle.yearly': 'Yearly',
    'pricing.start': 'Get started',
    'pricing.plan.starter.desc': 'Take your first steps with a tool designed for small volumes',
    'pricing.plan.basic.desc': 'Step up with more transactions and users.',
    'pricing.plan.standard.desc': 'The ideal balance for your growing needs',
    'pricing.plan.pro.desc': 'Perfect for handling a large number of transactions with peace of mind.',
    'pricing.features.users2': '2 users',
    'pricing.features.users_multiple': 'Multiple users',
    'pricing.features.sources_unlimited': 'Unlimited import sources',
    'pricing.features.tx200': '200 transactions over 12 months',
    'pricing.features.tx1500': '1,500 transactions over 12 months',
    'pricing.features.tx10000': '10,000 transactions over 12 months',
    'pricing.features.tx50000': '50,000 transactions over 12 months',
    'pricing.features.tag_ops': 'Tag operations on the platform',
    'pricing.features.custom_chart': 'Custom chart of accounts',
    'pricing.features.fec': 'FEC generation',
    'pricing.features.fifo_cump': 'FIFO and WAC (CUMP) calculations',
    'pricing.features.result_per_asset': 'Result file per digital asset',
    'pricing.features.statement_per_asset': 'Operations statement per digital asset',
    'pricing.custom.usage': 'Tailored usage for larger cases',
    'pricing.custom.benefit1': '<strong>Personalized service:</strong> All features of previous plans tailored to your specific needs.',
    'pricing.custom.benefit2': '<strong>Maximum flexibility:</strong> Transactions, wallet accounts, and custom exchange connections.',
    'pricing.custom.benefit3': '<strong>Priority support:</strong> Extended and personalized support service.',

    // Legal page
    'legal.hero.title': 'Legal Notice',
    'legal.hero.subtitle': 'Legal and regulatory information about Crypto Accounting',
    'legal.identity.title': 'Website owner identity',
    'legal.identity.intro': 'The website is owned by SAS CRYPTO ACCOUNTING',
    'legal.identity.capital': '<span class="font-medium">Share capital:</span> €25,500',
    'legal.identity.address': '<span class="font-medium">Registered office:</span> 40 RUE DE CRIMEE, 75019 PARIS',
    'legal.identity.ceo': '<span class="font-medium">President:</span> Sanaa MOUSSAID',
    'legal.identity.email': '<span class="font-medium">Email address:</span> contact@cryptoaccounting.fr',
    'legal.identity.siren': '<span class="font-medium">SIREN:</span> 905 297 941',
    'legal.identity.ape': '<span class="font-medium">APE code:</span> 6201Z',
    'legal.identity.vat': '<span class="font-medium">EU VAT number:</span> FR50 905297941',
    'legal.publication.title': 'Publication director',
    'legal.publication.text': 'Publication director: Sanaa MOUSSAID',
    'legal.hosting.title': 'Hosting',
    'legal.hosting.text': 'SAS OVH<br>2 rue Kellermann<br>59100 ROUBAIX<br>09 72 10 10 07',
    'legal.rgpd.title': 'Declaration and user rights',
    'legal.rgpd.text': 'In accordance with deliberation no. 2006-138 of 09/05/2006 and the General Data Protection Regulation (GDPR), SAS CRYPTO ACCOUNTING guarantees you the right to object, access, rectify, erase, and restrict the processing of personal data concerning you. This right can be exercised by email at contact@cryptoaccounting.fr.',
    'legal.marketing.title': 'Information and offer emails',
    'legal.marketing.text': 'If you have a professional account (company or accounting firm), Crypto Accounting may email you about its services, on the basis of its legitimate interest. If you are an individual, we only email you with your consent, given when you sign up. Every email includes an unsubscribe link; you can also write to us at contact@cryptoaccounting.fr. Emails related to your account (login codes, invoices) are not affected. These emails are sent by our service providers Brevo and Amazon Web Services.',
    'legal.links.title': 'Hyperlinks',
    'legal.links.text': 'Links to other websites are provided for reference purposes only. SAS CRYPTO ACCOUNTING declines all responsibility for their content. Any use of a link to cryptoaccounting.fr must be authorized in advance.',
    'legal.content.title': 'Website content',
    'legal.content.text': 'The website content is intended for general information only. It does not constitute advice or an offer of services. CRYPTO ACCOUNTING cannot be held liable for how this content is used.',
    'legal.rights.title': 'Copyright',
    'legal.rights.text': 'All elements composing the website are the exclusive property of SAS CRYPTO ACCOUNTING. Any reproduction without express authorization is prohibited.<br> © CRYPTO ACCOUNTING - https://cryptoaccounting.fr/ – all rights reserved.',

    // Terms (CGU) page
    'terms.hero.title': 'Terms and Conditions of Use',
    'terms.hero.subtitle': 'Conditions and terms of use for Crypto Accounting services',
    'terms.article1.title': 'ARTICLE 1. PURPOSE',
    'terms.article1.content': `<p>These "terms and conditions of use" define the conditions and methods for providing the services offered via https://app.cryptoaccounting.fr.</p>
<p>SAS Crypto Accounting has designed and operates software available in SaaS mode. The software offers an automated accounting tool for transactions involving digital assets and calculates capital gains using two methods accepted in France: FIFO and WAC (CMUP).</p>
<p>SAS Crypto Accounting is not a chartered accountancy firm registered with the Order, provides no bookkeeping services, and does not act as such for clients.</p>`,
    'terms.article2.title': 'ARTICLE 2. ACCEPTANCE OF THE TERMS',
    'terms.article2.content': `<p>Access to and use of the Application are subject to the user's full and express acceptance of these Terms. Therefore, access to the Software requires subscription to the online offer, which constitutes unreserved acceptance of the Terms by the Client.</p>
<p>These Terms may be updated at any time without prior notice by SAS Crypto Accounting. Users are invited to consult them regularly. Any update becomes effective upon publication in the "Terms and Conditions of Use" section of the website.</p>`,
    'terms.article3.title': 'ARTICLE 3. SCOPE OF THE TERMS',
    'terms.article3.content': `<p>These Terms apply, without restriction or reservation, to the use of the services offered on the application by users.</p>
<p>These Terms are accessible at any time on the website https://cryptoaccounting.fr/.</p>
<p>Changes to these Terms become binding on users once they are published online and cannot be applied to transactions concluded beforehand.</p>
<p>The conditions of use apply from the user's first connection to the website and for the full duration of the services.</p>`,
    'terms.article4.title': 'ARTICLE 4. TERM AND RENEWAL OF THE AGREEMENT',
    'terms.article4.content': `<p>The agreement takes effect on the date the user subscribes. The payment terms for the services are described on the website.</p>
<p>Payment may be made:</p>
<ul class="list-disc pl-6 space-y-2">
  <li>By automatic debit from the user's bank card number. The debit is initiated by the payment provider used on the site. It alone keeps the user's banking details for the purpose of processing payments. Crypto Accounting does not store any banking information.</li>
  <li>By bank transfer within 48 hours from a French IBAN.</li>
  <li>By payment in crypto-assets to the public address previously provided by SAS Crypto Accounting.</li>
</ul>
<p>Payment in crypto-assets must be authorised in advance by the company.</p>
<h3 class="text-xl font-semibold mt-6 mb-4 text-primary">Late payment and payment incidents</h3>
<p>In the event of late payment, penalties become due on the day following the due date shown on the invoice. Unless otherwise stipulated, and provided such stipulation does not set a rate lower than three times the legal interest rate, the rate applied will be equal to that of the European Central Bank for its most recent refinancing operation plus 10 percentage points.</p>
<p>A fixed compensation of €40 for recovery costs is also automatically due in case of late payment.</p>
<p>Access is restored only after payment of the €40 compensation and any late payment interest.</p>`,
    'terms.article5.title': 'ARTICLE 5. ADMINISTRATION AND SERVICES',
    'terms.article5.content': `<p>The user agrees to update their information in the dedicated interface whenever it changes. The service provider cannot be held liable if it was not informed of a change in the client's situation or of incorrect information.</p>
<p>Access to the software requires an active user account assigned by the client to each individual user.</p>
<p>The Crypto Accounting application is SaaS software for accounting for digital assets and calculating the related capital gains.</p>
<p>The user has access to the services described on the website, according to the terms, features, and technical resources defined by Crypto Accounting.</p>`,
    'terms.article6.title': 'ARTICLE 6. INTELLECTUAL PROPERTY',
    'terms.article6.content': `<p>All intellectual property elements used by the Company, in particular the Software, are protected by intellectual property laws and other applicable regulations, including copyright. The Software and all of its components-such as texts, images, videos, photographs, trademarks, logos, corporate names, and domain names-are the exclusive property of the company or its partners. Any unauthorised use or reproduction, even partial, of the software, content, or templates used to generate documents is strictly prohibited.</p>`,
    'terms.article7.title': 'ARTICLE 7. INFRASTRUCTURE OPERATING THE SERVICE',
    'terms.article7.content': `<p>The services operate in a secure environment that ensures access control, service availability, data storage and protection, hardware operation, and connectivity to remote networks.</p>`,
    'terms.article8.title': 'ARTICLE 8. COMMITMENTS AND LIABILITY OF SAS CRYPTO ACCOUNTING',
    'terms.article8.content': `<p>The service provider undertakes, under a best-efforts obligation, to exercise all due care and diligence necessary to deliver a quality service. It strives to ensure the continuity, availability, and quality of the Services. The Services are available 24/7, except during Maintenance.</p>
<p>However, the service provider does not guarantee that the Software covers every possible digital asset process or operation carried out by the client, given the existing legal, tax, and accounting grey areas. Consequently, the service provider makes no commitment regarding the accuracy or completeness of the information provided, published, or processed by users-particularly when there is a failure to supply all wallets held on the Platform.</p>
<p>Transactions that are not tagged or identified (inter-wallet, supplier, customer, etc.) are posted to suspense account “471 – Compte d'attente”. The client must then give their accountant the necessary information so that entries can be posted to their own accounting software in compliance with accounting regulations.</p>`,
    'terms.article9.title': 'ARTICLE 9. CLIENT OBLIGATIONS AND LIABILITY',
    'terms.article9.content': `<p>The client is solely responsible for use of the software by the users they authorise, for the data they submit or make available, and for the information transmitted, processed, and updated. The client undertakes to provide accurate, truthful, and complete information. The user agrees to supply all information required to process their digital assets.</p>
<p>The client undertakes to back up their data. To this end, the client authorises the Company to host and access the data for the purposes of this agreement. The company reserves the right to verify that the client uses the software in compliance with the agreement.</p>
<p>The user agrees in particular to respect third-party rights, including intellectual property rights such as copyrights, patents, and trademarks. Consequently, the service provider cannot be held liable for the content of the information transmitted, distributed, or collected, nor for how it is updated.</p>
<p>The client bears sole responsibility for any service malfunction resulting from use by members of their staff or by any person to whom the user has supplied their password.</p>
<p>The user is responsible for paying the price for accessing and using the application.</p>
<p>In the event of non-payment or breach of any obligation (under these general or specific terms), the service provider reserves the right to suspend access to the service without prior notice.</p>`,
    'terms.article10.title': 'ARTICLE 10. COOPERATION BETWEEN THE PARTIES',
    'terms.article10.content': `<p>The parties undertake to cooperate actively, continuously, and in good faith to ensure the proper performance of the Agreement and the smooth delivery of the Service.</p>
<p>To this end, the Parties agree to:</p>
<ul class="list-disc pl-6 space-y-2">
  <li>Actively and seriously fulfil their respective obligations</li>
  <li>Refrain from any behaviour that could affect or hinder the proper functioning of the services or the other Party's performance of its obligations</li>
  <li>Provide each other, within a sufficient, useful, and reasonable timeframe, with all information and documents necessary to perform the agreement</li>
  <li>Notify each other without delay of any difficulty encountered in performing their respective obligations and/or in providing the services</li>
</ul>
<p>In this context, the parties undertake to consult one another as soon as possible to identify and implement the most appropriate solution.</p>
<p>The parties agree to communicate on a continuous basis and to meet as often as necessary to ensure the agreement runs smoothly.</p>`,
    'terms.article11.title': 'ARTICLE 11. SERVICE SUSPENSION',
    'terms.article11.content': `<p>The agreement may be suspended and access to the application temporarily interrupted by the service provider in the following cases:</p>
<ul class="list-disc pl-6 space-y-2">
  <li>Maintenance operations. The user is informed that the service provider may carry out service updates without prior authorisation.</li>
  <li>Failure to comply with the commitments set out in Article 15 (User obligations)</li>
  <li>Non-payment for the service</li>
</ul>`,
    'terms.article12.title': 'ARTICLE 12. CONFIDENTIALITY',
    'terms.article12.content': `<p>The parties agree that all information, data, deliverables, and know-how-whether or not protected by intellectual property laws, regardless of their form or nature (commercial, industrial, technical, financial, etc.)-communicated by one Party (the “Discloser”) to the other (the “Recipient”), or of which they become aware during performance of the agreement, shall be treated as confidential.</p>
<p>The following information is not considered Confidential Information:</p>
<ul class="list-disc pl-6 space-y-2">
  <li>Information already in the Recipient's possession before disclosure by the Discloser</li>
  <li>Information that is in the public domain on the date the agreement is accepted</li>
  <li>Information independently developed by the Recipient</li>
</ul>
<p>The parties agree to comply with the obligations set out in this article throughout the term of the agreement and for three (3) years following the agreement's expiry or termination.</p>`,
    'terms.article13.title': 'ARTICLE 13. PERSONAL DATA PROTECTION POLICY',
    'terms.article13.content': `<p>The parties undertake to comply with the regulations in force regarding the protection of personal data, in particular French Law no. 78-17 of 6 January 1978 as amended, and EU Regulation 2016/679 of 27 April 2016, hereinafter referred to as the "GDPR".</p>
<h3 class="text-xl font-semibold mt-6 mb-4 text-primary">Contact and complaints</h3>
<p>All questions raised in this Policy, as well as requests to exercise data subject rights, are handled by the Data Protection Officer, who can be contacted as follows:</p>
<ul class="list-disc pl-6 space-y-2">
  <li>By email: rgpd@cryptoaccounting.fr</li>
  <li>By post: to the attention of the Data Protection Officer, SAS Crypto Accounting, 7 rue Mortinat, 92600 Asnière-sur-Seine, France</li>
</ul>`,
    'terms.article14.title': 'ARTICLE 14. GOVERNING LAW – JURISDICTION',
    'terms.article14.content': `<p>This agreement is governed by French law.</p>
<p>Subject to the user being classified as a “professional”, any dispute relating to the interpretation or performance of this agreement shall fall under the exclusive jurisdiction of the Commercial Court of Nanterre.</p>`,

    // Accompagnement page extras
    'accom.timeline.title': '5 steps to an actionable file',
    'accom.xref.compliance': 'Need AML compliance? <a href=”/compliance” class=”font-semibold text-[#133AC9] hover:underline transition-colors”>Discover our Compliance & AML offering.</a>',

    // Compliance page extras
    'compliance.xref.accompagnement': 'Complex crypto case? <a href=”/accompagnement” class=”font-semibold text-[#133AC9] hover:underline transition-colors”>Discover our full case support.</a>',

    // Calculateur page
    'calc.hero.title': 'Individuals||your crypto tax return (form 2086), automated',
    'calc.hero.subtitle': 'Import your transactions, generate your form 2086, file with peace of mind.',
    'calc.hero.text': 'Automatic import via API or file, form 2086 boxes ready to copy over. The old online calculator is now built into the app.',
    'calc.hero.cta.signup': 'Create my account',
    'calc.hero.cta.pricing': 'See pricing',
    'calc.features.title': 'A tax return ||simple and automated',
    'calc.features.subtitle': 'Focus on your investments, we handle the tax complexity.',
    'calc.features.f1.title': 'Universal connection',
    'calc.features.f1.text': 'Automatic import of your transactions via API, CSV/Excel file or onchain.',
    'calc.features.f2.title': 'Compliant calculation',
    'calc.features.f2.text': 'Method of article 150 VH bis of the French tax code, €305 threshold, price quotes at the date of each disposal.',
    'calc.features.f3.title': 'Built-in checks',
    'calc.features.f3.text': 'Missing price quotes, unclassified operations and exemption under the €305 threshold: everything is flagged in your tax return.',
    'calc.features.f4.title': 'Foreign accounts',
    'calc.features.f4.text': 'Your foreign platforms are detected for the 3916-bis annex.',
    'calc.features.f5.title': 'Accountant sharing',
    'calc.features.f5.text': 'Invite your accountant in read-only mode on your tax return.',
    'calc.features.f6.title': 'Year after year',
    'calc.features.f6.text': 'Your tax returns are saved: the acquisition history carries over automatically.',
    'calc.solution.badge': 'Our Solution',
    'calc.solution.title': 'From your crypto portfolio||to your tax return',
    'calc.solution.subtitle': 'The features that simplify your crypto tax return',
    'calc.solution.card1.title': 'Portfolio tracking',
    'calc.solution.card1.text': 'View your assets, capital gains and portfolio value, <strong>updated with every import</strong>.',
    'calc.solution.card2.title': 'Data import',
    'calc.solution.card2.text': '<strong>API, CSV, blockchain</strong>, connect your exchanges and wallets, all your sources in one click.',
    'calc.solution.card3.title': 'Automation',
    'calc.solution.card3.text': '<strong>Automatic categorization</strong>: trades, staking, airdrops, transfers between your wallets, no manual rework.',
    'calc.solution.card4.title': 'Form 2086 tax return',
    'calc.solution.card4.text': 'Disposal register with the <strong>pre-calculated form boxes</strong>, staking income, <strong>Excel and PDF</strong> exports.',
    'calc.work.badge': 'How it works',
    'calc.work.title': '<span class="gradient-text">From your wallets to form 2086 in 3 steps</span>',
    'calc.work.subtitle': 'A transparent process: you see the price of your tax return before paying.',
    'calc.work.step1.title': 'Secure connection',
    'calc.work.step1.text': 'Create your Individual account and connect your sources: exchange APIs, CSV/Excel files or direct blockchain reading.',
    'calc.work.step2.title': 'A price based on your file',
    'calc.work.step2.badge': 'No subscription',
    'calc.work.step2.text': 'Your transactions are categorized and viewable in the app: trades, staking, transfers between your wallets. When it\'s time to file, choose the year: the price is shown based on the <strong>number of transactions</strong> in your file.<br><span class="text-primary-600 font-medium">A single payment per tax year: €49, €99 or €229.</span>',
    'calc.work.step3.title': 'Calculation & filing',
    'calc.work.step3.text': 'As soon as the payment is confirmed, your capital gains are calculated under <strong>article 150 VH bis</strong>, with price quotes at the date of each disposal: the <strong>detailed register</strong> with the form boxes, ready to copy onto impots.gouv.fr.',
    'calc.work.step3.item1': 'Pre-calculated form 2086 boxes||',
    'calc.work.step3.item2': 'Staking income (BNC)||',
    'calc.work.step3.item3': '3916-bis annex||',
    'calc.work.step3.item4': 'Excel exports & PDF report||',
    'calc.pricing.badge': 'Individual pricing',
    'calc.pricing.title': 'Simple pricing, no subscription',
    'calc.pricing.subtitle': 'A single payment per tax year, when you generate your tax return: the price is determined automatically by the number of transactions in your file. You import and view your transactions before paying.',
    'calc.pricing.over10k': 'More than 10,000 transactions? <a href="/contact-us" class="text-primary-600 font-semibold underline underline-offset-2 hover:text-primary-700">Contact us</a>.',
    'calc.price.per_year': '/ tax year incl. VAT',
    'calc.price.cta': 'Get started',
    'calc.price.t200.name': 'Essential',
    'calc.price.t200.desc': 'A few operations in the year',
    'calc.price.t200.f1': 'File up to 200 transactions',
    'calc.price.t200.f5': 'Art. 150 VH bis calculation, net capital gain and tax estimate',
    'calc.price.t200.f2': 'Detailed register, form 2086 boxes',
    'calc.price.t200.f3': 'Excel and PDF exports',
    'calc.price.t200.f4': 'Recalculations and accountant sharing included',
    'calc.price.t200.f6': 'API and CSV/Excel import',
    'calc.price.t200.f7': 'Staking income and foreign accounts (form 3916-bis)',
    'calc.price.t1k.badge': 'POPULAR',
    'calc.price.t1k.name': 'Tax return',
    'calc.price.t1k.desc': 'The volume of a regular investor',
    'calc.price.t1k.f1': 'File up to 1,000 transactions',
    'calc.price.t1k.f2': 'Everything in Essential',
    'calc.price.t10k.name': 'Tax return +',
    'calc.price.t10k.desc': 'For active traders and DeFi',
    'calc.price.t10k.f1': 'File up to 10,000 transactions',
    'calc.price.t10k.f2': 'Everything in Essential',
    'calc.pricing.note': 'A tax-filing assistance tool, not tax advice: calculation parameters (rate, €305 threshold) are fully transparent, and amounts remain to be checked and reported under your responsibility.',
    'calc.guides.badge': 'Essential guides',
    'calc.guides.title': '3 must-reads',
    'calc.guides.subtitle': 'The essentials to understand crypto taxation for individuals.',
    'calc.guides.card1.title': 'The DAC8 directive in 5 points',
    'calc.guides.card1.text': 'What the new European directive changes for reporting your crypto assets.',
    'calc.guides.card2.title': 'Securing your digital assets',
    'calc.guides.card2.text': 'Vigilance stakes for crypto holders and their accountants.',
    'calc.guides.card3.title': 'Blockchain and crypto assets: concepts and stakes',
    'calc.guides.card3.text': 'The basics to understand what you hold and what you report.',
    'calc.faq.title': 'FAQ',
    'calc.faq.subtitle': 'Any questions?',
    'calc.faq.q2': 'What happened to the old free online calculator?',
    'calc.faq.a2': 'It has been integrated into the Crypto Accounting app: automatic imports from your exchanges, price quotes at the date of each disposal, tax returns saved from one year to the next. You import and view your transactions, then pay when you generate your tax return: from €49 incl. VAT per tax year, with no subscription.',
    'calc.faq.q3': 'Is the calculation compliant with form 2086?',
    'calc.faq.a3': 'The engine applies the method of article 150 VH bis of the French tax code: total portfolio acquisition price, disposals against fiat currency taxable, crypto-to-crypto trades in tax deferral, €305 exemption threshold. It also flags your staking income and your foreign digital asset accounts (form 3916-bis). It is a filing assistance tool: amounts remain to be reported and checked under your responsibility.',
    'calc.faq.q4': 'Can I share my tax return with my accountant?',
    'calc.faq.a4': 'Yes, you can invite your accountant in read-only mode on your tax return. Accounting firms can also manage individual client files directly from their own workspace.',
    'calc.faq.q5': 'How does pricing work?',
    'calc.faq.a5': 'You import and view your transactions in the app, then pay once, per tax year, when you generate your tax return: calculation, net capital gain, tax estimate, detailed register and exports. The price is determined automatically by the size of your file: €49 incl. VAT up to 200 transactions, €99 up to 1,000, €229 up to 10,000, beyond that contact us. No subscription, no renewal. Several years to catch up? Contact us.',
    'calc.final.title': 'Ready to file your crypto taxes ||with peace of mind?',
    'calc.final.subtitle': 'Import your transactions, choose the year and get your form 2086 ready to copy over.',
    'calc.also.experts': 'Accountants',
    'calc.also.tpe': 'Businesses & Professionals',

    // Comment ca marche (How it works) page
    'ccm.hero.title': 'How it works',
    'ccm.hero.subtitle': 'From data import to accounting export, Crypto Accounting follows a simple, structured and controlled process.',
    'ccm.overview.title': 'The process at a glance',
    'ccm.phase1': 'Phase 1 : Prepare your data',
    'ccm.phase2': 'Phase 2 : Check and structure',
    'ccm.phase3': 'Phase 3 : Produce and export',
    'ccm.step1.title': 'Data import',
    'ccm.step1.text': 'Crypto Accounting works from your transaction flows. You can import your data in 3 ways:',
    'ccm.step2.title': 'Transaction visualization and preparation',
    'ccm.step2.text': 'Once the data is integrated, access the transaction list and click on <strong>”Load quotes”</strong>.',
    'ccm.step3.title': 'Automatic categorization',
    'ccm.step3.text': 'Crypto Accounting automatically categorizes most operations:',
    'ccm.step4.title': 'Data control in the dashboard',
    'ccm.step4.text': 'The dashboard allows you to verify data integrity through several alerts:',
    'ccm.step5.title': 'User setup',
    'ccm.step5.text': 'Before generating accounting, add the relevant users to the case:',
    'ccm.step6.title': 'Defining accounting periods',
    'ccm.step6.text': 'In the accounting module, define one or more accounting periods. The software relies on the selected periods to correctly structure the periods to process.',
    'ccm.step7.title': 'Accounting generation',
    'ccm.step7.text': 'Once the data is verified, click <strong>generate accounting</strong>.',
    'ccm.step8.title': 'Capital gains/losses calculation',
    'ccm.step8.text': 'Once accounting is validated, access the unrealized values module. Calculate your capital gains or losses at your preferred frequency:',
    'ccm.step9.title': 'Export and integration',
    'ccm.step9.text': 'Entries can be exported or integrated directly:',
    'ccm.cta.title': 'Ready to structure your crypto accounting?',
    'ccm.cta.subtitle': 'Create your account for free or request a demo to see the solution in action.',
    'ccm.cta.register': 'Create an account',
    'ccm.cta.demo': 'Request a demo',

    // Webinars page
    'webinar.hero.badge': 'One free webinar per month \u2014 30 min with Q&A \u2014 at 1 PM',
    'webinar.hero.title': 'Our webinars',
    'webinar.hero.subtitle': 'Practical sessions to structure your crypto cases, understand taxation and anticipate regulatory changes.',
    'webinar.w1.title': 'Structuring a crypto case for accounting firms',
    'webinar.w1.subtitle': 'In 20 minutes, an actionable methodology to qualify, verify and deliver an audit-ready crypto case.',
    'webinar.w2.title': 'Declaring crypto-assets without mistakes',
    'webinar.w2.subtitle': 'Essential tax rules, common errors and how to anticipate growing transparency.',
    'webinar.w3.title': 'MiCA and DAC8: what changes in practice',
    'webinar.w3.subtitle': 'Practical consequences of the new European regulations on your cases and processes.',
    'webinar.w4.title': 'Accounting for crypto-assets in businesses',
    'webinar.w4.subtitle': 'Qualify, account for and secure crypto operations: the operational framework for businesses.',
    'webinar.cta.title': 'All our webinars are free',
    'webinar.cta.subtitle': '30 minutes of practical content + Q&A. A monthly appointment to build expertise in crypto accounting.',
    'webinar.cta.btn': 'Contact us to register',

    // Partenaires : Hub
    'partners.hub.badge': 'Partners & integrations',
    'partners.hub.h1': 'Our partners',
    'partners.hub.subtitle': 'Crypto Accounting works hand in hand with the best accounting tools and firms.',
    'partners.hub.intro': 'Whether you use accounting software or work with an accounting firm, we handle the entire crypto side of your accounting. Pick your partner below to see how it works.',
    'partners.hub.tag.integration': 'Integration',
    'partners.hub.tag.cabinet': 'Partner firm',
    'partners.hub.pennylane.desc': 'Export your crypto accounting entries and your FEC straight to Pennylane, in one click, with no re-keying.',
    'partners.hub.tiime.desc': 'Get your crypto accounting entries and your FEC inside Tiime through our dedicated export, with no re-keying.',
    'partners.hub.dougs.desc': 'Working with the Dougs accounting firm and holding crypto? We take care of the entire crypto side of your accounting.',
    'partners.hub.tag.tool': 'Partner tool',
    'partners.hub.build.desc': 'Paris-based chartered accounting firm supporting entrepreneurs, with dedicated expertise on crypto assets.',
    'partners.hub.fidunion.desc': 'National network of independent chartered accountants and statutory auditors, partnering on crypto cases.',
    'partners.hub.afigec.desc': '« An accountant for every profile » : Afigec\'s signature promise, now extended to your crypto matters.',
    'partners.hub.groupeexcel.desc': 'Federation of regional networks of independent chartered accountants, partnering on crypto accounting matters.',
    'partners.hub.smoney.desc': 'Multi-currency business banking platform for companies operating with crypto and internationally.',
    'partners.hub.chaincomply.desc': 'Crypto compliance platform (source-of-wealth, audit-ready) for AML/KYC requirements on digital assets.',
    'partners.hub.learnmore': 'Learn more',
    'partners.hub.visit': 'Visit website',
    'partners.hub.how.title': 'Two ways to work together',
    'partners.hub.how.subtitle': 'Depending on your partner, we connect directly to your accounting tool, or we hand you a crypto file ready to forward.',
    'partners.hub.how.integration.title': 'Software integrations',
    'partners.hub.how.integration.text': 'With Pennylane and Tiime, Crypto Accounting is connected to your accounting software: your crypto entries and your FEC are exported there in one click.',
    'partners.hub.how.cabinet.title': 'Partner firms',
    'partners.hub.how.cabinet.text': 'With a firm such as Dougs, we handle the crypto side and hand you a complete file (FEC and audit trail) that you forward to your accountant.',
    'partners.hub.cta.title': 'Ready to simplify your crypto accounting?',
    'partners.hub.cta.text': 'Create your account in a few minutes, or contact us to offer Crypto Accounting to your clients.',
    'partners.hub.cta.signup': 'Create an account',
    'partners.hub.cta.become': 'Become a partner',

    // Partenaires, cross-links (shared)
    'partners.xref.label': 'Discover also',
    'partners.xref.hub': 'All our partners',
    'partners.xref.experts': 'Solution for accountants',

    // Partenaires : Pennylane
    'partners.pennylane.hero.badge': 'Integration partner',
    'partners.pennylane.hero.h1': 'Your crypto accounting, straight into <span class="text-blue-200">Pennylane</span>.',
    'partners.pennylane.hero.text': 'Crypto Accounting is integrated with Pennylane. Your crypto accounting entries and your FEC are exported to your accounting software in one click, with no re-keying.',
    'partners.pennylane.hero.cta': 'Create an account',
    'partners.pennylane.intro.title': 'An integration built for accountants',
    'partners.pennylane.intro.text': 'Crypto Accounting reconstructs, computes and formats the entire crypto accounting of your clients. The result is then sent to Pennylane, ready to be added to their accounting file.',
    'partners.pennylane.intro.point1': 'Crypto accounting entries ready to use',
    'partners.pennylane.intro.point2': 'FEC compliant with the ANC 2026-01 regulation',
    'partners.pennylane.intro.point3': 'No re-keying, no data loss',
    'partners.pennylane.how.title': 'How it works',
    'partners.pennylane.how.step1.title': 'Connect your crypto sources',
    'partners.pennylane.how.step1.text': 'Link your wallets and exchanges to Crypto Accounting. Transactions are imported automatically.',
    'partners.pennylane.how.step2.title': 'Your entries are generated',
    'partners.pennylane.how.step2.text': 'Operation categorisation, capital gains calculation and FEC production: everything is automated and compliant.',
    'partners.pennylane.how.step3.title': 'Export to Pennylane in one click',
    'partners.pennylane.how.step3.text': 'Your crypto accounting entries and your FEC are sent to Pennylane, ready to be added to your file.',
    'partners.pennylane.benefits.title': 'Why use the Pennylane integration',
    'partners.pennylane.benefit1.title': 'Zero re-keying',
    'partners.pennylane.benefit1.text': 'No more copy-pasting or files to redo by hand: entries land directly in Pennylane.',
    'partners.pennylane.benefit2.title': 'Compliance guaranteed',
    'partners.pennylane.benefit2.text': 'The FEC and entries comply with the ANC 2026-01 regulation: a solid, justifiable crypto file.',
    'partners.pennylane.benefit3.title': 'Valuable time saved',
    'partners.pennylane.benefit3.text': 'Crypto accounting, often time-consuming, becomes a simple step in your Pennylane workflow.',
    'partners.pennylane.cta.title': 'Connect your crypto accounting to Pennylane',
    'partners.pennylane.cta.text': 'Create your account and export your first crypto entries to Pennylane today.',
    'partners.pennylane.cta.btn': 'Create an account',

    // Partenaires : Tiime
    'partners.tiime.hero.badge': 'Integration partner',
    'partners.tiime.hero.h1': 'Your crypto accounting, straight into <span class="text-blue-200">Tiime</span>.',
    'partners.tiime.hero.text': 'Crypto Accounting is integrated with Tiime. Your crypto accounting entries and your FEC are exported to your accounting software in one click, with no re-keying.',
    'partners.tiime.hero.cta': 'Create an account',
    'partners.tiime.intro.title': 'An integration built for accountants',
    'partners.tiime.intro.text': 'Crypto Accounting reconstructs, computes and formats the entire crypto accounting of your clients. The result is then sent to Tiime, ready to be added to their accounting file.',
    'partners.tiime.intro.point1': 'Crypto accounting entries ready to use',
    'partners.tiime.intro.point2': 'FEC compliant with the ANC 2026-01 regulation',
    'partners.tiime.intro.point3': 'No re-keying, no data loss',
    'partners.tiime.how.title': 'How it works',
    'partners.tiime.how.step1.title': 'Connect your crypto sources',
    'partners.tiime.how.step1.text': 'Link your wallets and exchanges to Crypto Accounting. Transactions are imported automatically.',
    'partners.tiime.how.step2.title': 'Your entries are generated',
    'partners.tiime.how.step2.text': 'Operation categorisation, capital gains calculation and FEC production: everything is automated and compliant.',
    'partners.tiime.how.step3.title': 'Export to Tiime in one click',
    'partners.tiime.how.step3.text': 'Your crypto accounting entries and your FEC are sent to Tiime, ready to be added to your file.',
    'partners.tiime.benefits.title': 'Why use the Tiime integration',
    'partners.tiime.benefit1.title': 'Zero re-keying',
    'partners.tiime.benefit1.text': 'No more copy-pasting or files to redo by hand: entries land directly in Tiime.',
    'partners.tiime.benefit2.title': 'Compliance guaranteed',
    'partners.tiime.benefit2.text': 'The FEC and entries comply with the ANC 2026-01 regulation: a solid, justifiable crypto file.',
    'partners.tiime.benefit3.title': 'Valuable time saved',
    'partners.tiime.benefit3.text': 'Crypto accounting, often time-consuming, becomes a simple step in your Tiime workflow.',
    'partners.tiime.cta.title': 'Connect your crypto accounting to Tiime',
    'partners.tiime.cta.text': 'Create your account and export your first crypto entries to Tiime today.',
    'partners.tiime.cta.btn': 'Create an account',

    // Partenaires : Dougs
    'partners.dougs.hero.badge': 'Partner : Dougs firm',
    'partners.dougs.hero.h1': 'Working with Dougs and holding crypto?',
    'partners.dougs.hero.subtitle': 'Crypto Accounting takes care of the entire crypto side of your accounting. Simple, clear, and with nothing to change in your relationship with Dougs.',
    'partners.dougs.hero.cta': 'Create my account',
    'partners.dougs.intro': 'Your crypto should not complicate your accounting. Crypto Accounting handles the entire crypto side, while Dougs keeps managing your accounts as usual.',
    'partners.dougs.about.badge': 'Our partner',
    'partners.dougs.about.with': 'With',
    'partners.dougs.about.kicker': '+49,000 entrepreneurs trust them',
    'partners.dougs.about.title': 'The online accountant that really simplifies your life',
    'partners.dougs.about.subtitle': 'No manual entry. Fantastic advice. No commitment.',
    'partners.dougs.about.li1': 'Automatic bank synchronisation and transaction categorisation',
    'partners.dougs.about.li2': 'Dedicated accountant 5 days a week via chat, phone, email and video',
    'partners.dougs.about.li3': 'Tax return and balance sheet prepared and certified',
    'partners.dougs.about.li4': 'Real-time forecasts of expenses, contributions and VAT',
    'partners.dougs.who.title': 'Who does what?',
    'partners.dougs.who.subtitle': 'Each plays its role, for complete accounting with no grey areas.',
    'partners.dougs.who.dougs.label': 'Your accounting firm',
    'partners.dougs.who.dougs.li1': 'Keeps your accounting and remains your accounting contact',
    'partners.dougs.who.dougs.li2': 'Prepares your annual accounts and your tax filings',
    'partners.dougs.who.dougs.li3': 'Integrates your crypto file into the rest of your accounting',
    'partners.dougs.who.ca.label': 'Your crypto side',
    'partners.dougs.who.ca.li1': 'Imports all your crypto transactions (wallets, exchanges)',
    'partners.dougs.who.ca.li2': 'Reconstructs and computes your entire crypto accounting',
    'partners.dougs.who.ca.li3': 'Produces your FEC and your audit trail, ready to forward',
    'partners.dougs.steps.title': 'How it works, step by step',
    'partners.dougs.steps.step1.title': 'You connect your crypto accounts',
    'partners.dougs.steps.step1.text': 'You create your Crypto Accounting account and link your wallets and exchanges. Your transactions are imported automatically.',
    'partners.dougs.steps.step2.title': 'We produce your crypto file',
    'partners.dougs.steps.step2.text': 'Crypto Accounting reconstructs and computes your crypto accounting, then produces your FEC and your audit trail.',
    'partners.dougs.steps.step3.title': 'You forward the file to Dougs',
    'partners.dougs.steps.step3.text': 'You retrieve your crypto file from Crypto Accounting and forward it to your Dougs accountant, who integrates it into your accounting.',
    'partners.dougs.billing.title': 'What about billing?',
    'partners.dougs.billing.text': 'Two complementary services: you pay the crypto side to Crypto Accounting and your accounting to Dougs. You only pay for what you need.',
    'partners.dougs.faq.title': 'Frequently asked questions',
    'partners.dougs.faq.q1': 'Do I have to leave Dougs to use Crypto Accounting?',
    'partners.dougs.faq.a1': 'No. Dougs remains your accounting firm and keeps managing your accounts. Crypto Accounting is added only to handle the crypto side of your accounting.',
    'partners.dougs.faq.q2': 'What should I forward to my Dougs accountant?',
    'partners.dougs.faq.a2': 'The crypto file produced by Crypto Accounting: your FEC and your audit trail. Your Dougs accountant then integrates them into your accounting.',
    'partners.dougs.faq.q3': 'How does billing work?',
    'partners.dougs.faq.a3': 'Crypto Accounting bills the crypto service and Dougs bills your accounting support. These are two separate subscriptions, each for its own scope.',
    'partners.dougs.cta.title': 'Ready to get your crypto accounting in order?',
    'partners.dougs.cta.text': 'Create your Crypto Accounting account and prepare the crypto side to forward to your Dougs accountant.',
    'partners.dougs.cta.btn': 'Create my account',
  }
};

function tr(key: string): string {
  return (translations as any)[currentLang][key] || key;
}

function switchLang(lang: Lang): void {
  const previousLang = currentLang;
  currentLang = lang;
  localStorage.setItem('cryptoaccounting-lang', lang);
  updateAllTexts();
  document.dispatchEvent(new CustomEvent('languageChange', { detail: { lang } }));

  // AJOUT: Tracking du changement de langue pour mesurer l'intérêt international
  if (previousLang !== lang) {
    trackEvent('engagement', 'language_switch', lang);
  }
}

function updateAllTexts(): void {
  console.log('🔄 Mise à jour des traductions...');
  ensureBaseI18nCaptured();

  // Méthode 1: Mise à jour via data-nav et data-btn (navbar)
  const navLinks = {
    'nav-home': 'nav.home',
    'nav-solution': 'nav.solution',
    'nav-skip': 'nav.skip',
    'nav-solutions': 'nav.solutions',
    'nav-solutions-header': 'nav.solutions.header',
    'nav-solutions-experts': 'nav.solutions.experts',
    'nav-solutions-experts-desc': 'nav.solutions.experts.desc',
    'nav-solutions-tpe': 'nav.solutions.tpe',
    'nav-solutions-tpe-desc': 'nav.solutions.tpe.desc',
    'nav-solutions-psca': 'nav.solutions.psca',
    'nav-solutions-psca-desc': 'nav.solutions.psca.desc',
    'nav-solutions-pricing': 'nav.solutions.pricing',
    'nav-solutions-how': 'nav.solutions.how',
    'nav-solutions-how-desc': 'nav.solutions.how.desc',
    'nav-solutions-accompagnement': 'nav.solutions.accompagnement',
    'nav-solutions-accompagnement-desc': 'nav.solutions.accompagnement.desc',
    'nav-solutions-compliance': 'nav.solutions.compliance',
    'nav-solutions-compliance-desc': 'nav.solutions.compliance.desc',
    'nav-solutions-flux': 'nav.solutions.flux',
    'nav-solutions-flux-desc': 'nav.solutions.flux.desc',
    'nav-solutions-particuliers': 'nav.solutions.particuliers',
    'nav-solutions-particuliers-desc': 'nav.solutions.particuliers.desc',
    'nav-pricing': 'nav.pricing',
    'nav-blog': 'nav.blog',
    'nav-academy': 'nav.academy',
    'nav-webinaires': 'nav.webinaires',
    'nav-partners': 'nav.partners',
    'nav-contact': 'nav.contact'
  };

  Object.entries(navLinks).forEach(([id, key]) => {
    const elements = document.querySelectorAll(`[data-nav="${id}"]`);
    elements.forEach(element => {
      if (!element || !(element instanceof HTMLElement)) return;
      if (currentLang === DEFAULT_LANG) {
        const base = baseNavBtnText.get(element);
        if (base !== undefined) {
          element.textContent = base;
          return;
        }
      }
      element.textContent = tr(key);
    });
  });

  const buttons = {
    'btn-account': 'btn.account',
    'btn-login': 'btn.login',
    'btn-appointment-trigger': 'btn.appointment',
    'btn-appointment-header': 'btn.appointment.header',
    'btn-appointment-sanaa': 'btn.appointment.sanaa',
    'btn-appointment-gonzague': 'btn.appointment.gonzague'
  };

  Object.entries(buttons).forEach(([id, key]) => {
    const elements = document.querySelectorAll(`[data-btn="${id}"]`);
    elements.forEach(element => {
      if (!element || !(element instanceof HTMLElement)) return;
      if (currentLang === DEFAULT_LANG) {
        const base = baseNavBtnText.get(element);
        if (base !== undefined) {
          element.textContent = base;
          return;
        }
      }
      element.textContent = tr(key);
    });
  });

  // Méthode 2: Mise à jour via data-translate (Footer et autres)
  const elementsToTranslate = document.querySelectorAll('[data-translate]');
  elementsToTranslate.forEach(element => {
    const key = element.getAttribute('data-translate');
    if (key && element instanceof HTMLElement) {
      if (currentLang === DEFAULT_LANG && restoreBaseI18nForElement(element)) return;
      const translatedText = tr(key);
      if (translatedText !== key) { // Seulement si une traduction existe
        applyTranslatedText(element, translatedText);
      }
    }
  });

  // Méthode 3: Traduction des attributs via data-tr-*
  const attrNodes = document.querySelectorAll<HTMLElement>('[data-tr-placeholder], [data-tr-title], [data-tr-aria-label], [data-tr-value]');
  attrNodes.forEach((el) => {
    if (currentLang === DEFAULT_LANG) {
      const base = baseAttrs.get(el);
      if (base) {
        base.forEach((val, attr) => {
          if (val === null) el.removeAttribute(attr);
          else el.setAttribute(attr, val);

          if (attr === 'placeholder' && (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement)) {
            el.placeholder = val ?? '';
          }
          if (attr === 'value' && (el instanceof HTMLInputElement || el instanceof HTMLButtonElement)) {
            el.value = val ?? '';
          }
        });
        return;
      }
    }

    const map: Record<string, string | null> = {
      'placeholder': el.getAttribute('data-tr-placeholder'),
      'title': el.getAttribute('data-tr-title'),
      'aria-label': el.getAttribute('data-tr-aria-label'),
      'value': el.getAttribute('data-tr-value'),
    };
    Object.entries(map).forEach(([attr, key]) => {
      if (key) {
        const val = tr(key);
        if (attr === 'placeholder' && (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement)) {
          (el as HTMLInputElement | HTMLTextAreaElement).placeholder = val;
        } else if (attr === 'value' && (el instanceof HTMLInputElement || el instanceof HTMLButtonElement)) {
          (el as HTMLInputElement | HTMLButtonElement).value = val;
        } else {
          el.setAttribute(attr, val);
        }
      }
    });
  });

  updateCanonicalAndHreflang();
  updateHomepageFaqSchema();
  updateBreadcrumbSchema();

  // Mettre à jour les boutons de langue
  updateLangButtons();

  // Mettre à jour les catégories si elles sont affichées
  updateCategoriesIfVisible();

  console.log(`✅ Traductions mises à jour (langue: ${currentLang})`);
}

function updateCanonicalAndHreflang(): void {
  // SEO : la version /en/ est une traduction côté client du même HTML (titre, description,
  // lang="fr" identiques). Elle ne doit JAMAIS se déclarer canonique d'elle-même, sinon Google
  // indexe /en/ comme doublon de la page FR et peut la retenir à la place de l'URL française
  // (constaté en septembre 2026 : la home indexée était /en/). On conserve la balise canonical
  // du HTML source (URL FR) et on supprime les hreflang, qui contredisaient cette canonical.
  document.querySelectorAll('link[rel="alternate"][hreflang]').forEach((link) => link.remove());
}

function updateHomepageFaqSchema(): void {
  const path = window.location.pathname || '/';
  const isHomepage = path === '/' || path === '/en/' || path.endsWith('index.html');
  if (!isHomepage) return;

  const faqSection = document.getElementById('faq');
  if (!faqSection) return;

  const hasFaqPage = (node: unknown): boolean => {
    if (!node || typeof node !== 'object') return false;
    const obj = node as Record<string, unknown>;
    if (obj['@type'] === 'FAQPage') return true;
    const graph = obj['@graph'];
    if (Array.isArray(graph)) {
      return graph.some(item => typeof item === 'object' && (item as Record<string, unknown>)['@type'] === 'FAQPage');
    }
    return false;
  };

  document.querySelectorAll('script[type="application/ld+json"]').forEach((script) => {
    try {
      const data = JSON.parse(script.textContent ?? '');
      if (hasFaqPage(data)) {
        script.remove();
      }
    } catch (error) {
      // Ignore non-JSON or invalid JSON-LD blocks.
    }
  });

  const questions = Array.from(faqSection.querySelectorAll('.faq-question'));
  const entries = questions.slice(0, 8).map((questionEl) => {
    const questionText = questionEl.querySelector('span')?.textContent?.trim();
    const answerEl = questionEl.parentElement?.querySelector('.faq-answer p');
    const answerText = answerEl?.textContent?.trim();
    if (!questionText || !answerText) return null;
    return {
      question: questionText,
      answer: answerText
    };
  }).filter(Boolean) as Array<{ question: string; answer: string }>;

  if (!entries.length) return;

  const schema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": entries.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer
      }
    }))
  };

  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.setAttribute('data-schema', 'homepage-faq');
  script.textContent = JSON.stringify(schema);
  document.head.appendChild(script);
}

function updateBreadcrumbSchema(): void {
  const path = window.location.pathname || '/';
  const isArticle = /\/(blog|article)\/[^/]+\/?$/.test(path) || /\/en\/(blog|article)\/[^/]+\/?$/.test(path);
  const isCategoryPage = /\/(experts-comptables|tpe-pme|psca)(?:\.html)?$/.test(path);
  if (isArticle || isCategoryPage) return;

  const isBlogList = path === '/blog' || path === '/blog/' || path.includes('blog.html')
    || path === '/en/blog' || path === '/en/blog/';
  const isPricing = path === '/pricing' || path === '/pricing/' || path.includes('pricing.html')
    || path === '/en/pricing' || path === '/en/pricing/';

  const existingSchema = document.querySelector('script[type="application/ld+json"][data-schema="breadcrumb"]');
  if (!isBlogList && !isPricing) {
    if (existingSchema) existingSchema.remove();
    return;
  }

  const isEn = currentLang === 'en' || path.startsWith('/en');
  const homeUrl = isEn ? 'https://cryptoaccounting.fr/en/' : 'https://cryptoaccounting.fr/';
  const homeLabel = isEn ? 'Home' : 'Accueil';

  const itemListElement = [
    {
      "@type": "ListItem",
      "position": 1,
      "name": homeLabel,
      "item": homeUrl
    }
  ];

  if (isBlogList) {
    const blogUrl = isEn ? 'https://cryptoaccounting.fr/en/blog' : 'https://cryptoaccounting.fr/blog';
    itemListElement.push({
      "@type": "ListItem",
      "position": 2,
      "name": "Blog",
      "item": blogUrl
    });
  }

  if (isPricing) {
    const pricingLabel = isEn ? 'Pricing' : 'Tarifs';
    const pricingUrl = isEn ? 'https://cryptoaccounting.fr/en/pricing' : 'https://cryptoaccounting.fr/pricing';
    itemListElement.push({
      "@type": "ListItem",
      "position": 2,
      "name": pricingLabel,
      "item": pricingUrl
    });
  }

  const schema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement
  };

  if (existingSchema) {
    existingSchema.textContent = JSON.stringify(schema);
    return;
  }

  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.setAttribute('data-schema', 'breadcrumb');
  script.textContent = JSON.stringify(schema);
  document.head.appendChild(script);
}

function applyTranslatedText(element: HTMLElement, translatedText: string): void {
  // Mode 1: HTML complet (ex: liens <a>, spans stylés, etc.)
  if (translatedText.includes('<') && translatedText.includes('>')) {
    element.innerHTML = translatedText;
    return;
  }

  // Mode 2: Remplacement par "parts" (préserve le HTML existant: <br>, <span>, classes...)
  // Syntaxe: "part1||part2||part3"
  if (translatedText.includes('||')) {
    const parts = translatedText.split('||');
    const textNodes = collectTranslatableTextNodes(element);

    if (textNodes.length > 0) {
      const overlap = Math.min(parts.length, textNodes.length);
      for (let i = 0; i < overlap; i++) {
        textNodes[i].nodeValue = parts[i];
      }
      for (let i = overlap; i < textNodes.length; i++) {
        textNodes[i].nodeValue = '';
      }
      if (parts.length > textNodes.length) {
        const tail = parts.slice(textNodes.length).join('');
        textNodes[textNodes.length - 1].nodeValue = (textNodes[textNodes.length - 1].nodeValue ?? '') + tail;
      }
      return;
    }
  }

  // Mode 3: Texte simple
  element.textContent = translatedText;
}

function collectTranslatableTextNodes(root: HTMLElement): Text[] {
  const nodes: Text[] = [];

  const walk = (node: Node): void => {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as HTMLElement;
      if (el !== root && el.hasAttribute('data-translate')) return;
    }

    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.nodeValue ?? '';
      if (text.trim().length > 0) nodes.push(node as Text);
      return;
    }

    node.childNodes.forEach(walk);
  };

  walk(root);
  return nodes;
}

// Fonction pour mettre à jour les catégories quand la langue change
function updateCategoriesIfVisible(): void {
  // Chercher quelle catégorie est actuellement active
  const activeButton = document.querySelector('button.bg-primary[id*="experts"], button.bg-primary[id*="tpe"], button.bg-primary[id*="psan"]');
  if (activeButton && activeButton instanceof HTMLElement) {
    const categoryId = activeButton.id;
    console.log('🔄 Mise à jour de la catégorie active:', categoryId);
    // Déclencher directement updateContent avec le bon ID
    if (categoryId === 'experts-comptables' || categoryId === 'tpe-pme' || categoryId === 'psan-grandes') {
      // Attendre un petit délai pour que les traductions soient mises à jour
      setTimeout(() => {
        const updateContentEvent = new CustomEvent('updateCategory', { detail: { categoryId } });
        document.dispatchEvent(updateContentEvent);
      }, 50);
    }
  }
}

function updateLangButtons(): void {
  const frBtns = document.querySelectorAll('[data-lang="fr"]');
  const enBtns = document.querySelectorAll('[data-lang="en"]');

  const styleBtn = (btn: Element, active: boolean) => {
    if (!(btn instanceof HTMLElement)) return;
    const isMobile = btn.getAttribute('data-variant') === 'mobile';

    // Design allégé: pas de fond; couleur + soulignement si actif
    btn.style.background = 'transparent';
    btn.style.boxShadow = 'none';
    btn.style.transform = 'none';

    if (isMobile) {
      btn.style.color = active ? '#ffffff' : '#DCE5FE';
      btn.style.textDecoration = active ? 'underline' : 'none';
      btn.style.fontWeight = active ? '600' : '500';
    } else {
      btn.style.color = '#133AC9';
      btn.style.textDecoration = active ? 'underline' : 'none';
      btn.style.fontWeight = active ? '600' : '500';
    }
  };

  frBtns.forEach(btn => styleBtn(btn, currentLang === 'fr'));
  enBtns.forEach(btn => styleBtn(btn, currentLang === 'en'));
}

// Rendre la fonction globale pour les boutons onclick
(window as any).switchLang = switchLang;

// === FONCTION POUR LES CATÉGORIES TRADUITES ===
function getCategoryContent(category: string): CategoryContent | null {
  const baseIcons = {
    optimize: `<svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6 text-primary group-hover:text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
  <circle cx="12" cy="12" r="10"/> <!-- Horloge -->
  <polyline points="12 6 12 12 16 14"/> <!-- Aiguilles -->
  <path d="M12 2v2M12 20v2M2 12h2M20 12h2"/> <!-- Marques -->
</svg>`,
    compliance: `<svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6 text-primary group-hover:text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
  <rect x="3" y="2" width="18" height="20" rx="2"/> <!-- Document -->
  <path d="M7 7h10M7 12h10M7 17h7"/> <!-- Lignes de texte -->
  <path d="M17 14l2 2l4-4"/> <!-- Coche de validation -->
</svg>`,
    support: `<svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6 text-primary group-hover:text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                <circle cx="9" cy="7" r="4"/>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
              </svg>`
  };

  if (category === "experts-comptables") {
    return {
      title: tr('category.experts.title'),
      description: tr('category.experts.description'),
      details: [
        {
          title: tr('category.experts.optimize'),
          text: tr('category.experts.optimize.text'),
          icon: baseIcons.optimize
        },
        {
          title: tr('category.experts.compliance'),
          text: tr('category.experts.compliance.text'),
          icon: baseIcons.compliance
        },
        {
          title: tr('category.experts.support'),
          text: tr('category.experts.support.text'),
          icon: baseIcons.support
        }
      ],
      features: currentLang === 'en'
        ? [
            'A complete, documented report ready to integrate into your software',
            'A simplified relationship with your client thanks to clear, actionable data',
            'Compliance with tax standards and ANC recommendations',
            'FEC generation per fiscal year',
            'Significant time savings on crypto operations analysis',
            'Dedicated technical support'
          ]
        : [
            "Un rapport complet, documenté et prêt à intégrer dans vos logiciels",
            "Une relation simplifiée avec votre client grâce à des données claires et exploitables",
            "Conformité avec les normes fiscales et les recommandations de l'ANC",
            "Génération de FEC par exercice",
            "Un gain de temps considérable sur l'analyse des opérations crypto",
            "Support technique dédié"
          ]
    };
  }

  if (category === "tpe-pme") {
    return {
      title: tr('category.tpe.title'),
      description: tr('category.tpe.description'),
      details: [
        {
          title: tr('category.tpe.efficiency'),
          text: tr('category.tpe.efficiency.text'),
          icon: baseIcons.optimize
        },
        {
          title: tr('category.tpe.reports'),
          text: tr('category.tpe.reports.text'),
          icon: baseIcons.compliance
        },
        {
          title: tr('category.tpe.personalized'),
          text: tr('category.tpe.personalized.text'),
          icon: baseIcons.support
        }
      ],
      features: currentLang === 'en'
        ? [
            'Automatic import of your transactions (wallets, exchanges, etc.)',
            'No hassle: connect your wallets and let the software work.',
            'No manual entry: all your flows are processed automatically.',
            'No mistakes: automatic classification and calculations compliant with tax rules.',
            'No lock‑in: you own your data and your accounting choices.'
          ]
        : [
            "Import automatique de vos transactions (wallets, exchanges, etc.)",
            "Zéro prise de tête : connectez vos wallets, laissez le logiciel faire.",
            "Zéro saisie manuelle : tous vos flux sont traités automatiquement.",
            "Zéro erreur : classification et calcul automatiques, conformes aux normes fiscales.",
            "Zéro dépendance : vous restez propriétaire de vos données et vos choix comptables."
          ]
    };
  }

  if (category === "psan-grandes") {
    const compliance = `<svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6 text-primary group-hover:text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
  <path d="M9 12l2 2l4-4"/>
</svg>`;

    const scalability = `<svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6 text-primary group-hover:text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
  <path d="M3 3v18h18"/>
  <path d="M3 15l4-4l4 4l7-10"/>
  <circle cx="18" cy="5" r="1"/>
</svg>`;

    const expertise = `<svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6 text-primary group-hover:text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
  <circle cx="12" cy="12" r="3"/>
  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
</svg>`;

    return {
      title: tr('category.psan.title'),
      description: tr('category.psan.description'),
      details: [
        {
          title: tr('category.psan.compliance'),
          text: tr('category.psan.compliance.text'),
          icon: compliance
        },
        {
          title: tr('category.psan.scalability'),
          text: tr('category.psan.scalability.text'),
          icon: scalability
        },
        {
          title: tr('category.psan.expertise'),
          text: tr('category.psan.expertise.text'),
          icon: expertise
        }
      ],
      features: currentLang === 'en'
        ? [
            'Automatic processing of your crypto operations (trading, staking, fees, commissions)',
            'Tailor‑made chart of accounts adapted to your model (exchange, broker, asset management...)',
            'Generation of a structured report, directly usable by your CPA or auditor',
            'Very low human intervention: import, calculation, and output 100% automated'
          ]
        : [
            "Traitement automatique de vos opérations crypto (trading, staking, frais, commissions)",
            "Plan comptable sur mesure adapté à votre modèle (exchange, broker, gestion d'actifs...)",
            "Génération d'un rapport structuré, directement exploitable par votre expert-comptable ou CAC",
            "Très faible intervention humaine : import, calcul, restitution 100 % automatisés"
          ]
    };
  }

  return null;
}
import { createStatsCounter } from './components/StatsCounter';
import { createLogoCarousel } from './components/LogoCarousel';
import { initWhySection } from './components/WhySection';
import { initSolutionSection } from './components/SolutionSection';
import { createWebinarModal } from './components/WebinarModal';
import { initializeAnalytics } from './components/Analytics';
import { trackEvent, trackExternalLinks, trackFormSubmission, initReadingTimeTracker, trackCtaClicks } from './utils/analytics';
import { decorateAppLinks } from './utils/attribution';
import { createCookieConsent } from './components/CookieConsent';
import { CookieManager } from './utils/cookieManager';
import ContactForm from './components/contactForm';
import newsletterForm from "./components/newsletterForms";
import WebinarForm from './components/WebinarForm';
import { getWebinarRegistrationModal } from './components/WebinarRegistrationModal';
import { getWebinarById } from './data/webinars';
import { initWebinarPage } from './components/WebinarPageAnimator';
import { initWebinarWaitlistForm } from './components/WebinarWaitlistForm';
import './components/ChecklistForm';
import './utils/navigation-debug';
// import { initAnalytics, initReadingTimeTracker } from "/src/utils/analytics.ts";

interface CategoryDetail {
  title: string;
  text: string;
  icon: string;
}

interface CategoryContent {
  title: string;
  description: string;
  details: CategoryDetail[];
  features: string[];
  image?: string;
}

const mountGlobalLayout = () => {
  if (!document.querySelector('#mainHeader')) {
    const navbar = createNavbar();
    const navbarContainer = document.getElementById('navbar-container');
    if (navbarContainer) {
      navbarContainer.replaceWith(navbar);
    } else {
      document.body.prepend(navbar);
    }
  }

  const mainContent = document.querySelector('main');
  if (mainContent && !mainContent.id) {
    mainContent.id = 'main-content';
  }

  const footerContainer = document.getElementById('footer-container');
  const existingSiteFooter = document.getElementById('site-footer');
  if (footerContainer) {
    const footer = createFooter();
    footerContainer.replaceWith(footer);
  } else if (!existingSiteFooter) {
    const footer = createFooter();
    document.body.appendChild(footer);
  }
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', mountGlobalLayout);
} else {
  mountGlobalLayout();
}

if (import.meta.env.VITE_GA_MEASUREMENT_ID) {
  initializeAnalytics(import.meta.env.VITE_GA_MEASUREMENT_ID);
}
function renderFeaturesList(features: string[]): string {
  return features.map(feature => `
    <li class="flex items-center gap-3">
      <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5 text-blue-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <polyline points="20 6 9 17 4 12"/>
      </svg>
      ${feature}
    </li>
  `).join('');
}

function renderDetails(details: CategoryDetail[]): string {
  return details.map(detail => `
    <div class="p-6 rounded-xl hover:shadow-md transition-all duration-300 cursor-pointer group">
      <div class="space-y-3">
        <div class="flex items-center gap-4">
          <div class="p-3 bg-primary/10 rounded-lg group-hover:bg-primary group-hover:text-white transition-colors duration-300">
            ${detail.icon}
          </div>
          <h3 class="text-xl font-semibold text-primary-900">${detail.title}</h3>
        </div>
        <p class="text-gray-600">${detail.text}</p>
      </div>
    </div>
  `).join('');
}

function initCategoryButtons(): void {
  if (!document.getElementById('experts-comptables')) {
    console.log('Not on category page, skipping initialization');
    return;
}
  const buttons = {
    "experts-comptables": document.getElementById("experts-comptables"),
    "tpe-pme": document.getElementById("tpe-pme"),
    "psan-grandes": document.getElementById("psan-grandes")
  };

  const contentDisplay = document.getElementById("content-display");
  const detailsContainer = document.getElementById("details-container");

  if (!contentDisplay || !detailsContainer) {
    return;
  }

  const updateContent = (categoryId: string): void => {
    // Utiliser la fonction de traduction dynamique
    const content = getCategoryContent(categoryId);
    if (!content) {
      console.error(`Category ${categoryId} not found or not translated`);
      return;
    }

    // Update main content
    contentDisplay.innerHTML = `
      ${content.image ? `<img src="${content.image}" alt="Feature illustration" class="w-full h-auto rounded-xl mb-8"/>` : ''}
      <div class="space-y-4">
        <h3 class="text-2xl font-bold">${content.title}</h3>
        <p class="text-lg">${content.description}</p>
        <ul class="space-y-3">
          ${renderFeaturesList(content.features)}
        </ul>
      </div>
    `;

    // Update details section
    detailsContainer.innerHTML = renderDetails(content.details);

    // Update active state for buttons
    Object.entries(buttons).forEach(([id, button]) => {
      if (button) {
        button.classList.toggle('bg-primary', id === categoryId);
        button.classList.toggle('text-white', id === categoryId);
        button.classList.toggle('bg-white', id !== categoryId);
        button.classList.toggle('text-primary-900', id !== categoryId);
      }
    });
  };

  // Add click event listeners to buttons
  Object.entries(buttons).forEach(([categoryId, button]) => {
    if (button) {
      button.addEventListener("click", () => {
        updateContent(categoryId);
        // AJOUT: Tracking de la segmentation (Persona)
        trackEvent('segmentation', 'persona_click', categoryId);
      });
    }
  });

  // Initialize with experts-comptables category
  updateContent("experts-comptables");

  // Event listener pour mettre à jour les catégories lors du changement de langue
  document.addEventListener('updateCategory', (event: any) => {
    const categoryId = event.detail?.categoryId;
    if (categoryId) {
      updateContent(categoryId);
    }
  });
}


// Initialize when DOM is loaded
document.addEventListener('DOMContentLoaded', initCategoryButtons);
document.addEventListener('DOMContentLoaded', initInfographieConnections);

// menu mobile
function initMobileMenu() {
  const mobileMenu = document.getElementById('mobileMenu');
  const hamburgerBtn = document.getElementById('hamburgerBtn');

  if (mobileMenu && hamburgerBtn) {
    mobileMenu.classList.remove('js-only');
    let menuOpen = false;

    hamburgerBtn.addEventListener('click', () => {
      menuOpen = !menuOpen;
      trackEvent('navigation', 'mobile_menu', menuOpen ? 'open' : 'close');
      if (menuOpen) {
        mobileMenu.classList.remove('hidden');
      } else {
        mobileMenu.classList.add('hidden');
      }
    });
  }
}

// Iscroll sur le header
function initHeaderScroll() {
  const header = document.getElementById('mainHeader');
  if (header) {
    let lastTrackedPosition = 0;
    let ticking = false; // Pour la performance

    window.addEventListener('scroll', () => {
      const currentPosition = window.scrollY || document.documentElement.scrollTop || document.body.scrollTop;

      // Gestion du tracking avec throttling
      if (!ticking) {
        window.requestAnimationFrame(() => {
          // Track seulement tous les 500px pour éviter trop d'événements
          if (Math.abs(currentPosition - lastTrackedPosition) > 500) {
            trackEvent('engagement', 'scroll_depth', undefined, Math.floor(currentPosition));
            lastTrackedPosition = currentPosition;
          }
          ticking = false;
        });

        ticking = true;
      }
    });

    // Track initial position
    trackEvent('engagement', 'initial_scroll_position', undefined, 0);
  } else {
    console.log('Header element not found');
  }
}

function initInfographieConnections() {
  const stage = document.querySelector<HTMLElement>('.infographie-blockchain__stage');
  if (!stage) return;

  const svg = stage.querySelector<SVGSVGElement>('.infographie-blockchain__connections');
  const centerLogo = stage.querySelector<HTMLElement>('.infographie-blockchain__logo');
  if (!svg || !centerLogo) return;

  const icons = Array.from(stage.querySelectorAll<HTMLElement>('.infographie-blockchain__icon'));
  if (icons.length === 0) return;

  const svgNamespace = 'http://www.w3.org/2000/svg';
  const linesGroup = svg.querySelector<SVGGElement>('[data-connection-lines]');
  const rightLinesGroup = svg.querySelector<SVGGElement>('[data-connection-lines-right]');
  if (!linesGroup || !rightLinesGroup) return;

  linesGroup.innerHTML = '';
  rightLinesGroup.innerHTML = '';
  const paths = icons.map(() => {
    const path = document.createElementNS(svgNamespace, 'path');
    path.classList.add('connection-line');
    path.setAttribute('stroke', 'url(#lineGradient)');
    path.setAttribute('stroke-width', '2');
    path.setAttribute('fill', 'none');
    path.setAttribute('filter', 'url(#glow)');
    path.setAttribute('opacity', '0.85');
    path.setAttribute('vector-effect', 'non-scaling-stroke');
    linesGroup.appendChild(path);
    return path;
  });
  const rightCards = Array.from(
    stage.querySelectorAll<HTMLElement>('.infographie-blockchain__right .infographie-blockchain__card')
  );
  const rightPaths = rightCards.map(() => {
    const path = document.createElementNS(svgNamespace, 'path');
    path.classList.add('connection-line', 'connection-line--right');
    path.setAttribute('stroke', 'url(#lineGradient)');
    path.setAttribute('stroke-width', '2');
    path.setAttribute('fill', 'none');
    path.setAttribute('filter', 'url(#glow)');
    path.setAttribute('opacity', '0.75');
    path.setAttribute('vector-effect', 'non-scaling-stroke');
    rightLinesGroup.appendChild(path);
    return path;
  });

  let lastWidth = 0;
  let lastHeight = 0;
  let animationFrame = 0;

  const isMobile = () => window.innerWidth <= 768;

  const updateConnections = () => {
    // Toutes les lectures de position d'abord, les écritures ensuite : alterner
    // getBoundingClientRect et setAttribute forçait un recalcul de mise en page par logo.
    const svgRect = svg.getBoundingClientRect();
    if (svgRect.width === 0 || svgRect.height === 0) return;
    const mobile = isMobile();
    const centerRect = centerLogo.getBoundingClientRect();
    const iconRects = icons.map((icon) => icon.getBoundingClientRect());
    const cardRects = mobile ? [] : rightCards.map((card) => card.getBoundingClientRect());

    if (svgRect.width !== lastWidth || svgRect.height !== lastHeight) {
      svg.setAttribute('viewBox', `0 0 ${svgRect.width} ${svgRect.height}`);
      lastWidth = svgRect.width;
      lastHeight = svgRect.height;
    }

    const endX = centerRect.left + centerRect.width / 2 - svgRect.left;
    const endY = centerRect.top + centerRect.height / 2 - svgRect.top;

    paths.forEach((path, index) => {
      const icon = icons[index];
      const iconRect = iconRects[index];

      // Icône masquée (display:none) → pas de flux
      if (iconRect.width === 0 || iconRect.height === 0) {
        path.removeAttribute('d');
        return;
      }

      const startX = iconRect.left + iconRect.width / 2 - svgRect.left;
      const startY = iconRect.top + iconRect.height / 2 - svgRect.top;

      const dx = endX - startX;
      const dy = endY - startY;
      const distance = Math.hypot(dx, dy) || 1;
      const dirX = dx / distance;
      const dirY = dy / distance;
      const startInset = mobile ? iconRect.width * 0.55 : iconRect.width * 0.45;
      const endInset = mobile ? centerRect.width * 0.35 : centerRect.width * 0.28;
      const adjStartX = startX + dirX * startInset;
      const adjStartY = startY + dirY * startInset;
      const adjEndX = endX - dirX * endInset;
      const adjEndY = endY - dirY * endInset;

      const adjDx = adjEndX - adjStartX;
      const adjDy = adjEndY - adjStartY;
      const adjDistance = Math.hypot(adjDx, adjDy) || 1;
      const normalX = -adjDy / adjDistance;
      const normalY = adjDx / adjDistance;
      const isCenterIcon = icon.classList.contains('pennylane-center');

      // Mobile : courbes quasi-directes pour éviter de traverser d'autres icônes
      const bend = mobile
        ? Math.min(18, Math.max(5, adjDistance * 0.08))
        : Math.min(160, Math.max(70, adjDistance * 0.32)) * (isCenterIcon ? 1.35 : 1);
      const baseSweep = (index % 2 === 0 ? 1 : -1) * (adjDy >= 0 ? 1 : -1);
      const sweep = isCenterIcon ? 1 : baseSweep;

      const control1X = adjStartX + adjDx * 0.25 + normalX * bend * sweep;
      const control1Y = adjStartY + adjDy * 0.25 + normalY * bend * sweep;
      const control2X = adjStartX + adjDx * 0.75 - normalX * bend * 0.35 * sweep;
      const control2Y = adjStartY + adjDy * 0.75 - normalY * bend * 0.35 * sweep;

      path.setAttribute(
        'd',
        `M ${adjStartX.toFixed(2)} ${adjStartY.toFixed(2)} C ${control1X.toFixed(2)} ${control1Y.toFixed(2)} ${control2X.toFixed(2)} ${control2Y.toFixed(2)} ${adjEndX.toFixed(2)} ${adjEndY.toFixed(2)}`
      );
    });

    // Ne pas dessiner les lignes vers les cartes en mobile (masquées en CSS aussi)
    if (!mobile) {
      rightPaths.forEach((path, index) => {
        const cardRect = cardRects[index];
        const startX = centerRect.left + centerRect.width / 2 - svgRect.left;
        const startY = centerRect.top + centerRect.height / 2 - svgRect.top;
        const endX = cardRect.left - svgRect.left;
        const endY = cardRect.top + cardRect.height / 2 - svgRect.top;

        const dx = endX - startX;
        const dy = endY - startY;
        const distance = Math.hypot(dx, dy) || 1;
        const dirX = dx / distance;
        const dirY = dy / distance;
        const startInset = centerRect.width * 0.32;
        const endInset = 6;
        const adjStartX = startX + dirX * startInset;
        const adjStartY = startY + dirY * startInset;
        const adjEndX = endX - dirX * endInset;
        const adjEndY = endY - dirY * endInset;

        path.setAttribute(
          'd',
          `M ${adjStartX.toFixed(2)} ${adjStartY.toFixed(2)} L ${adjEndX.toFixed(2)} ${adjEndY.toFixed(2)}`
        );
      });
    } else {
      // Vider les paths des cartes en mobile
      rightPaths.forEach((path) => path.removeAttribute('d'));
    }
  };

  const animateConnections = () => {
    updateConnections();
    animationFrame = window.requestAnimationFrame(animateConnections);
  };

  const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  let isVisible = false;

  // L'animation ne tourne que lorsque l'infographie est à l'écran : avant, elle recalculait
  // les tracés à chaque image pendant toute la visite, même hors écran.
  const startAnimation = () => {
    if (!animationFrame && isVisible && !reducedMotionQuery.matches) {
      animationFrame = window.requestAnimationFrame(animateConnections);
    }
  };
  const stopAnimation = () => {
    window.cancelAnimationFrame(animationFrame);
    animationFrame = 0;
  };

  // Le premier rappel arrive dès l'observation et dessine les tracés si l'infographie est visible
  new IntersectionObserver(([entry]) => {
    isVisible = entry.isIntersecting;
    if (!isVisible) {
      stopAnimation();
    } else if (reducedMotionQuery.matches) {
      updateConnections();
    } else {
      startAnimation();
    }
  }).observe(stage);

  window.addEventListener('resize', updateConnections);
  if (typeof reducedMotionQuery.addEventListener === 'function') {
    reducedMotionQuery.addEventListener('change', (event) => {
      if (event.matches) {
        stopAnimation();
        updateConnections();
      } else {
        startAnimation();
      }
    });
  }
}


// FAQ
function initFaq() {
  const questions = document.querySelectorAll<HTMLElement>(".faq-question");

  questions.forEach((question) => {
    question.onclick = function() {
      const parent = (this as HTMLElement).parentElement;
      if (!parent) return;

      const answer = parent.querySelector(".faq-answer");
      const icon = (this as HTMLElement).querySelector("svg");

      if (!answer || !icon) return;

      if (answer.classList.contains("hidden")) {
        answer.classList.remove("hidden");
        icon.classList.add("rotate-180");
      } else {
        answer.classList.add("hidden");
        icon.classList.remove("rotate-180");
      }
    };
  });
}

// Blog et Ressources : contrôleurs importés à la demande, seulement sur leurs pages. Importés
// statiquement, ils embarquaient le contenu de toutes les leçons, articles et du glossaire
// (~60 % du JavaScript) sur chaque page, accueil compris.
async function initContentControllers(): Promise<void> {
  const path = window.location.pathname;

  const isBlogList = path.includes('blog.html') || path === '/blog' || path === '/blog/';
  const isBlogArticle = path.includes('single-article.html') || /\/(blog|article)\/[^/]+\/?$/.test(path);

  const isAcademyHub =
    path.includes('ressources.html') ||
    path === '/ressources' ||
    path === '/ressources/';
  const isAcademyArticle =
    path.includes('ressources-article.html') ||
    (/^\/ressources\/[^/]+\/?$/.test(path) &&
     !/\/(glossaire|parcours|categorie|niveau|articles)(\/|$)/.test(path));
  const isAcademyCategory =
    path.includes('ressources-category.html') ||
    /\/ressources\/(categorie|niveau)\//.test(path) ||
    /^\/ressources\/articles\/?$/.test(path);
  const isGlossaryIndex =
    path.includes('ressources-glossary.html') ||
    path === '/ressources/glossaire' ||
    path === '/ressources/glossaire/';
  const isGlossaryTerm =
    path.includes('ressources-glossary-term.html') ||
    /^\/ressources\/glossaire\/[^/]+\/?$/.test(path);
  const isAcademyPath =
    path.includes('ressources-path.html') ||
    /\/ressources\/parcours(\/|$)/.test(path);

  try {
    if (isBlogList || isBlogArticle) {
      const { BlogController } = await import('./features/blog/BlogController');
      const blogController = new BlogController();

      // PATCH SEO: On force la détection de la page blog sur les URLs propres (/blog)
      // Le contrôleur attendait "blog.html" par défaut
      (blogController as any).isBlogListRoute = (p: string) => {
        return p.includes('blog.html') || p === '/blog' || p === '/blog/';
      };

      blogController.initialize();
    }

    if (isAcademyHub) {
      const { AcademyController } = await import('./features/academy/AcademyController');
      new AcademyController().initialize();
    } else if (isAcademyArticle) {
      const { AcademyArticleController } = await import('./features/academy/AcademyArticleController');
      new AcademyArticleController().initialize();
    } else if (isAcademyCategory) {
      const { AcademyCategoryController } = await import('./features/academy/AcademyCategoryController');
      new AcademyCategoryController().initialize();
    } else if (isGlossaryIndex || isGlossaryTerm) {
      const { GlossaryController } = await import('./features/academy/GlossaryController');
      new GlossaryController().initialize();
    } else if (isAcademyPath) {
      const { AcademyPathController } = await import('./features/academy/AcademyPathController');
      new AcademyPathController().initialize();
    }
  } catch (error) {
    console.error('❌ Chargement du blog ou des ressources impossible :', error);
  }
}

document.addEventListener('DOMContentLoaded', () => {
  // === INITIALISATION DU SYSTÈME I18N ===
  console.log('🌍 Initialisation du système de traduction...');

  // Déterminer si on est sur la home (sert pour différer la FAQ après la capture i18n)
  const currentPath = window.location.pathname;
  const isHomepage = currentPath === '/' || currentPath.endsWith('index.html');

  // Attendre un délai plus long pour que le DOM soit entièrement rendu
  setTimeout(() => {
    updateAllTexts();
    console.log('✅ Système i18n initialisé - Langue:', currentLang);

    // FAQ sur la page d'accueil : on l'ajoute après la 1ère passe i18n
    // pour éviter que le contenu "Question X / Réponse X" ne soit capturé comme base en FR.
    if (isHomepage) {
      // FAQ est maintenant statique dans index.html pour le SEO
      // const faq = createFaqSection();
      // const main = document.querySelector('main');
      // if (main) {
      //   main.appendChild(faq);
      //   trackEvent('content', 'faq_loaded');
      // }

      // Appliquer les traductions sur la FAQ nouvellement injectée (y compris en FR)
      updateAllTexts();
    }

    // Initialiser la FAQ sur toutes les pages (Home + Catégories)
    if (!document.querySelector('[data-faq-initialized="true"]')) {
      initFaq();
    }
  }, 200);

  // Gestion des cookies - Modal uniquement (pas de bannière automatique)
  // Les cookies analytics sont exemptés du consentement préalable en France avec IP anonymisé
  document.body.insertAdjacentHTML('beforeend', createCookieConsent());

  const settingsModal = document.getElementById('cookie-settings-modal');
  const saveBtn = document.getElementById('cookie-save');
  const analyticsToggle = document.getElementById('analytics-toggle') as HTMLInputElement;
  const preferencesToggle = document.getElementById('preferences-toggle') as HTMLInputElement;
  const footerCookieLink = document.getElementById('footer-cookie-settings');

  // Ouvrir le modal depuis le lien du footer
  footerCookieLink?.addEventListener('click', (e) => {
    e.preventDefault();
    settingsModal?.classList.remove('hidden');
    trackEvent('cookies', 'open_settings_footer');
  });

  // Fermeture du modal en cliquant en dehors
  settingsModal?.addEventListener('click', (e) => {
    if (e.target === settingsModal) {
      settingsModal.classList.add('hidden');
    }
  });

  // Gestion du bouton "Enregistrer les préférences"
  saveBtn?.addEventListener('click', () => {
    const consent = {
      analytics: analyticsToggle?.checked || false,
      necessary: true,
      preferences: preferencesToggle?.checked || false,
      timestamp: Date.now()
    };

    CookieManager.setConsent(consent);
    settingsModal?.classList.add('hidden');

    trackEvent('cookies', 'save_preferences', JSON.stringify(consent));
  });

  // Initialisation des toggles avec les valeurs actuelles ou par défaut (analytics activé)
  const currentConsent = CookieManager.getConsent();
  if (currentConsent) {
    analyticsToggle.checked = currentConsent.analytics;
    preferencesToggle.checked = currentConsent.preferences;
  } else {
    // Par défaut, analytics est activé (conforme RGPD français avec IP anonymisé)
    analyticsToggle.checked = true;
    preferencesToggle.checked = false;
  }


  // Track la page vue
  trackEvent('navigation', 'page_view', window.location.pathname);

  // AJOUT: Tracking global des erreurs JS pour détecter les bugs bloquants
  window.addEventListener('error', (event) => {
    trackEvent('system', 'js_error', `${event.message} at ${event.filename}:${event.lineno}`);
  });

	  // Éléments de base
	  mountGlobalLayout();

	  // Blog et Ressources (chargés à la demande, seulement sur leurs pages)
	  const contentReady = initContentControllers();

	  // WebP global (si supporté) pour toutes les images du site
	  void enableGlobalWebpImages();

	  // Ces traitements ne voient que les liens présents à leur exécution : on attend le rendu
	  // du blog ou des ressources (immédiat sur les autres pages).
	  void contentReady.then(() => {
	    // Tracking des liens externes
	    trackExternalLinks();

	    // AJOUT: Tracking spécifique pour les contacts directs (Mailto/Tel)
	    // Très important pour le B2B où les gens appellent souvent directement
	    document.querySelectorAll('a[href^="mailto:"], a[href^="tel:"]').forEach(link => {
	      link.addEventListener('click', () => {
	        const href = link.getAttribute('href') || '';
	        const type = href.startsWith('mailto:') ? 'email_click' : 'phone_click';
	        trackEvent('contact', type, href.replace(/^(mailto:|tel:)/, ''));
	      });
	    });

	    // Tracking des CTA (démos, comptes, checklist, etc.)
	    trackCtaClicks();

	    // Attribution : propage les paramètres ?ref/utm persistés vers les liens
	    // d'inscription/connexion de l'app (app.cryptoaccounting.fr).
	    decorateAppLinks();
	  });

  // Tracking des formulaires
  trackFormSubmission('contactForm');
  trackFormSubmission('newsletterForm');

  // Si on est sur une page article, init le tracking de lecture
  if (window.location.pathname.includes('single-article')) {
    const articleId = window.location.pathname.split('/').pop() || 'unknown';
    initReadingTimeTracker(articleId);
  }

  // Initialisation des sections et fonctionnalités
  initMobileMenu();
  initHeaderScroll();
  createStatsCounter();
  createLogoCarousel();
  initWhySection();
  initSolutionSection();

  initCategoryButtons();

  // Inscription par webinaire (modale légère sur webinaires.html)
  const webinarRegistrationTriggers = document.querySelectorAll<HTMLElement>('[data-webinar-register]');
  if (webinarRegistrationTriggers.length > 0) {
    const registrationModal = getWebinarRegistrationModal();
    webinarRegistrationTriggers.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const id = btn.getAttribute('data-webinar-register');
        const webinar = getWebinarById(id);
        if (!webinar) {
          console.warn('Webinaire inconnu pour l\'ID:', id);
          return;
        }
        registrationModal.open(webinar);
        trackEvent('webinar', 'registration_modal_opened', webinar.id);
      });
    });
  }

  // Ancienne modale webinaire (page d'accueil), conservée pour compatibilité
  const webinarModal = createWebinarModal();

  // Ajouter les gestionnaires d'événements pour ouvrir la modale sur tous les boutons/liens du webinaire
  const webinarTriggers = document.querySelectorAll('[href="#webinaire"], .webinar-trigger, [data-webinar-trigger]');
  webinarTriggers.forEach(trigger => {
    trigger.addEventListener('click', (e) => {
      e.preventDefault();
      webinarModal.open();
      trackEvent('webinar', 'modal_opened');
    });
  });

  // Détecter le paramètre d'URL pour ouvrir automatiquement la modale du webinaire
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.get('webinar') === 'open') {
    console.log('🎯 Paramètre webinar=open détecté, ouverture de la modale...');
    // Ouvrir la modale après un délai plus long pour s'assurer que tout est chargé
    setTimeout(() => {
      console.log('📂 Tentative d\'ouverture de la modale webinaire');
      if (webinarModal && webinarModal.open) {
        webinarModal.open();
        trackEvent('webinar', 'modal_opened_from_url');
        console.log('✅ Modale webinaire ouverte avec succès');
      } else {
        console.error('❌ La modale webinaire n\'est pas disponible');
      }
    }, 1000);
  }

  // Initialisation du formulaire de contact s'il est présent sur la page
  if (document.getElementById('contactForm')) {
    try {
      console.log("Initialisation du formulaire de contact depuis main.ts");
      new ContactForm();
    } catch (error) {
      console.error("Erreur lors de l'initialisation du formulaire de contact:", error);
    }
  }

  if (document.getElementById('newsletterForm')) {
    try {
      console.log("Initialisation du formulaire de newsletter depuis main.ts");
      new newsletterForm();
    } catch (error) {
      console.error("Erreur lors de l'initialisation du formulaire de newsletter:", error);
    }
  }

  if (document.getElementById('webinarForm')) {
    try {
      console.log("Initialisation du formulaire de webinaire depuis main.ts");
      new WebinarForm();
    } catch (error) {
      console.error("Erreur lors de l'initialisation du formulaire de webinaire:", error);
    }
  }

  // Initialiser les animations de la page webinaire
  initWebinarPage();

  // Formulaire "Prévenez-moi" du bandeau d'attente (liste d'attente webinaire)
  initWebinarWaitlistForm();

  // Track le temps passé sur la page
  let startTime = Date.now();
  window.addEventListener('beforeunload', () => {
    const timeSpent = Math.round((Date.now() - startTime) / 1000);
    // AJOUT: Seuil de 5 secondes pour ne tracker que les visites qualifiées
    if (timeSpent > 5) {
      trackEvent('engagement', 'time_on_page', window.location.pathname, timeSpent);
    }
  });
 });

// ===================================================================
// Section "Comment ça marche" - Animations et interactions modernes
// ===================================================================

function initHowItWorksAnimations() {
  // Check for prefers-reduced-motion
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (prefersReducedMotion) {
    // Si l'utilisateur préfère réduire les animations, on affiche directement tout
    document.querySelectorAll('.how-step-card.step-animation').forEach((card) => {
      card.classList.remove('opacity-0');
      card.classList.add('animate-in');
    });
    return;
  }

  const stepCards = document.querySelectorAll('.how-step-card.step-animation');

  if (stepCards.length === 0) return;

  // Configuration de l'IntersectionObserver
  const observerOptions = {
    root: null,
    rootMargin: '0px',
    threshold: 0.3, // Déclencher quand 30% de la card est visible
  };

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting && entry.target.classList.contains('opacity-0')) {
        // Calculer le délai en fonction de l'index de la card
        const stepIndex = Array.from(stepCards).indexOf(entry.target as Element);
        const delay = stepIndex * 150; // 150ms de délai entre chaque étape

        setTimeout(() => {
          entry.target.classList.remove('opacity-0');
          entry.target.classList.add('animate-in');

          // Trigger animation spécifique de l'illustration
          triggerIllustrationAnimation(entry.target as HTMLElement);
        }, delay);

        // Une fois animée, on arrête d'observer pour éviter de re-trigger
        observer.unobserve(entry.target);
      }
    });
  }, observerOptions);

  // Observer toutes les step cards
  stepCards.forEach((card) => {
    observer.observe(card);
  });
}

// Fonction pour déclencher les animations spécifiques des illustrations
function triggerIllustrationAnimation(stepCard: HTMLElement) {
  const stepNumber = stepCard.getAttribute('data-step');
  const illustration = stepCard.querySelector('.how-illustration-svg');

  if (!illustration) return;

  // Animations spécifiques par étape
  switch (stepNumber) {
    case '1':
      // Animation des connexions de wallets
      animateConnectionNodes(illustration as SVGElement);
      break;
    case '2':
      // Animation du réseau neuronal
      animateNeuralNetwork(illustration as SVGElement);
      break;
    case '3':
      // Animation de génération du document
      animateDocumentGeneration(illustration as SVGElement);
      break;
    case '4':
      // Animation du flux d'export
      animateExportFlow(illustration as SVGElement);
      break;
  }
}

// Animations spécifiques pour chaque étape
function animateConnectionNodes(svg: SVGElement) {
  const nodes = svg.querySelectorAll('.wallet-node circle');
  nodes.forEach((node, index) => {
    setTimeout(() => {
      (node as SVGElement).style.animation = 'nodePulse 2s ease-in-out infinite';
      (node as SVGElement).style.animationDelay = `${index * 0.2}s`;
    }, index * 200);
  });
}

function animateNeuralNetwork(svg: SVGElement) {
  const processingNodes = svg.querySelectorAll('.processing-node');
  processingNodes.forEach((node, index) => {
    setTimeout(() => {
      (node as SVGElement).style.animation = 'processingGlow 1.5s ease-in-out infinite';
      (node as SVGElement).style.animationDelay = `${index * 0.3}s`;
    }, index * 300);
  });
}

function animateDocumentGeneration(svg: SVGElement) {
  const lines = svg.querySelectorAll('line[stroke-dasharray]');
  lines.forEach((line, index) => {
    setTimeout(() => {
      (line as SVGElement).style.animation = 'documentReveal 1s ease-out forwards';
    }, index * 300);
  });
}

function animateExportFlow(svg: SVGElement) {
  const exportArrows = svg.querySelectorAll('.export-arrows path');
  exportArrows.forEach((arrow, index) => {
    setTimeout(() => {
      (arrow as SVGElement).style.animation = 'dataFlow 1.5s ease-in-out forwards';
    }, index * 400);
  });
}

// Initialiser les animations quand le DOM est chargé
document.addEventListener('DOMContentLoaded', initHowItWorksAnimations);

// Ré-initialiser si la page est chargée depuis le cache (bfcache)
window.addEventListener('pageshow', (event) => {
  if (event.persisted) {
    initHowItWorksAnimations();
  }
});

// === Hero, spotlight qui suit le curseur (trame de points illuminée) ===
function initHeroSpotlight(): void {
  const hero = document.querySelector<HTMLElement>('.hero-dark');
  if (!hero) return;
  // Souris uniquement, sur tactile/sans hover, le halo reste centré (fallback CSS)
  if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

  let targetX = 0, targetY = 0, curX = 0, curY = 0, rafId = 0, initialized = false;

  const setVars = (): void => {
    hero.style.setProperty('--mx', `${curX}px`);
    hero.style.setProperty('--my', `${curY}px`);
  };

  const tick = (): void => {
    // lissage (lerp) pour un suivi fluide
    curX += (targetX - curX) * 0.15;
    curY += (targetY - curY) * 0.15;
    setVars();
    if (Math.abs(targetX - curX) > 0.5 || Math.abs(targetY - curY) > 0.5) {
      rafId = requestAnimationFrame(tick);
    } else {
      rafId = 0;
    }
  };

  hero.addEventListener('mousemove', (e: MouseEvent) => {
    const r = hero.getBoundingClientRect();
    targetX = e.clientX - r.left;
    targetY = e.clientY - r.top;
    if (!initialized) { curX = targetX; curY = targetY; initialized = true; setVars(); }
    if (!rafId) rafId = requestAnimationFrame(tick);
  });

  // Au départ du hero, le halo revient doucement vers le centre haut
  hero.addEventListener('mouseleave', () => {
    const r = hero.getBoundingClientRect();
    targetX = r.width / 2;
    targetY = r.height * 0.3;
    if (!rafId) rafId = requestAnimationFrame(tick);
  });
}

document.addEventListener('DOMContentLoaded', initHeroSpotlight);

export type { CategoryContent, CategoryDetail };
export { initCategoryButtons, initHowItWorksAnimations };
